import { arrivalKeepFor, dependentsByBlock, rollVersions, versionRollKey, drivenVersionRoll, lastAnswersFor, aliasesNamedBy, aliasToInsert, applyAliasPatch, composeSetName, planAliasRepairs, sameRef, retargetChips, setLeaf, unusedAliases, DEFAULT_FLOW_CONFIG, EXPERIENCE_ROLL_SCOPE, exitContentFor, lastAttemptFor, resolveChanges, resolveFlow, resolveFlowOutcome, type AnswerRef, type AnswerRefAlias, type FeedbackState, type TaskFlowConfig } from '@agavi/blocks';
import { DndContext, DragOverlay, type CollisionDetection, type DragEndEvent, type DragStartEvent, MouseSensor, TouchSensor, closestCenter, pointerWithin, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { ArrowRight, Braces, Brackets, ChevronLeft, ChevronRight, EyeOff, Play, ScanEye, SquarePen, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { BlockType } from '@/components/task/block-library';
import type { BlockUpdateOptions } from '@/lib/block-update';
import type { ClipboardBlockItem, ClipboardBlocks } from '@/lib/clipboard';
import type { ConflictChoice } from '@/lib/paste-blocks';
import type { PullRow } from '@/lib/pull-through';
import type { Op, VariableScope } from '@/lib/undo-ops';
import type { Block, Experience, Task, TaskVariable } from '@/types/experience';

import UnsavedChangesModal from '@/components/experience/unsaved-changes-modal';
import ModalShell from '@/components/modal-shell';
import { usePreviewSession, useRecallStandIns } from '@/components/preview-session-context';
import TakeOverBanner from '@/components/take-over-banner';
import { aliasChipName, aliasDisplayName, AnswerRefProvider } from '@/components/task/answer-ref-context';
import { AnswerRefManager, answerRefCount } from '@/components/task/answer-ref-manager';
import { BlockCopyProvider } from '@/components/task/block-copy-context';
import { BlockDeleteGuardProvider } from '@/components/task/block-delete-guard-context';
import { focusIssueFieldAfter } from '@/components/task/block-issues';
import BlockLibrary, { BLOCK_META } from '@/components/task/block-library';
import CompassBlock from '@/components/task/compass-block';
import DataVizBlock from '@/components/task/dataviz-block';
import DropdownBlock from '@/components/task/dropdown-block';
import { pulseFirstField, scrollIntoViewIfNeeded } from '@/components/task/field-reveal';
import { ArrivalPolicyProvider, BlockIdProvider, FiredStatesProvider, ReviewAttemptProvider, useCreateFiredStates } from '@/components/task/fired-states';
import FitbBlock from '@/components/task/fitb-block';
import FlowPanel from '@/components/task/flow-panel';
import GpsBlock from '@/components/task/gps-block';
import ImageBlock from '@/components/task/image-block';
import { forgetSettingsOpen } from '@/components/task/input-block-frame';
import KeywordBlock from '@/components/task/keyword-block';
import MCQBlock from '@/components/task/mcq-block';
import NumericalBlock from '@/components/task/numerical-block';
import OrderingBlock from '@/components/task/ordering-block';
import PasteConflictDialog from '@/components/task/paste-conflict-dialog';
import { PreviewA11yControls, PreviewA11yFilters, visionFilterStyle, type PreviewPanel, type VisionMode } from '@/components/task/preview-a11y';
import { PreviewA11yPanel, revealElement } from '@/components/task/preview-a11y-panel';
import { PreviewDebugPanel } from '@/components/task/preview-debug-panel';
import { PreviewSummaryDialog, readBlockResults, summarize, summarizeRecorded, type Submission } from '@/components/task/preview-submission';
import PullThroughDialog from '@/components/task/pull-through-dialog';
import { recallFeeders, recallMarkFor } from '@/components/task/recall-feeders';
import RotationBlock from '@/components/task/rotation-block';
import ScaleBlock from '@/components/task/scale-block';
import { SensorSimulatorProvider } from '@/components/task/sensor-simulator-context';
import { SensorSimulatorSheet, SensorSimulatorToggle } from '@/components/task/sensor-simulator-sheet';
import { rotationBandsIn, tiltBandsIn, compassBandsIn, gpsTargetsIn, taskHasBlock } from '@/components/task/sensor-simulator-targets';
import SliderBlock from '@/components/task/slider-block';
import SoundBlock from '@/components/task/sound-block';
import TaskContent, { renderEmbeddedBlock } from '@/components/task/task-content';
import TaskNavbar, { taskHref } from '@/components/task/task-navbar';
import TaskStepper from '@/components/task/task-stepper';
import TaskTitlebar from '@/components/task/task-titlebar';
import TextBlock from '@/components/task/text-block';
import { PHONE_QUERY, SHORT_LANDSCAPE_QUERY } from '@/components/task/use-viewport';
import { BlockPreviewProvider, LocalVariableProvider, rollVariableValues } from '@/components/task/variable-chip-preview';
import { describeDefinition, firstDependentOf, isSavableVariable, isVarUsed, isVarUsedByFormula, renameVarInBlockData, renameVarInFormula, rollsInForce, variablesInForce, variablesUsedByBlock } from '@/components/task/variable-utils';
import VariablesPanel, { type VariableUsage } from '@/components/task/variables-panel';
import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { FlowIcon } from '@/components/ui/icons';
import { PanelToggleGroup, type PanelToggleItem } from '@/components/ui/panel-toggle-group';
import { RadioGroup } from '@/components/ui/radio-group';
import { PhoneUndoProvider } from '@/contexts/phone-undo-context';
import { TaskVariablesContext, TaskPreviewRollContext } from '@/contexts/task-variables-context';
import { useCapability } from '@/hooks/use-capabilities';
import { ReducedMotionOverrideContext } from '@/hooks/use-reduced-motion';
import { useTaskTakeOver } from '@/hooks/use-task-take-over';
import { api, ApiError } from '@/lib/api';
import { signaturesFor } from '@/lib/attempt-signature';
import { blockDeleteReason } from '@/lib/block-delete-guard';
import { backfillBlockNames, computeAutoBlockName } from '@/lib/block-naming';
import { changedFields } from '@/lib/changed-fields';
import { foldExperienceVariables, readClipboard, snapshotTask, subscribeClipboard, withPulledAliases, writeClipboard } from '@/lib/clipboard';
import { freeVariableName, planRenameAcrossTasks, planSetRenameAcrossTasks, planTaskRename, type TaskRenamePlan } from '@/lib/cross-task-rename';
import { isRulePaused, normalizeFlowConfig, taskHasGradableBlock, unacceptedStarvedBlocks } from '@/lib/flow-utils';
import i18n from '@/lib/i18n';
import { describeError, recordLoadEvent, sampleIds } from '@/lib/load-recorder';
import { parkUndo, takeUndo, type ParkedUndoMap } from '@/lib/parked-undo';
import { applyBlockPaste, findVariableConflicts, incomingVariables, sameDefinition } from '@/lib/paste-blocks';
import { canRedo as sessionCanRedo, canUndo as sessionCanUndo, closeSession, openSession, recordEdit, recordOther, sessionCovers, stepBack, stepForward, type PhoneSession } from '@/lib/phone-session';
import { previewRouteFor, previewTaskFinished } from '@/lib/preview-route';
import { alreadyHereIds, PASTED_BLOCKS_SEED, pullPasteOrder, pullRows, seedTaskForBlocks } from '@/lib/pull-through';
import { pruneFollowers } from '@/lib/scenario-prune';
import { scenarioSetMembers } from '@/lib/scenario-set';
import { standInAnswers } from '@/lib/stand-in-answers';
import { familyKeysFor } from '@/lib/stand-in-family';
import { taskHasDraw } from '@/lib/task-randomness';
import { taskTrack } from '@/lib/task-track';
import { crossTaskRenameFor } from '@/lib/undo-ops';
import { useUndoStack, type UndoStack } from '@/lib/use-undo-stack';
import { duplicateSet } from '@/lib/variable-sets';

type ActiveDrag =
  | { source: 'block'; block: Block }
  | { source: 'library'; blockType: BlockType };

const collisionDetection: CollisionDetection = (args) => {
  if (args.active.data.current?.source === 'library') return pointerWithin(args);
  return closestCenter(args);
};


function blockLabel(type: string): string {
  const key = BLOCK_META[type]?.labelKey;
  return key ? i18n.t(key) : type;
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab within a full-screen mobile overlay (the Variables/Flow `createPortal` panels below),
 * moves focus into it on open, closes on Escape, and restores focus to whatever triggered it once
 * it closes. Without this, a `createPortal`-based overlay leaves the trigger button "hidden" but
 * still focused, so Tab would continue past it into the rest of the page instead of staying
 * inside the overlay, and closing it would drop focus to `<body>` since the trigger is gone from
 * view. `onClose` is read via a ref so the effect only re-runs when `isOpen` actually flips, not
 * on every render where the caller passes a fresh inline closure.
 */
function useFullScreenPanelA11y(isOpen: boolean, onClose: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const el = containerRef.current;
    const firstFocusable = el?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? el)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !el) return;
      const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((n) => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length));
      if (!nodes.length) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen]);

  return containerRef;
}

// ─────────────────────────────────────────────────────────────────────────────


/**
 * The live id of a task, following any versioning forks forward.
 *
 * Editing a locked task in a PUBLISHED experience does not mutate it — `forkIfLocked` tombstones it
 * and pushes a copy under a new id, so students mid-attempt keep seeing what they started. The
 * tombstone is then filtered out of every response, which left an open editor holding an id that no
 * longer resolved, with nothing in the payload pointing at its successor. Reloading showed "Task not
 * found", permanently, for any task edited after its experience was published.
 *
 * A fork is a rename, so the fix is to follow it rather than to report the old name missing.
 *
 * Walks a CHAIN, not one hop, because forks stack: each edit batch against an already-forked task
 * made another. And it takes the highest `version` among candidates rather than the first, because
 * a client that never followed a fork kept re-forking the same tombstone — so stored data can hold
 * SEVERAL tasks all claiming one `forkedFromId`, and only the newest carries the latest edits.
 *
 * `seen` guards a cycle in stored data. Without it a self-referential `forkedFromId` hangs the
 * editor, which is a worse failure than the one being fixed.
 */
export function resolveLiveTaskId(tasks: { id: string; forkedFromId?: string; version?: number }[], taskId: string): string {
  const seen = new Set<string>([taskId]);
  let current = taskId;
  for (;;) {
    const successors = tasks.filter(t => t.forkedFromId === current && !seen.has(t.id));
    if (successors.length === 0) return current;
    const next = successors.reduce((best, t) => ((t.version ?? 0) > (best.version ?? 0) ? t : best));
    seen.add(next.id);
    current = next.id;
  }
}

/** One frozen empty set, so a surface with no band hands back a stable identity. */
const EMPTY_ALIAS_IDS: ReadonlySet<string> = new Set();

export default function TaskWorkspacePage() {
  const { id: taskId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const experienceId = searchParams.get('experience') ?? '';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { readOnly, resume } = useTaskTakeOver(
    taskId ? `task-${taskId}` : undefined,
    taskId ? { resourceType: 'task', resourceId: taskId } : undefined,
  );

  const [experience, setExperience] = useState<Experience | null>(null);
  /**
   * The loaded experience, readable from the load effect without making it a dependency.
   *
   * It is what lets a hop between sibling tasks render instantly: the document already holds
   * every task's blocks, so there is nothing to wait for — see `applyTaskRef`.
   */
  const experienceRef = useRef<Experience | null>(null);
  experienceRef.current = experience;
  /** Read by the preview roll, which runs from an effect and must not close over a stale list. */
  const experienceVariablesRef = useRef<TaskVariable[] | undefined>(undefined);
  experienceVariablesRef.current = experience?.variables;
  const aliasesRef = useRef<AnswerRefAlias[]>([]);
  aliasesRef.current = experience?.answerRefAliases ?? [];
  /*
   * Read through a ref by the alias callbacks below, which have to be STABLE.
   *
   * They go into the answer-reference context, and a context value that changes identity every
   * render re-renders every chip in the task on every keystroke. Reading the flag from a ref is
   * what lets the callbacks keep one identity while still seeing the current value.
   */
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  /** The `contentRevision` the editor's current state was built from, so a revalidating fetch
   *  can tell "same task, nothing new" from "someone else changed it". */
  const appliedRevisionRef = useRef<number | null>(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [variables, setVariables] = useState<TaskVariable[]>([]);
  const [flow, setFlow] = useState<TaskFlowConfig>(DEFAULT_FLOW_CONFIG);
  const [variablesPanelOpen, setVariablesPanelOpen] = useState(false);
  const [flowPanelOpen, setFlowPanelOpen] = useState(false);
  // The exit a problems-panel trip asked for — the slot, and the block inside it to flash — held
  // only until FlowPanel has opened its modal.
  const [pendingExit, setPendingExit] = useState<{ slot: string; blockId?: string } | null>(null);
  // Same, for a routing fault: the flow card to open the panel on.
  const [pendingFlowCard, setPendingFlowCard] = useState<string | null>(null);
  const consumePendingFlowCard = useCallback(() => setPendingFlowCard(null), []);
  // Stable, so the panel's open-once effect can depend on it without re-opening the modal under a
  // teacher who has closed it.
  const consumePendingExit = useCallback(() => setPendingExit(null), []);
  /*
   * The third authoring panel, and the only one that can be EMPTY of its own subject. Variables
   * and Flow exist on every task; references exist on the few that author them, so the button is
   * conditional on there being something to manage — see `refStats` below.
   */
  const [refsPanelOpen, setRefsPanelOpen] = useState(false);
  const [mobileEditingId, setMobileEditingId] = useState<string | null>(null);
  const [phoneBlockCanUndo, setPhoneBlockCanUndo] = useState(false);
  const [phoneBlockCanRedo, setPhoneBlockCanRedo] = useState(false);
  const [phoneBlockMountKey, setPhoneBlockMountKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** The run this Preview belongs to, or no run at all while the teacher is simply editing. */
  const preview = usePreviewSession();
  /**
   * Preview is the RUN, not a toggle on this page.
   *
   * It used to be local state, which meant routing a student to the next task dropped them back
   * into the editor -- the flag lived on the page they were leaving. The session is held above the
   * router, so deriving from it makes Preview survive the navigation that Flow exists to cause.
   *
   * Scoped to this experience so a run left open elsewhere cannot put an unrelated task into a
   * student view.
   */
  const isPreview = preview.session?.experienceId === experienceId;
  // A single bottom Submit evaluates every block at once (no per-block Submit button in Preview
  // any more — see each block's own isPreview branch). A plain incrementing counter rather than a
  // boolean: each block reacts to it via a `useEffect` keyed on this value, so "changed" (not
  // "truthy") is the signal a block's own effect needs — a boolean flip-and-never-flip-back would
  // work too, but the counter reads unambiguously as "this happened N times" if ever needed later.
  // Reset to 0 on exiting Preview so a stale non-zero count doesn't make blocks think they've
  // already been submitted the instant a fresh Preview session mounts.
  const [previewSubmitCount, setPreviewSubmitCount] = useState(0);
  useEffect(() => {
    if (!isPreview) setPreviewSubmitCount(0);
  }, [isPreview]);
  // Accessibility review modes. Reset on leaving Preview for the same reason the submit count is:
  // these are INSPECTION modes, not settings, and one that outlived the session would leave a
  // teacher wondering whether they had changed what students see.
  const [visionMode, setVisionMode] = useState<VisionMode>('none');
  const [largeText, setLargeText] = useState(false);
  /** Preview's reduced-motion simulation — see `ReducedMotionOverrideContext`. */
  const [simReducedMotion, setSimReducedMotion] = useState(false);
  /** The read-through, open or not. There used to be a second mode — a list of faults — and
   *  every fault it reported is an error on the block's own card. See `PreviewA11yPanel`. */
  const [a11yPanel, setA11yPanel] = useState<PreviewPanel>(null);
  /**
   * INSPECT IS NOT ACCESSIBILITY TOOLING, and sharing `a11yPanel` said it was.
   *
   * The toggle lived inside `PreviewA11yControls` behind a `withDebug` flag and set
   * `a11yPanel = 'debug'`, so the one panel that is about FLOW was filed with the vision
   * filters and the read-through, and the two could never be open at once. Its own state, and
   * its own control in the bar.
   */
  const [inspectOpen, setInspectOpen] = useState(false);
  /** The bottom bar's primary — the verdict dialog returns focus to it. */
  const previewPrimaryRef = useRef<HTMLButtonElement>(null);
  /** The consolidated sensor panel. Closed on entering Preview, so it never opens over a task the
   *  teacher has not looked at yet. */
  const [sensorSheetOpen, setSensorSheetOpen] = useState(false);
  /**
   * The toolbar's review buttons toggle, and that is now the only way the review opens and shuts.
   *
   * There used to be a third state between open and closed — "stepped aside", where the panel
   * collapsed to a bar over the task — and it needed this callback to keep the toolbar button
   * meaning "bring it back" rather than "close". Pressing "Show me" simply closes the review now,
   * so the button means what it looks like again.
   */
  const choosePanel = useCallback((next: PreviewPanel) => setA11yPanel(next), []);
  /* Leaving Preview puts the simulations back as they were: they describe a STUDENT's experience,
     and a colour filter or a reduced-motion stand-in left on over the editor is a bug report
     waiting to happen. */
  useEffect(() => {
    if (!isPreview) {
      setVisionMode('none'); setLargeText(false); setSimReducedMotion(false); setA11yPanel(null);
      setInspectOpen(false);
    }
  }, [isPreview]);
  // Resolved when a scan runs, not captured: the task re-renders on submit and reset, so a held
  // node would go stale and the report would describe a detached tree.
  const previewRootRef = useRef<HTMLDivElement>(null);
  /**
   * What each block DECIDED this submission, as opposed to how it looked.
   *
   * Held here rather than in a provider of its own because grading happens in this component,
   * which is above every block it grades — the one place a context value from below is never
   * visible. See `fired-states.tsx`.
   */
  const firedStates = useCreateFiredStates();
  const getPreviewRoot = useCallback(() => previewRootRef.current, []);
  /**
   * What the accessibility review reads: the task AND the bottom furniture beneath it.
   *
   * Wider than `previewRootRef` on purpose. That one is the task itself and stays that way — the
   * vision filter, the read-through's badge layer and the phone frame's geometry are all about
   * the task's own box. The REVIEW is about what a student meets, and a student meets the
   * results bar and Submit.
   *
   * Safe to widen because `contrastFindings` gates every element on `isAuthoredContent`: the bars
   * carry no authored content, so the contrast report cannot gain findings about our own chrome —
   * which is the thing the narrower root was protecting. Only the read-through grows, and that is
   * the half accessibility testers use to check we built the controls right.
   */
  const scanRootRef = useRef<HTMLDivElement>(null);
  const getScanRoot = useCallback(() => scanRootRef.current, []);
  // ONE roll of the task's variables for the whole of Preview — see TaskPreviewRollContext for why
  // this can't be each block's own roll (they disagreed: n=7 in one block, n=9 in another). Rolled on
  // entering Preview and cleared on leaving, so every Preview session is a fresh draw and nothing
  // stale leaks into the next one. Blocks keep their own private rolls for block-level testing.
  const [previewRoll, setPreviewRoll] = useState<Record<string, string> | null>(null);
  /**
   * Drawn from the SESSION, which is what makes it survive walking away and back.
   *
   * The die is cast once per task per run: `roll` only calls the producer the first time it is
   * asked, so returning to a task you have already seen shows the same numbers rather than a
   * fresh draw. Without that, routing a student to a remediation task and back would hand them a
   * different question from the one they got wrong.
   */
  useEffect(() => {
    if (!isPreview || !taskId) { setPreviewRoll(null); return; }
    /*
     * TWO scopes, two rolls, merged by name.
     *
     * The experience's variables are drawn against a reserved scope that is NOT this task, so
     * `river` is the same river in task 1 and task 5 — a scenario that continues across tasks
     * would otherwise contradict itself, which it silently did. The task's own are drawn against
     * the task, where re-rolling per task is the right behaviour.
     *
     * Merging by name is safe because the two scopes cannot share one: a task variable may not
     * take an experience variable's name, and `variablesInForce` drops it if one ever does.
     */
    const experienceRoll = preview.roll(
      EXPERIENCE_ROLL_SCOPE,
      () => rollVariableValues(experienceVariablesRef.current ?? []),
    );
    setPreviewRoll({ ...experienceRoll, ...preview.roll(taskId, () => ({
      ...rollVariableValues(variablesRef.current),
      // The question-version draw rides the SAME roll, so it inherits the whole lifecycle —
      // sticky for the run, fresh on restart or Reroll, preserved on Reset — instead of needing
      // a second one kept in step. Per block, and blocks are not locked to each other.
      ...rollVersions(blocksRef.current),
    })) });
    /*
     * `variables` and `blocks` are dependencies even though the roll reads their REFS, because
     * what they say is "the set of things needing a value may have changed". A roll taken before
     * the task finished loading held no variable keys at all, and nothing re-ran to notice: the
     * chips stayed chips for the whole run, and Reroll looked like the cure because it throws the
     * roll away and draws a complete one.
     *
     * Re-running is cheap and converges: `rollFor` tops up only the keys it is missing and returns
     * the same roll object by identity when there are none, so `setPreviewRoll` bails out.
     */
  }, [isPreview, taskId, preview, variables, blocks]);
  /**
   * Draw another student's variant.
   *
   * A NEW RUN, not a new roll on the old one: the attempts already logged were answers to
   * different questions, so keeping them would make "unique attempts" count across two variants
   * and leniency earned on one carry into the other.
   */
  function rerollPreview() {
    // No rolls carried over, which is the whole difference: a fresh session draws fresh.
    startPreviewRun(undefined, i18n.t('task.bottomBar.previewRerollAnnounced'));
  }
  /**
   * Put the task back to how a student first meets it: answers cleared, feedback gone, nothing
   * submitted.
   *
   * Every answer lives inside its own block (each keeps its own draft, submitted snapshot and
   * continuous-testing flag), so there is no single piece of state to clear from here — the honest
   * way to reset eleven independent components is to remount them, which is what bumping the key
   * does. The same device the phone editor already uses to re-seed a block after undo/redo.
   *
   * Deliberately KEEPS the current roll. "Same variant, another go" and "a different student's
   * variant" are two different questions, and Reroll sitting next to this one answers the second.
   */
  const [previewResetKey, setPreviewResetKey] = useState(0);
  /**
   * EVERYTHING A FRESH RUN CLEARS, which is the same list whichever button started it.
   *
   * The two buttons differ in ONE argument — whether the new session keeps the old rolls — and
   * they used to differ in five more by accident. Reroll called `preview.start` alone, so it
   * drew a new variant underneath a page still showing the previous run's submitted answers,
   * fired feedback, submit count and open summary. Its own doc comment claimed it was "a NEW
   * RUN, not a new roll on the old one"; only this function makes that true.
   *
   * Reported as: having to press Reset after every Reroll to see the new scenario.
   */
  function startPreviewRun(rolls: Record<string, Record<string, string>> | undefined, announcement: string) {
    preview.start(experienceId, rolls);
    setPreviewSubmitCount(0);
    setPreviewResetKey(k => k + 1);
    setSubmission(null);
    setSummaryOpen(false);
    setPreviewResetAnnouncement(announcement);
  }

  function resetPreview() {
    // A new run carrying the SAME variants. Reset is "another go at this question" and Reroll is
    // "a different student's question"; both clear the log, because both are a fresh run and the
    // session has exactly two end triggers -- this is one of them.
    startPreviewRun(preview.session?.rolls, i18n.t('task.bottomBar.previewResetAnnounced'));
  }
  // Reset changes a lot of the page at once with no focus move, so it has to be spoken.
  const [previewResetAnnouncement, setPreviewResetAnnouncement] = useState('');

  /**
   * What the last Submit graded, and whether the student is still looking at the verdict.
   *
   * `null` until the first Submit of a Preview session — which is the state Reset returns to, and
   * the reason Reset is the only way back to a Submit button once a task has been completed.
   *
   * Two pieces rather than one, because "the dialog is up" and "there is a result" have different
   * lifetimes: the dialog closes on Review or Fix, the result stays until the next Submit or a
   * Reset, and the button's label is read off the result long after the dialog has gone.
   */
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  /**
   * Bumped by Submit, and the ONLY thing the grading read below keys off.
   *
   * Separate from `previewSubmitCount` even though the two move together, because that count is
   * also what tells each block to evaluate — and a reader keyed on it would run again on any other
   * render that happened to change it, including the zero that Reset writes.
   */
  /**
   * How long the grading read will wait for a task that never grades anything.
   *
   * Not a delay — the read normally finishes in two or three frames, as soon as the answer stops
   * changing. This is only the floor under the pathological case, generous because being slow on a
   * task that has nothing to report costs nothing and being early costs the whole verdict.
   */
  const GRADE_SETTLE_CAP_MS = 600;
  const [gradeRequest, setGradeRequest] = useState(0);
  /**
   * The dim goes up in the SAME click, before anything has been graded.
   *
   * It used to wait for the counts, and the wait was visible: feedback boxes grew all over the task,
   * the student's eye went to them, and only then did the dialog arrive and take it back. Two
   * attention grabs for one action, in the wrong order. Covering the task on the click means the
   * feedback is already behind the overlay by the time it appears, and the student meets it when
   * they dismiss the dialog and go looking — which is the whole design.
   */
  function submitPreview() {
    setPreviewSubmitCount(c => c + 1);
    setGradeRequest(n => n + 1);
    setSummaryOpen(true);
    // Cleared, so a second attempt cannot flash the FIRST attempt's counts for the frame before its
    // own arrive. The dim is up either way; only the dialog waits.
    setSubmission(null);
  }
  /**
   * Read the graded task once it has SETTLED, rather than after a chosen delay.
   *
   * The race the retired results bar already hit: Submit bumps a counter, each block reacts to it in
   * ITS OWN effect by setting its submitted state, and only the render after that puts any
   * `data-eval` in the tree. A synchronous read runs before all of it and finds an ungraded task.
   *
   * That bar waited a flat 120ms, and the wait was the thing that made it feel late. A single frame
   * was tried instead, on the reasoning that React runs child effects before parent ones so every
   * block has already requested its re-render by the time this fires — and measuring it in the
   * browser showed that reasoning to be wrong: on a twenty-block task the boxes land around 80ms,
   * two or three frames out, and the one-frame version reported a confident nought of nought.
   *
   * So it waits for the answer to stop changing instead of for a number somebody picked. Read every
   * frame; take the first reading that is non-empty and the same as the one before it. Fast machine,
   * two frames; slow machine, as many as it needs; and no constant to be wrong about.
   *
   * The cap is the case where nothing ever grades — a task whose blocks fire no feedback at all.
   * Then the honest answer IS the empty one, and it arrives at the cap rather than never.
   */
  /**
   * What the submission MEANT -- where it sends the student, and what it changes about the task.
   *
   * Separate from `summarize`, which answers a different question. The dialog reports what the
   * student can see (three counts and a name per block, read off the DOM); this reads what the
   * blocks decided (the states themselves, with their triggers) because that is the only thing
   * `resolveFlow`'s ladder can run on. Both are driven from the one settled read, so they can
   * never describe different submissions.
   *
   * Recording happens AFTER resolving, and the counts are taken WITH the pending attempt, because
   * the record carries the outcome and the outcome depends on a count that includes this try.
   */
  const gradeFlow = useCallback((blockResults: Record<string, FeedbackState[]>) => {
    if (!preview.session || !taskId) return;
    const answers = firedStates.readAnswers();
    const pending = {
      results: blockResults,
      // Derived here rather than reported by each block: a signature is a pure function of the
      // block's configuration and the answer, and both are already in hand at this one place.
      signatures: signaturesFor(blocksRef.current, answers, previewRoll ?? {}),
    };
    const counts = preview.counts(taskId, pending) ?? undefined;
    const config = flowRef.current;

    /*
     * BOTH halves of the decision, from one walk of the ladder. The slot is what says which
     * exit text applies; the action alone cannot, because two slots routing to the same task
     * produce identical actions.
     */
    const { slot, action: outcome } = resolveFlowOutcome(config, blockResults, counts);
    const effects = resolveChanges(config, blockResults, counts);

    preview.record({
      taskId,
      at: new Date().toISOString(),
      answers,
      ...pending,
      outcome,
      slot,
    });
    // Effects latch onto the SESSION, not this component: they have to survive leaving the task
    // and coming back, which page state does not. The outcome needs no home of its own -- it is
    // recorded on the attempt above, which is where review and routing both read it from.
    preview.applyEffects(effects);
    // `flowRef` rather than `flow`: this must describe the task as it was when Submit was pressed,
    // and it is called from a poll that deliberately does not re-run on every render.
  }, [preview, taskId, firedStates, previewRoll]);

  useEffect(() => {
    if (!gradeRequest) return;
    const root = previewRootRef.current;
    if (!root) return;
    const nameFor = (id: string) => blocksRef.current.find(b => b.id === id)?.name?.trim()
      || i18n.t('task.submission.heading');
    const deadline = performance.now() + GRADE_SETTLE_CAP_MS;
    /*
     * Settled means the VERDICTS have stopped moving, not that their number has.
     *
     * This counted results, which works on a first submission because they appear one by one from
     * nothing, and cannot work on a resubmit: every block already carries a verdict from the last
     * attempt, so the count is final on the very first read and the poll stopped two frames later
     * with whatever was in the DOM. A student who corrected an answer saw the blocks agree they
     * were right while the summary still reported one wrong, and only a THIRD submission caught
     * up. A count cannot see incorrect become correct; a signature can.
     *
     * `SETTLE_MIN_FRAMES` is the honest part: a block grades in an effect, so its verdict lands in
     * a render after the one that carried the submit, and a reading taken too early is stable and
     * stale. Holding for a few frames is a heuristic, not a proof. The proof would be stamping
     * each verdict with the submission it answers and waiting for the current one, which is worth
     * doing if this is ever seen again.
     */
    const SETTLE_MIN_FRAMES = 3;
    const signature = (rows: ReturnType<typeof readBlockResults>) =>
      rows.map(r => `${r.blockId}:${r.evaluation}`).join('|');
    let previous: string | null = null;
    let frames = 0;
    let frame = 0;
    const poll = () => {
      const results = readBlockResults(root, nameFor);
      const now = signature(results);
      frames += 1;
      const settled = results.length > 0 && now === previous && frames >= SETTLE_MIN_FRAMES;
      if (settled || performance.now() >= deadline) {
        setSubmission(summarize(results));
        gradeFlow(firedStates.read());
        return;
      }
      previous = now;
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
    // `blocks` is read through a ref rather than depended on: it changes identity on every render,
    // and this must run once per SUBMIT, not once per render that happens to follow one. Same for
    // `gradeFlow`, whose identity moves with the session it records into — depending on it would
    // re-grade the last submission every time the session changed, which recording one does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeRequest]);
  /**
   * WHAT THE LAST SUBMISSION TO THIS TASK DECIDED — read once, used by both readers below.
   *
   * `previewNext` already took this position and said why: *"read off the recorded attempt rather
   * than recomputed, so the button cannot disagree with what the log says happened"*. The other
   * reader did not follow it, which is the whole of the bug the next comment describes.
   */
  /*
   * WITHIN THIS VISIT, which is the whole correctness of it.
   *
   * Read over the session, the attempt that sent a student away LAST time still says this task
   * is finished — so a student who loops back is met by a locked task and a Continue button,
   * and the loop they were sent round cannot be re-run. The attempt that ended a previous visit
   * ended that visit; this one has not been submitted yet.
   */
  const lastOutcome = useMemo(
    () => (isPreview && taskId && preview.session
      ? lastAttemptFor(preview.session, taskId, 'visit')?.outcome
      : undefined),
    [isPreview, taskId, preview.session],
  );

  /**
   * Nothing left to answer, so the task stops accepting answers.
   *
   * THE FLOW DECIDES THIS, NOT THE BLOCKS. It used to be recomputed from the verdicts on screen —
   * `incorrect === 0 && neutral === 0` — which put two different questions in charge of one
   * button: *are the blocks satisfactory* and *is there somewhere to go*. When they disagreed the
   * student had a destination the UI would not let them take, which is exactly what happened on a
   * split pathway: `resolveFlow` chose the goto and the bar stayed on Submit.
   *
   * The owner's rule: *"exit of the page is dependent only on what the flow event says,
   * regardless of the state of the blocks."* A `stay` keeps them here; anything else is the task
   * being finished with them. Block verdicts still decide what the flow chose — they just no
   * longer get a second vote afterwards.
   *
   * All-or-nothing, not per block: locking each correct answer as it lands would make the second
   * attempt a different task from the first, with the student's own earlier work frozen into it as
   * scaffolding. A retry is a retry of the whole thing.
   */
  const previewComplete = previewTaskFinished(lastOutcome);

  /**
   * The exit text for the submission being shown, or nothing.
   *
   * Read from the SLOT the attempt recorded rather than re-derived: the action alone cannot say
   * which exit fired, and re-running the ladder here would be a replay that can disagree with
   * what actually happened. Content is looked up in the live flow, which is correct for both
   * readers of it — a teacher editing sees their edit immediately, and a student's session is
   * pinned to an edition, so the flow they ran against cannot move under them.
   */
  const lastSlot = useMemo(
    () => (isPreview && taskId
      ? preview.session?.attempts.filter(a => a.taskId === taskId).at(-1)?.slot
      : undefined),
    [isPreview, taskId, preview.session],
  );
  const exitContent = lastSlot ? exitContentFor(flow, lastSlot) : undefined;

  /**
   * Stepping back through earlier VISITS to this task, locked.
   *
   * Visits, not attempts. Three submissions without leaving the task are one visit, and flipping
   * through them would mostly show the same answer three times; what a teacher is actually asking
   * is "what did they have when they left here", which is one entry per arrival. `visitsOf` was
   * written for exactly this and keeps the definition in one place.
   *
   * `null` is live. Reset on the way into a different task, and whenever a new submission lands,
   * because both make the cursor point at a list that has changed under it.
   */
  const previewVisits = useMemo(
    () => (isPreview && taskId ? preview.visits(taskId) : []),
    [isPreview, taskId, preview],
  );
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const reviewing = reviewIndex === null ? null : previewVisits[reviewIndex] ?? null;
  useEffect(() => { setReviewIndex(null); }, [taskId, previewSubmitCount]);

  /**
   * Everything about the LAST task, cleared on the way into the next one.
   *
   * Routing does not remount this page -- React Router keeps the component and swaps the param --
   * so without this the submission state walks forward with the student. Arriving at task two
   * showed task one's verdict: its feedback already fired, its counts in the dialog, and Submit
   * replaced by a Continue that was spent before they had answered anything.
   *
   * Keyed on the task, NOT on `isPreview`: entering and leaving Preview has its own resets, and
   * folding them together would clear a verdict the teacher is still reading.
   */
  useEffect(() => {
    setSubmission(null);
    setSummaryOpen(false);
    setPreviewSubmitCount(0);
    setPreviewResetKey(k => k + 1);
  }, [taskId]);


  /**
   * APPLY THIS VISIT'S ARRIVAL POLICY, which for effects means a deletion and so happens once.
   *
   * The answers half needs nothing here -- `usePriorAnswer` decides it at read time and a
   * remount re-decides it correctly. Dropping a latched effect cannot work that way: it is a
   * change to the session, and running it twice would wipe a scaffold a Change turned on since
   * arriving. `settle` refuses a visit it has already settled, which is what makes this safe to
   * call from an effect that re-runs whenever the block list is rebuilt.
   *
   * Blocks whose policy KEEPS their effects are simply left out of the list.
   */
  useEffect(() => {
    if (!isPreview || !taskId) return;
    const policy = flow.onArrival;
    preview.settle(taskId, blocks.filter(b => !arrivalKeepFor(policy, b.id).effects).map(b => b.id));
  }, [isPreview, taskId, blocks, flow.onArrival, preview]);

  /**
   * Leaving Preview resets it COMPLETELY, so re-entering is always a fresh run.
   *
   * The comment above says entering and leaving Preview "has its own resets". It did not, and two
   * things leaked across the boundary:
   *
   *  - `summaryOpen`, so the results dialog stayed up over the editor after exiting — reported as
   *    a "0 correct, 1 incorrect" overlay that would not go away;
   *  - `submission`, which is quieter and worse. `previewComplete` derives from it and draws a
   *    transparent click-shield across the task, so re-entering Preview after finishing a run
   *    gave a preview where nothing responded to a click. Reported as "everything is disabled".
   *
   * The owner's rule is the simple one and this is it: whatever the exit — Finish, the Exit
   * button, routing to `end` — the next entry starts from nothing. Resetting on the way OUT
   * rather than on the way in means there is no ordering to get wrong between clearing this
   * state and the new session being created.
   *
   * Deliberately NOT the announcement `resetPreview` makes: that speaks because the page changes
   * under a teacher who is still looking at it. Here they are leaving.
   */
  useEffect(() => {
    if (isPreview) return;
    setSubmission(null);
    setSummaryOpen(false);
    setPreviewSubmitCount(0);
    // Remounts every block on re-entry, which is what drops restored answers and fired feedback.
    setPreviewResetKey(k => k + 1);
  }, [isPreview]);

  /**
   * Where the last submission actually sent them, as opposed to where the config suggests.
   *
   * Read off the recorded attempt rather than recomputed, so the button cannot disagree with what
   * the log says happened -- the two would drift the moment an effect or an attempt count changed
   * the answer between the submission and the render. The rule itself is `previewRouteFor`.
   */
  const previewNext = useMemo(() => {
    if (!isPreview || !taskId || !experience) return null;
    /*
     * A TASK WITH NOTHING TO GRADE ROUTES FROM ITS CONFIG, because the attempt this button reads
     * is the one pressing it creates. Waiting for a record would leave a display-only page with a
     * dead Continue for ever. `resolveFlow` over empty results is the derivation rather than
     * reaching for `allCorrectAction` directly: every block passed, vacuously, which is the same
     * ladder any other task walks. The same fallback `previewEnds` has always used for its label.
     */
    const outcome = lastOutcome
      ?? (taskHasGradableBlock(blocks) ? undefined : resolveFlow(flow, {}));
    return previewRouteFor(outcome, taskId, experience.tasks);
  }, [isPreview, taskId, experience, lastOutcome, blocks, flow]);

  /**
   * Walk the route the last submission chose.
   *
   * Navigating is enough to carry Preview with them: the run lives above the router, and
   * `isPreview` is derived from it, so the next task opens in the student's view rather than the
   * editor. Ending drops the run, which leaves the teacher editing the task they finished on --
   * the same place Exit leaves them, and the one they are most likely to want.
   */
  function followPreviewRoute() {
    if (!previewNext) return;
    /*
     * A DISPLAY-ONLY TASK RECORDS ITS VISIT ON THE WAY OUT.
     *
     * It never grades, so it never reached `gradeFlow` and left no trace in the ATTEMPT log: no
     * submission, no outcome, nothing saying where it sent them. The arrival log records that
     * they were here — that is its job, and a display page is exactly the case it was added for
     * — but an arrival cannot say what moved them on, and `previewNext` reads the outcome.
     *
     * Pressing Continue IS the submission for such a task, so that is where the record belongs.
     * The outcome comes from the same `resolveFlow` the button's own route did, over empty
     * results, so the log says what actually moved them rather than a second guess at it.
     */
    if (taskId && !taskHasGradableBlock(blocks)) {
      const settled = resolveFlowOutcome(flow, {});
      preview.record({
        taskId,
        at: new Date().toISOString(),
        answers: {},
        results: {},
        signatures: {},
        outcome: settled.action,
        // Recorded even though a content-only task shows no exit text: the log keeps one shape,
        // and the slot is what every later reader asks for.
        slot: settled.slot,
      });
    }
    if (previewNext.kind === 'end') preview.end();
    else {
      /* THIS one is an arrival; the stepper's navigations are not. Marked before the route
         changes, because the provider's arrival effect fires on the new pathname. */
      preview.arriveByFlow(previewNext.taskId);
      // `?experience=` is not decoration: the task page reads its experience from that one param
      // and renders "Missing experience context" without it, so a route that drops it sends the
      // student to an error page instead of to the next task.
      navigate(`/app/task/${previewNext.taskId}?experience=${experienceId}`);
    }
  }


  /**
   * When the previewed task has actually re-rendered — the only thing an accessibility scan cares
   * about. Entering Preview, submitting, resetting, rerolling; nothing else changes the tree.
   *
   * ONE value, read by both the badge below and the panel's own scan, because the two disagreeing is
   * the bug that produced it: the badge already recounted on submit while the open panel scanned
   * once and never again, so submitting could leave the badge reading 4 above a list of 2. Feedback
   * bodies are authored rich text and only enter the DOM on submit, so they are precisely the
   * content a stale scan misses.
   *
   * `summaryOpen` is in here for a narrower reason than the rest: the summary dialog marks the task
   * `aria-hidden` behind it, and `isHidden` is exactly what both scans skip on. A scan that happened
   * to land while the dialog was up would read an empty task and stay that way, since closing the
   * dialog changes nothing else on this list. Counting it means the scan simply runs again once the
   * task is back.
   */
  const previewRevision = useMemo(
    () => `${previewSubmitCount}:${previewResetKey}:${previewRoll ? Object.keys(previewRoll).length : 'none'}:${blocks.length}:${summaryOpen}`,
    [previewSubmitCount, previewResetKey, previewRoll, blocks, summaryOpen],
  );
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  /**
   * The task whose content is on screen — which trails `taskId` by one commit.
   *
   * `taskId` comes from the URL and changes the instant an arrow is clicked; the blocks follow
   * when the effect below applies them. Anything that must switch WITH the content rather than
   * with the address reads this instead, and `applyTaskRef` is the one place it is set, in the
   * same batch as the blocks themselves.
   */
  const [shownTaskId, setShownTaskId] = useState<string | undefined>(undefined);

  /*
   * WHEN that exit fired. Only while a summary is being READ BACK on return — the submission
   * the student just made is the present, and a chip in it should quote what they have in front
   * of them rather than freezing at the instant they pressed Submit.
   */
  const exitAt = useMemo(
    () => (isPreview && shownTaskId && !previewSubmitCount
      ? preview.session?.attempts.filter(a => a.taskId === shownTaskId).at(-1)?.at
      : undefined),
    [isPreview, shownTaskId, previewSubmitCount, preview.session],
  );

  /**
   * ARRIVING AT A TASK THAT IS ALREADY FINISHED SHOWS ITS SUMMARY AGAIN.
   *
   * Flipping back through a run, a student meets the screen they left each task on — the counts
   * and whatever the teacher wrote there — and dismisses it to look at the work underneath. It
   * comes back the next time they arrive, deliberately: its PRESENCE is the wayfinding signal,
   * so the task they are currently working on is the only one without it.
   *
   * BUT ONLY FOR THE VISIT IT BELONGS TO, which is what `'visit'` buys here. A loop that sends a
   * student back to retry a task is an arrival like any other, and this greeted them with the
   * verdict from the attempt that sent them away — over a task they had been returned to in
   * order to answer again, with the arrival policy having possibly just cleared it. Reported as
   * exactly that: the summary should not be there on a retry via flow.
   *
   * The two cases were already told apart and nothing here was reading it. A FLOW arrival
   * records a visit; the stepper deliberately does not, so that stepping back to look at a
   * finished task leaves it finished. Scoping to the visit therefore keeps the wayfinding above
   * and drops the verdict exactly where the student has been sent to try again.
   *
   * Runs AFTER the reset above, which clears the previous task's verdict; this puts back the
   * one belonging to the task being entered. Keyed on `taskId` for the same reason — an
   * arrival, not a render — so dismissing it and carrying on does not bring it straight back.
   *
   * Rebuilt from the recorded attempt rather than from the screen: the task has just mounted
   * fresh, so there is no graded DOM to read.
   *
   * KEYED ON `shownTaskId`, NOT `taskId`, and the difference is the whole correctness of it.
   * `taskId` changes the instant an arrow is clicked; the blocks follow a commit later. Keyed on
   * the address, this ran while `blocksRef` still held the PREVIOUS task's blocks, so the
   * recorded results matched nothing and the dialog read "0 questions correct" over a task the
   * student had got right. `shownTaskId` is set in the same batch as the blocks themselves.
   */
  useEffect(() => {
    if (!isPreview || !shownTaskId) return;
    const taskId = shownTaskId;
    const last = preview.session && lastAttemptFor(preview.session, taskId, 'visit');
    if (!last || !previewTaskFinished(last.outcome)) return;
    const named = (id: string) => blocksRef.current.find(b => b.id === id)?.name?.trim()
      || blockLabel(blocksRef.current.find(b => b.id === id)?.type ?? '');
    setSubmission(summarizeRecorded(last.results, blocksRef.current, named));
    setSummaryOpen(true);
    // `preview.session` is deliberately NOT a dependency: the summary belongs to the arrival,
    // and re-running it on every recorded attempt would reopen the dialog mid-task.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownTaskId, isPreview]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showLibrary, setShowLibrary] = useState(true);
  const [blockFeedbackModes, setBlockFeedbackModes] = useState<Record<string, boolean>>({});
  const [highlightedBlockId, setHighlightedBlockId] = useState<string | null>(null);
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  /**
   * What the guarded exit will DO, held while the modal is up.
   *
   * It used to be implicit: the only way out was the back chevron, so "leave" meant the
   * experience and nothing else. The bar now also steps between sibling tasks, opens the
   * account pages and logs out — and logout is why this holds a thunk rather than a URL, since
   * it has to clear the token on the way and only if the teacher really goes.
   */
  const pendingExitRef = useRef<(() => void) | null>(null);

  // Breakpoint detection for adaptive layout
  const [isPhone, setIsPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  const [needsSubbar, setNeedsSubbar] = useState(() => window.matchMedia('(orientation: portrait) and (max-width: 1023px)').matches);
  const [isShortLandscape, setIsShortLandscape] = useState(() => window.matchMedia(SHORT_LANDSCAPE_QUERY).matches);
  useEffect(() => {
    const entries = [
      { q: PHONE_QUERY, set: setIsPhone },
      { q: '(orientation: portrait) and (max-width: 1023px)', set: setNeedsSubbar },
      { q: SHORT_LANDSCAPE_QUERY, set: setIsShortLandscape },
    ] as const;
    const listeners = entries.map(({ q, set }) => {
      const mq = window.matchMedia(q);
      const h = (e: MediaQueryListEvent) => set(e.matches);
      mq.addEventListener('change', h);
      return { mq, h };
    });
    return () => listeners.forEach(({ mq, h }) => mq.removeEventListener('change', h));
  }, []);
  /*
   * A PHONE, HELD SIDEWAYS — the `&& isPhone` is load-bearing, and mirrors `useIsPhoneLandscape`.
   *
   * Short-and-wide on its own is also a laptop window with devtools docked at the bottom. That
   * window is not a phone, so the right-hand column below renders (it is gated on `!isPhone`)
   * — and if the header slot answered to geometry alone it would render its copy of the panel
   * toggles and Preview button too, giving the teacher two of each. The two predicates have to
   * be unable to disagree, which is what composing one from the other buys.
   */
  const isPhoneLandscape = isPhone && isShortLandscape;
  // Phone: no right panel. Tablet portrait: right panel visible only when something is in it.
  // Desktop + tablet landscape: always visible.
  const rightPanelVisible = !isPhone && (!needsSubbar || selectedBlockId !== null || variablesPanelOpen || flowPanelOpen || refsPanelOpen);

  const variablesFullScreenRef = useFullScreenPanelA11y(isPhone && variablesPanelOpen, () => setVariablesPanelOpen(false));
  const flowFullScreenRef = useFullScreenPanelA11y(isPhone && flowPanelOpen, () => setFlowPanelOpen(false));
  const refsFullScreenRef = useFullScreenPanelA11y(isPhone && refsPanelOpen, () => setRefsPanelOpen(false));
  /* No focus trap for the read-through: it was the PROBLEMS sheet that needed one, being a
     full-screen dialog over the task. The read-through on a phone is a frame AROUND the task
     (`phoneFrame`) precisely so a teacher can keep touching the thing being read, and trapping
     focus inside it would take that away. */

  // Stack refs — mutations are synchronous, React state (canUndo/canRedo/labels) is derived
  // Held in a ref so the load effect, which is declared above the stack, can clear the history
  // when a different task arrives in the same page.
  const stackRef = useRef<UndoStack | null>(null);
  // Phone block-editing session — completely separate from task-level undo stack
  const phoneSessionRef = useRef<PhoneSession | null>(null);
  const blocksRef = useRef<Block[]>([]); // always mirrors blocks state, updated synchronously
  const variablesRef = useRef<TaskVariable[]>([]); // always mirrors variables state
  /**
   * What a block sees: the experience's variables plus this task's own.
   *
   * `variables` stays the TASK's throughout — every add, rename and delete below operates on it,
   * and the two scopes are edited separately. This is the read side only, and it is what the
   * context carries, so a chip resolves against either scope without knowing which it came from.
   */
  const experienceVariables = experience?.variables;
  const variablesInForceHere = useMemo(
    () => variablesInForce(experienceVariables ?? [], variables),
    [experienceVariables, variables],
  );
  /**
   * THE NAMES THIS TASK OWNS, which is what the `·` on a chip means.
   *
   * Derived from the SAME two lists the merge above reads, and derived rather than stored: the
   * cross-task refusal tells teachers to promote a variable, and a scope baked into a chip at
   * insert time would go stale at exactly the moment they do.
   *
   * It is the in-force list minus the experience names rather than `variables` itself, so a
   * task copy that an experience variable is currently shadowing is NOT marked local — it is
   * not in force at all, and marking it would point the teacher at the definition that is
   * being ignored. `variablesInForce`'s own rule, read the other way round.
   */
  /** The other half of the same split, for the cross-task rules — see `AnswerRefScope`. */
  const experienceVariableNameSet = useMemo(
    () => new Set((experienceVariables ?? []).map(v => v.name)),
    [experienceVariables],
  );
  const localVariableNames = useMemo(() => {
    const shared = new Set((experienceVariables ?? []).map(v => v.name));
    return new Set(variablesInForceHere.filter(v => !shared.has(v.name)).map(v => v.name));
  }, [experienceVariables, variablesInForceHere]);
  /** For the paste path, which runs from handlers and must not close over a stale list. */
  /**
   * WHAT THE ANSWER-REFERENCE CHIPS AND PICKER LOOK INTO.
   *
   * Reachability is a question about the whole experience, so it cannot be answered by a block
   * or even by this task — the provider hands down the task list and where we are in it, and
   * every chip asks from there. Absent outside the task editor, which is exactly why the
   * status iconography can never reach a student: with no provider there is no status.
   */
  /**
   * THE ONE WRITE PATH FOR ALIASES, mirroring `writeExperienceVariables`.
   *
   * Local state first and the PATCH after, unawaited, because an alias is created in the middle
   * of inserting a chip: the chip needs the id in the same tick, and waiting on a round trip
   * would either block the insert or leave a chip pointing at an alias that does not exist yet.
   * The id is minted here, so it is real before the server has heard of it.
   */
  const writeAliases = useCallback((next: AnswerRefAlias[]) => {
    setExperience(prev => (prev ? { ...prev, answerRefAliases: next } : prev));
    aliasesRef.current = next;
    api.patch(`/experiences/${experienceId}`, { answerRefAliases: next }).catch(() => {});
  }, [experienceId]);

  /**
   * Find the alias for an address, or make one — the insert path, returning an id either way.
   *
   * This is what makes "put that reference in again and you get the alias" true without a
   * sweep: the address is looked up before anything is minted, so a second chip at the same
   * address joins the first rather than starting a rival name.
   */
  /**
   * What an address is CALLED right now, stored on the member for the day it stops resolving.
   *
   * Flow's `targetTitle` device. Read only when the live lookup fails, so it cannot show a
   * stale name to anyone — a member whose target still exists is always described from the
   * target itself.
   */
  const aliasMemberLabel = useCallback((ref: AnswerRef): string => {
    const task = experienceRef.current?.tasks.find(t => t.id === ref.taskId);
    const block = task?.blocks?.find(b => b.id === ref.blockId);
    return [task?.title, block?.name].filter(Boolean).join(' \u00b7 ');
  }, []);

  const ensureAlias = useCallback((ref: AnswerRef): string => {
    const decision = aliasToInsert(aliasesRef.current, ref);
    if ('existing' in decision) return decision.existing.id;
    const created: AnswerRefAlias = {
      id: `ara_${crypto.randomUUID()}`,
      ...decision.create,
      members: decision.create.members.map(m => ({ ...m, label: aliasMemberLabel(m) })),
    };
    /*
     * NOT a step of its own.
     *
     * Creating an alias only ever happens as part of inserting a chip, and that insert is
     * already one step — the rich-text edit. A second op here would make one gesture take two
     * undos, with a middle state holding an alias nothing names. Undoing the insert therefore
     * leaves the alias behind, unused and harmless, and re-inserting the same reference finds
     * it again rather than minting a rival.
     */
    writeAliases([...aliasesRef.current, created]);
    return created.id;
  }, [writeAliases, aliasMemberLabel]);

  /**
   * DROP ANY ALIAS NO CHIP NAMES, at the moment the teacher leaves the block.
   *
   * An alias is minted on insert and nothing ever removed one, so every deleted chip left a
   * name behind — owning its address, and on no screen anywhere, because the manager lists
   * references used in THIS task. Not harmless either: the next chip written at that address
   * finds it by address and silently inherits whatever the old one was called.
   *
   * NOT WHEN THE CHIP GOES — when the BLOCK does. Deleting a chip and putting it straight back
   * is one thought, and a sweep on the keystroke would destroy the name in between and leave
   * the restored chip naming nothing. Leaving the block is the teacher saying "done", which is
   * the same boundary `AuthoringPhase` calls `settled`.
   *
   * UNDOABLE, as its own step, which is the one thing it must be. The chip's deletion was a
   * step of its own and this is pushed after it, so the first undo brings the names back and
   * the second brings the chip back — in that order, never a chip restored onto a dead alias.
   * (Creating an alias is deliberately NOT a step — see `ensureAlias` — because it rides the
   * insert. Removing one cannot ride anything: nothing else is happening.)
   *
   * The server has the last word. It refuses to drop an alias a chip still names, so a census
   * taken against a stale copy of another task cannot destroy a live name — it just leaves one
   * for the next sweep.
   */
  const sweepUnusedAliases = useCallback(() => {
    if (readOnlyRef.current) return;
    if (!sweepPendingRef.current) return;
    /*
     * NEVER WHILE THE TASK HAS UNSAVED EDITS, which is the one ordering that could destroy a
     * live name. Inserting a chip writes its alias immediately — the id has to be real before
     * the chip can name it — while the chip itself waits on the autosave. Sweeping in that
     * gap would find the alias cited by nothing ANYWHERE, here and on the server both, and
     * the save would then land a chip pointing at something already deleted.
     *
     * So the sweep waits for the save rather than skipping: the flag stays up and the save's
     * own success calls back here. Leaving a block is still what arms it.
     */
    if (JSON.stringify(blocksRef.current) !== savedBlocksJsonRef.current) return;
    sweepPendingRef.current = false;
    const held = aliasesRef.current;
    if (held.length === 0) return;
    const exp = experienceRef.current;
    if (!exp || !taskId) return;
    // The LIVE blocks for the task being edited: `experience.tasks` only catches up after the
    // autosave round trip, and a chip deleted a second ago is exactly what this is asking about.
    const tasks = (exp.tasks ?? []).map(tk => (tk.id === taskId ? { ...tk, blocks: blocksRef.current } : tk));
    const gone = unusedAliases(tasks as never, held);
    if (gone.length === 0) return;
    const dropped = new Set(gone.map(g => g.id));
    const next = held.filter(a => !dropped.has(a.id));
    stackRef.current?.push({
      type: 'update-aliases',
      label: i18n.t('blocks.answerRef.manager.sweepOp', { count: gone.length }),
      prev: held,
      next,
    });
    writeAliases(next);
  }, [taskId, writeAliases]);

  /**
   * DROP A NAME NOTHING CITES — the manual half of `sweepUnusedAliases`.
   *
   * The sweep is armed by leaving a block or deleting one, and waits for the save. Neither
   * reaches a name stranded before either existed, so the manager offers this on its
   * `unused` rows. Reported as "i don't see a way to clean this up".
   *
   * UNDOABLE, by the same op the sweep pushes: an alias is small, the list is the unit of
   * change, and one undo has to bring a name back whichever route removed it.
   *
   * No guard on it being unused. The manager only shows the control on an unused row, and a
   * second opinion here would be a second definition of "unused" to keep in step with
   * `unusedAliases`. The server still refuses to drop one a chip names.
   */
  const removeAlias = useCallback((id: string) => {
    if (readOnlyRef.current) return;
    const held = aliasesRef.current;
    const next = held.filter(a => a.id !== id);
    if (next.length === held.length) return;
    stackRef.current?.push({
      type: 'update-aliases',
      label: i18n.t('blocks.answerRef.manager.removeOp'),
      prev: held,
      next,
    });
    writeAliases(next);
  }, [writeAliases]);

  /**
   * Rename, re-point, or change an alias's members — universal, and UNDOABLE.
   *
   * A snapshot op, like flow's. Every one of these edits is already "replace the list", the
   * list is small, and the alternative is four per-item op types for four verbs that all do
   * the same thing. `coalesce` on the name so typing one is a step rather than a step per
   * keystroke.
   */
  const updateAlias = useCallback((id: string, patch: Partial<Omit<AnswerRefAlias, 'id'>>) => {
    if (readOnlyRef.current) return;
    const prev = aliasesRef.current;
    /*
     * A FAMILY KEEPS EXACTLY ONE NAME, which `applyAliasPatch` is what enforces.
     *
     * The owner's rule: "renamed sibling overrides the family and becomes the family name ...
     * this is the easiest, as it keeps the family coordinated everywhere all the time". With
     * one name in play the question "which of several wins" never arises.
     */
    const next = applyAliasPatch(prev, id, patch);
    stackRef.current?.push({
      type: 'update-aliases',
      label: i18n.t('blocks.answerRef.manager.reanchorOp'),
      prev,
      next,
      ...('name' in patch ? { coalesce: `alias-name:${id}` } : {}),
    });
    writeAliases(next);
  }, [writeAliases]);

  /*
   * THE TASK BEING EDITED COMES FROM LOCAL STATE, not from the server's copy.
   *
   * `experience.tasks` only catches up after the autosave round trip, so everything derived
   * here lagged a save behind for the one task the teacher is actually changing: chip status,
   * the panel, and the warning on the grading-mode button. Reported as the warning taking "a
   * while to come onto the button" — not a probe, just the wait for a PUT.
   *
   * Only the current task is swapped. Every other task genuinely is whatever the server last
   * said, and this page holds no newer version of them.
   */
  /*
   * WHAT THE TEACHER SAID AN EARLIER ANSWER WAS, while testing. Bench state, like a roll:
   * it belongs to this sitting and never to the document, where it would be a second answer
   * key nobody asked for. Cleared when the task changes, because an alias set on one task's
   * bench says nothing about the next.
   */
  /*
   * ONE ROLL PER TASK FOR EVERY RECALLED ANSWER — see `benchVariablesFor`.
   *
   * A recalled answer belongs to another task, so no single block's draw has a claim on it,
   * and two blocks quoting one answer must not show two numbers. Drawn here, once each, and
   * re-drawn only when the variables themselves change.
   *
   * PER TASK, because a flat map cannot answer the question. This was one roll of the VIEWING
   * task's variables in force, which is right for the experience's variables — they are one
   * definition shared by everything — and wrong for a task's own. A source block reading its
   * own task's `{time}` found nothing, or found the viewing task's `time`, which is a
   * different quantity wearing the same name.
   *
   * The experience's roll is drawn ONCE and shared by every task, which is what makes two
   * tasks quoting one experience variable agree. Each task's own variables are drawn
   * separately and merged UNDER it, matching `variablesInForce`'s rule that the experience
   * wins a collision — so the two layers can never disagree about a name.
   */
  const asNumbers = (rolled: Record<string, string>) => Object.fromEntries(
    Object.entries(rolled)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((e): e is [string, number] => !Number.isNaN(e[1])),
  );
  /*
   * KEYED ON THE VARIABLES THEMSELVES, never on the array holding them.
   *
   * These were keyed on `experienceVariables` and `experience.tasks`, which are fresh objects
   * after any edit anywhere in the experience — so typing a single character into a block
   * re-rolled every variable in it. On a chip's digit rows that was worse than churn: the
   * quoted number changed as the teacher typed a precision for it, which reads as the digit
   * setting CHANGING THE ANSWER when all it changes is how many digits of it are shown.
   *
   * A roll is bench state and should outlast every edit that does not touch what it draws
   * from. Stringifying is how this file already keys a roll on its content rather than its
   * identity, and these lists are short.
   */
  const experienceVarsKey = JSON.stringify(experienceVariables ?? []);
  const taskVarsKey = JSON.stringify((experience?.tasks ?? []).map(tk => [tk.id, tk.variables ?? []]));
  const experienceRoll = useMemo(
    () => asNumbers(rollVariableValues(experienceVariables ?? [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [experienceVarsKey],
  );
  const taskRolls = useMemo(() => Object.fromEntries(
    (experience?.tasks ?? []).map(tk => [tk.id, asNumbers(rollVariableValues(tk.variables ?? []))]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [taskVarsKey]);
  const benchVariablesFor = useCallback(
    (ofTask: string) => rollsInForce(taskRolls[ofTask] ?? {}, experienceRoll),
    [taskRolls, experienceRoll],
  );

  const [testAnswers, setTestAnswers] = useState<Record<string, unknown>>({});
  useEffect(() => { setTestAnswers({}); }, [taskId]);
  /**
   * ONE PICK, WRITTEN TO THE WHOLE FAMILY — see `familyKeysFor`.
   *
   * The answer a stand-in holds is the source BLOCK's, and a Numerical's value, uncertainty
   * and unit are three aliases over one block. Stored per alias, each could hold a different
   * draw, and the sentence would read a value from one beside an uncertainty from another:
   * "you end up with text disconnected from the answer."
   */
  const setTestAnswer = useCallback((key: string, answer: unknown | null) => {
    setTestAnswers(prev => {
      const keys = familyKeysFor(key, aliasesRef.current);
      if (answer === null) {
        if (!keys.some(k => k in prev)) return prev;
        const next = { ...prev };
        for (const k of keys) delete next[k];
        return next;
      }
      const next = { ...prev };
      for (const k of keys) next[k] = answer;
      return next;
    });
  }, []);

  const answerRefScope = useMemo(
    () => (experience && taskId
      ? {
        tasks: (experience.tasks ?? []).map(tk => (tk.id === taskId ? { ...tk, blocks } : tk)),
        currentTaskId: taskId,
        aliases: experience.answerRefAliases ?? [],
        /* Derived from the experience's own list, so it cannot disagree with the panel that
           shows them — and NOT from `localVariableNames`'s complement, which would make the
           two definitions of "shared" two things that have to be kept in step. */
        experienceVariableNames: experienceVariableNameSet,
        ensureAlias,
        updateAlias,
        removeAlias,
        testAnswers,
        setTestAnswer,
        benchVariablesFor,
        /*
         * A recalling block is almost never in the task you are looking at — that is what
         * recall MEANS — so an issue naming one has to be able to leave.
         *
         * Everything this closes over is either a dependency below or fixed for the life of
         * the page (`navigate`, `experienceId`, the setters), so the memo holding a closure
         * from an earlier render cannot send it anywhere stale.
         */
        openUsage: (usageTaskId: string, blockId: string) => {
          if (usageTaskId === taskId) {
            setSelectedBlockId(blockId);
            requestAnimationFrame(() => flashBlock(blockId));
            return;
          }
          navigate(`/app/task/${usageTaskId}?experience=${experienceId}&block=${blockId}`);
        },
      }
      : null),
    [experience, taskId, blocks, ensureAlias, updateAlias, removeAlias, experienceId, navigate, testAnswers, setTestAnswer, benchVariablesFor, experienceVariableNameSet],
  );

  /**
   * LEAVING A BLOCK IS THE BOUNDARY, and it is one of three gestures: closing the editor,
   * opening a different block, or leaving the task. All three show up here as `selectedBlockId`
   * ceasing to be what it was, which is why this watches the value rather than the handlers —
   * there are six places that clear a selection and a seventh would have been missed.
   */
  const lastSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const left = lastSelectedRef.current;
    lastSelectedRef.current = selectedBlockId;
    // Opening the first block of a session leaves nothing, and a re-render that did not move
    // the selection is not a gesture at all.
    if (!left || left === selectedBlockId) return;
    sweepPendingRef.current = true;
    sweepUnusedAliases();
  }, [selectedBlockId, sweepUnusedAliases]);



  /** Armed by leaving a block, spent by the sweep — which may have to wait for a save first. */
  const sweepPendingRef = useRef(false);


  const variablesInForceRef = useRef<TaskVariable[]>([]);
  variablesInForceRef.current = variablesInForceHere;

  const flowRef = useRef<TaskFlowConfig>(DEFAULT_FLOW_CONFIG); // always mirrors flow state
  const savedBlocksJsonRef    = useRef<string>('[]');
  const savedVariablesJsonRef = useRef<string>('[]');
  const savedFlowJsonRef      = useRef<string>(JSON.stringify(DEFAULT_FLOW_CONFIG));
  // The contentRevision last read from the server for this task — sent back as
  // expectedRevision on every save so the backend can detect a concurrent write (see
  // experiences.service.ts's assertRevision). Updated after every successful load/save.
  const contentRevisionRef = useRef<number>(1);

  /**
   * THE ROLL AS THE BLOCKS READ IT: what was drawn, plus what an earlier answer selected.
   *
   * A driven block's version is not drawn — it is chosen by what the student answered in an
   * earlier task — but it answers the same question every drawn one does, so it rides the same
   * key. Twelve blocks read `taskPreviewRoll[versionRollKey(block.id)]`, and a second channel
   * would be twelve sites that each have to learn about it.
   *
   * MERGED HERE AND NOWHERE ELSE, which is what keeps derived apart from drawn. `previewRoll`
   * is the state the session stores and `rollVersions` refuses to put a driven key in it, so
   * the record stays a record of draws. This adds the derived half for the surfaces that read
   * it, and the two can never write the same key because a block is drawn or driven, never
   * both.
   *
   * It recomputes as the session does, which is the point: a student who walks back and
   * changes the driving answer must come forward to the question that answer now selects.
   */
  const rollInForce = useMemo(() => {
    if (!previewRoll) return null;
    /*
     * A STAND-IN IS AN ANSWER, and the driven axis reads the same one the chips do.
     *
     * `testAnswers` is bench state keyed by ALIAS, and `sampleCorrectAnswer` records it in the
     * block's own answer shape — the same thing `lastAnswersFor` returns — so it drops
     * straight in here. That is what makes one pick move the sentence, the equation AND the
     * question a driven block is asking, instead of the teacher setting a chip and finding
     * the version underneath it unchanged.
     *
     * It OUTRANKS the session, because it only exists where somebody set it: a stand-in is an
     * explicit "pretend they answered this", and a run that already has a real answer is
     * exactly when that is worth saying.
     */
    const standIn = standInAnswers(experience?.answerRefAliases ?? [], testAnswers);
    const derived = drivenVersionRoll(
      blocks,
      answerRefScope?.tasks ?? [],
      (tid, bid) => standIn.at(tid, bid)
        /*
         * THE SESSION, not this visit. A driven block reads what the student answered
         * WHEREVER they answered it; an arrival policy decides what a task shows them on
         * the way back in, not what a reference somewhere else is allowed to see.
         */
        ?? (preview.session ? lastAnswersFor(preview.session, tid, 'session')[bid] : undefined),
      versionRollKey,
      /* An ALIAS-driven block resolves through its members, so the roll needs both: which
         alias, and which names are shared — the second because a member whose options carry
         a task variable is refused, and a refused alias must select nothing rather than
         guess. Omitting these would leave every alias-driven block on `pickVersion`'s
         fallback, which is the first version, silently. */
      /*
       * NOT ANSWERED IS NOT ANSWERED, whatever put the student here — see `drivenVersionRoll`.
       * This used to pass an `attempted` veto so an unreached driver's task left no entry and
       * the block fell back to version one. A student who branched past the driver was then
       * asked the first version, about an answer they never gave.
       *
       * The teacher inspecting mid-experience, which is what the veto was really for, sets the
       * driver with the version cycler beside Edit. That arrives through `standIn.at` above,
       * so it still outranks the session and still moves the version here.
       */
      {
        aliases: experience?.answerRefAliases ?? [],
        experienceVariableNames: experienceVariableNameSet,
      },
    );
    // The common case by far: no block in this task is driven, so the stored roll IS the roll.
    return Object.keys(derived).length ? { ...previewRoll, ...derived } : previewRoll;
  }, [previewRoll, blocks, answerRefScope, preview.session, testAnswers,
    experience?.answerRefAliases, experienceVariableNameSet]);

  const { setNodeRef: setLibraryCancelRef } = useDroppable({ id: 'library-cancel' });
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
  );

  // ── Stack helpers ──────────────────────────────────────────────────────────

  /**
   * The stack, and what the VIEW owes each step it applies.
   *
   * The split is the point: `useUndoStack` knows what a step is and when one ends, and knows
   * nothing about panels; everything below `onApplied` is about showing the teacher what moved, and
   * needs this page's selection. The rules both halves serve are in `docs/undo-redo-contract.md`.
   */
  const stack = useUndoStack({
    readOnly,
    getState: () => ({
      title: taskTitle, blocks: blocksRef.current, variables: variablesRef.current,
      experienceVariables: experienceVariablesRef.current ?? [], flow: flowRef.current,
      answerRefAliases: aliasesRef.current,
    }),
    applyState: (next) => {
      setBlocksWithRef(next.blocks);
      setVariablesWithRef(next.variables);
      /*
       * The experience's variables live in ANOTHER document, so undoing one is a write to it
       * rather than a change to the task being saved. Compared by reference first: every step
       * that did not touch them hands back the same array, which is almost all of them.
       */
      if (next.experienceVariables !== (experienceVariablesRef.current ?? [])) {
        writeExperienceVariables(next.experienceVariables);
      }
      // Aliases, the same way and for the same reason: another document, referentially compared
      // because every alias edit replaces the whole list.
      if (next.answerRefAliases !== aliasesRef.current) writeAliases(next.answerRefAliases);
      // Referentially compared, not deep-compared: every flow edit replaces the whole object, so a
      // step that did not touch flow hands back the identical reference and this is a no-op.
      if (next.flow !== flowRef.current) setFlowWithRef(next.flow);
      if (next.title !== taskTitle) {
        setTaskTitle(next.title);
        api.patch(`/experiences/${experienceId}/tasks/${taskId}`, { title: next.title }).catch(() => {});
      }
    },
    describeBlockEdit: (type) => i18n.t('task.a11y.editedBlock', { label: blockLabel(type) }),
    onApplied: (op, direction, before) => {
      /*
       * AN EXPERIENCE VARIABLE'S RENAME REACHES TASKS THAT ARE NOT LOADED.
       *
       * `applyOp` is a pure reducer over `StackState`, which holds this task's blocks and the two
       * variable lists — so it can put back the row and the chips on screen, and nothing else.
       * The live rename also rewrote blocks in every OTHER task, over the network. Undo was
       * leaving those pointing at a name that no longer existed, which is the same dangling
       * reference the live path was fixed to stop creating.
       *
       * Here rather than in the reducer because this is the one part that cannot be pure. The
       * direction decides which way: undo runs the inverted op, so the name goes next -> prev.
       * Replaying is safe — renaming is idempotent, so a sweep over tasks that already say the
       * target name changes nothing.
       */
      const sweep = crossTaskRenameFor(op, direction);
      if (sweep) {
        const plans = planRenameAcrossTasks(
          experienceRef.current?.tasks ?? [], taskId, sweep.from, sweep.to, sweep.variableId,
        );
        if (plans.length > 0) void renameAcrossTasks(plans);
      }
      if (op.type === 'update-block') { revealUndoneChange(op); return; }
      /**
       * A flow step is invisible unless the panel is open, so open it.
       *
       * The contract's rule is that a step shows the teacher what it moved (D-1). For a block that
       * means scrolling and ringing it; for flow there is nothing on the canvas to ring — the whole
       * change lives in a panel that may well be shut, and an Undo that silently rewires routing is
       * exactly the "it highlighted but did not change anything" failure the other branches guard.
       */
      if (op.type === 'update-flow') {
        if (!isPreview) { setVariablesPanelOpen(false); setFlowPanelOpen(true); }
        return;
      }
      // A block that came back is pointed at; one that went away needs no comment (D-4), beyond
      // closing the editor if it was the thing on screen.
      const appeared = direction === 'undo' ? op.type === 'remove-block'
        : op.type === 'add-block' || op.type === 'duplicate-block';
      const vanished = direction === 'undo' ? op.type === 'add-block' || op.type === 'duplicate-block'
        : op.type === 'remove-block';
      if (appeared && 'block' in op) { touchBlock(op.block.id); return; }
      if (vanished && 'block' in op) { closeIfOpen(op.block.id); return; }
      if (op.type === 'move-block') {
        // After an undo the block sits at `to`; a redo has to read where it was before it moved.
        const moved = direction === 'undo' ? blocksRef.current[op.to] : before.blocks[op.from];
        if (moved) flashBlock(moved.id);
      }
    },
  });

  stackRef.current = stack;

  // These keep the call sites reading the same.
  const pushOp = (op: Op) => stack.push(op);
  const flushPendingUpdate = () => stack.flush();

  function setBlocksWithRef(newBlocks: Block[]) {
    blocksRef.current = newBlocks;
    setBlocks(newBlocks);
  }

  function setFlowWithRef(next: TaskFlowConfig) {
    flowRef.current = next;
    setFlow(next);
  }

  function setVariablesWithRef(newVariables: TaskVariable[]) {
    variablesRef.current = newVariables;
    setVariables(newVariables);
  }

// ── Drag ──────────────────────────────────────────────────────────────────

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current;
    if (data?.source === 'library') {
      setActiveDrag({ source: 'library', blockType: data.blockType as BlockType });
    } else {
      const block = blocks.find(b => b.id === event.active.id);
      if (block) setActiveDrag({ source: 'block', block });
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    if (readOnly) { setActiveDrag(null); return; }
    const data = event.active.data.current;
    if (data?.source === 'library') {
      const blockType = data.blockType as BlockType;
      const overId = event.over ? String(event.over.id) : null;
      if (!overId || overId === 'library-cancel') { setActiveDrag(null); return; }
      if (overId.startsWith('gap-')) {
        handleAddBlock(blockType, parseInt(overId.slice(4)));
      } else {
        const overBlockIndex = blocks.findIndex(b => b.id === overId);
        handleAddBlock(blockType, overBlockIndex !== -1 ? overBlockIndex : undefined);
      }
    } else {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const from = blocks.findIndex(b => b.id === String(active.id));
        const to   = blocks.findIndex(b => b.id === String(over.id));
        if (from !== -1 && to !== -1) {
          setBlocksWithRef(arrayMove(blocks, from, to).map((b, i) => ({ ...b, order: i })));
          pushOp({ type: 'move-block', label: i18n.t('task.a11y.movedBlock'), from, to });
        }
      }
    }
    setActiveDrag(null);
  }

  // ── Load ──────────────────────────────────────────────────────────────────

  /** Undo histories for tasks already visited this session — the rules are in `parked-undo`. */
  const parkedUndoRef = useRef<ParkedUndoMap>(new Map());
  /** The applied task, readable synchronously — `shownTaskId` is state, a commit behind. */
  const shownTaskIdRef = useRef<string | undefined>(undefined);

  /** Set when the teacher chose "Leave without saving" — see `handleLeaveWithoutSaving`. */
  const discardedTaskRef = useRef<string | undefined>(undefined);

  /** On the way out of a task: settle any in-flight edit, then park what the stack holds. */
  function parkUndoHistory(incomingId: string | undefined) {
    const leaving = shownTaskIdRef.current;
    if (!leaving || leaving === incomingId) return;
    if (discardedTaskRef.current === leaving) {
      discardedTaskRef.current = undefined;
      // Drop anything parked on an EARLIER visit too: it describes the same content this exit
      // just rolled back.
      parkedUndoRef.current.delete(leaving);
      return;
    }
    // Flush first, or an edit still inside its debounce is lost: `restore` DROPS a pending edit
    // rather than filing it against whichever task is arriving.
    stackRef.current?.flush();
    // `flush` settles the debounced recorder, which is the DESKTOP path. The phone's is not the
    // same mechanism and does not settle with it — see the call above this one.
    //
    // The phone session has already been closed by the time this runs, so there is nothing more
    // to settle here; this note exists because "flush covers everything in flight" is the
    // reasonable assumption, and it is false.

    const snapshot = stackRef.current?.peek();
    // `contentRevisionRef` still holds the LEAVING task's revision — this runs before the
    // incoming one overwrites it.
    if (snapshot) parkUndo(parkedUndoRef.current, leaving, snapshot, contentRevisionRef.current);
  }

  /**
   * Put one task's content into the editor. The ONLY place that does.
   *
   * Two callers now — the instant path below and the fetch that follows it — and they must set
   * the same things in the same order or the save model drifts: `savedBlocksJsonRef` is what
   * `isDirty` compares against, so a copy of this that forgot one would leave the editor dirty
   * the moment it opened and re-upload a task nobody touched.
   *
   * In a ref so the load effect can call it without listing it as a dependency and re-running
   * the fetch on every render.
   */
  const applyTaskRef = useRef<(exp: Experience, id: string | undefined) => void>(() => {});
  applyTaskRef.current = (exp, id) => {
    const task = exp.tasks.find((t) => t.id === id);
    const revision = task?.contentRevision ?? 1;
    /*
     * CLOSE AN OPEN PHONE EDITOR FIRST — it owns an undo entry that nothing else will commit.
     *
     * The phone does not record a step per field the way the desktop does. A block's edits live
     * in `phoneSessionRef` while its editor is open, with their own in-editor Undo, and only
     * `handlePhoneEditClose` collapses the whole visit into ONE task-level `update-block`. So a
     * session that is still open has contributed nothing to the stack yet.
     *
     * The top bar is NOT hidden while that editor is open, so the stepper arrows are reachable
     * mid-edit. Stepping away used to drop the session silently — which mattered little when the
     * stack was being thrown away anyway, and matters a great deal now that everything else about
     * the task survives the trip. `handleEnterPreview` has closed it this way all along, for the
     * same reason.
     *
     * Before `parkUndoHistory`, so the op lands on the stack that is about to be parked rather
     * than on the arriving task's. It also clears `mobileEditingId`, which would otherwise keep
     * an editor open over a block the new task does not contain.
     */
    if (mobileEditingId) handlePhoneEditClose();
    /*
     * And the DESKTOP equivalent, which the note above describes exactly and did not do.
     *
     * React Router keeps this page mounted when only `:id` changes, so a block left open while
     * the nav bar moved to another task kept its id here. Every block in the ARRIVING task then
     * fails `selectedBlockId !== block.id`, so the canvas dimmed all of them to 0.4 at once while
     * the editor panel opened on a block that is not there — the report was that switching task
     * disabled every block, with nothing left to click that would undo it.
     *
     * Conditional on the block being ABSENT, not on this being a task switch, because this runs
     * for the same task too: another tab saving moves `contentRevision` and re-applies. Closing
     * the editor then would take it out from under a teacher who is working in it, while a block
     * that genuinely went away still clears.
     */
    if (selectedBlockId && !task?.blocks?.some((b) => b.id === selectedBlockId)) {
      setSelectedBlockId(null);
    }
    // Now the history and revision still in hand belong to the task being left, not the one
    // arriving — park before anything below overwrites them.
    parkUndoHistory(id);
    const initial = backfillBlockNames(task?.blocks ?? [], i18n.t);
    const initialVars = task?.variables ?? [];
    const initialFlow = normalizeFlowConfig(task?.flow, initial, i18n.t, i18n.language, exp.tasks);
    setTaskTitle(task?.title ?? '');
    setBlocksWithRef(initial);
    setVariablesWithRef(initialVars);
    setFlowWithRef(initialFlow);
    savedBlocksJsonRef.current    = JSON.stringify(initial);
    savedVariablesJsonRef.current = JSON.stringify(initialVars);
    savedFlowJsonRef.current      = JSON.stringify(initialFlow);
    contentRevisionRef.current    = revision;
    appliedRevisionRef.current    = revision;
    shownTaskIdRef.current        = id;
    setShownTaskId(id);
    stackRef.current?.restore(takeUndo(parkedUndoRef.current, id, revision));

    /*
     * ARRIVED HERE TO FIX ONE BLOCK, so point at it.
     *
     * `?block=` is set by the warning on a recalled answer, which names a block in ANOTHER
     * task — landing on the task and leaving the teacher to find it among twenty is most of
     * the journey and none of the arrival. Consumed immediately: it describes one trip, and
     * a reload should not keep re-selecting.
     */
    const focusBlock = searchParams.get('block');
    const focusExit = searchParams.get('exit');
    const focusFlowCard = searchParams.get('flowCard');
    if (focusBlock && initial.some(b => b.id === focusBlock)) {
      setSelectedBlockId(focusBlock);
      requestAnimationFrame(() => flashBlock(focusBlock));
      /*
       * AND THE CONTROL, where the trip named one.
       *
       * The board's problems panel sends `field` beside `block`: an issue already carries the
       * `data-field-id`s it is about, so a row that says "minimum must be less than maximum" can
       * land on the minimum rather than on the block holding it. `focusIssueFieldAfter` waits for
       * the editor the selection above opens — the control does not exist until it has mounted —
       * and warns rather than failing silently when the id reaches nothing.
       */
      const focusField = searchParams.get('field');
      if (focusField) focusIssueFieldAfter([focusField], () => setSelectedBlockId(focusBlock));
    }
    /*
     * OR THE TRIP WAS ABOUT A PATHWAY, which has no block to point at.
     *
     * Summary feedback lives nowhere on the canvas: it is a modal hanging off one of the flow's
     * exits. A problems panel row about it sends `?exit=<slot id>` instead, and opening the flow
     * panel would only be half the arrival, so the slot goes down to FlowPanel to be opened.
     */
    if (focusExit) {
      handleOpenFlow();
      setPendingExit({ slot: focusExit, blockId: searchParams.get('exitBlock') ?? undefined });
    }
    /* AND A ROUTING FAULT, which is on a card in the flow panel rather than on the canvas. */
    if (focusFlowCard) {
      handleOpenFlow();
      setPendingFlowCard(focusFlowCard);
    }
    if (focusBlock || focusExit || focusFlowCard) {
      const next = new URLSearchParams(searchParams);
      next.delete('block');
      next.delete('field');
      next.delete('exit');
      next.delete('exitBlock');
      next.delete('flowCard');
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    if (!experienceId) { setError(i18n.t('task.errors.missingContext')); setLoading(false); return; }
    let cancelled = false;
    // Re-entering the loading state matters on the fork-redirect path below: that run finishes,
    // clears `loading`, and re-enters this effect under the new id with `experience` still null —
    // which renders the "Task not found" screen for a frame. `redirecting` holds `loading` across
    // the handover so the redirect is invisible.
    let redirecting = false;
    /** Whether the request itself came back, separating a failed fetch from a failed apply below. */
    let loaded = false;
    recordLoadEvent('mount', { page: 'task', experienceId, taskId });

    /*
     * A HOP BETWEEN SIBLING TASKS NEEDS NO WAIT, and used to take one.
     *
     * `loading` replaces the entire page — nav bar, sidebar, title bar and all — with a centred
     * "Loading", so every step between tasks tore the editor down and rebuilt it. Measured at
     * about six frames, and read as a white flash.
     *
     * It was never necessary. Nothing clears `experience` on a task change, so the document is
     * still in state, and an experience document carries EVERY task's blocks. The data for the
     * destination was already in memory while the page sat there showing a spinner for it.
     *
     * So: render it now, and let the fetch below revalidate. Only when the task is genuinely not
     * in hand — a first load, or a different experience — does the loading screen appear.
     *
     * Deliberately NOT taken when the id resolves to a fork (`resolveLiveTaskId`): that path
     * redirects, and showing the stale task first would make the redirect visible.
     */
    const cached = experienceRef.current;
    const cachedTask = cached?.tasks.find((x) => x.id === taskId);
    const instant = !!cached && !!cachedTask && !!taskId
      && resolveLiveTaskId(cached.tasks, taskId) === taskId;
    if (instant && cached) {
      setError('');
      applyTaskRef.current(cached, taskId);
    } else {
      appliedRevisionRef.current = null;
      setLoading(true);
    }
    // React StrictMode (dev) double-invokes this effect on mount, firing this GET twice with no
    // ordering guarantee between the two in-flight requests. Without this guard, whichever
    // request SETTLES last wins regardless of which fired first — if the replayed request is
    // slower and fails (e.g. a transient reconnect right after a laptop/tab wakes from idle, which
    // is exactly when teachers reported this), its `.catch()` overwrites state a moment after the
    // other request already loaded the task correctly, showing "Task not found"/"could not load"
    // for a task that just successfully rendered.
    api.get<Experience>(`/experiences/${experienceId}`)
      .then((exp) => {
        if (cancelled) {
          // A request that came back after its effect was torn down. Recorded because the ONLY
          // reason it is discarded is this guard, and a lap that went wrong needs to show whether
          // two requests were in flight and which one the screen ended up reflecting.
          recordLoadEvent('load', { page: 'task', phase: 'discarded', taskId, ...sampleIds(exp.tasks.map((x) => x.id)) });
          return;
        }
        loaded = true;
        // Follow a versioning fork before concluding the task is missing. The id in the URL is
        // STALE rather than wrong whenever this task was edited after its experience was published.
        const liveId = taskId ? resolveLiveTaskId(exp.tasks, taskId) : taskId;
        recordLoadEvent('resolve', {
          page: 'task', taskId, liveId,
          found: exp.tasks.some((x) => x.id === taskId),
          ...sampleIds(exp.tasks.map((x) => x.id)),
        });
        if (liveId !== taskId) {
          redirecting = true;
          navigate(`/app/task/${liveId}?experience=${experienceId}`, { replace: true });
          return;
        }
        setExperience(exp);
        // Clearing on success matters because `error` is never otherwise reset: one transient
        // failure would keep the error screen up across every later successful re-load of this
        // component, which reads exactly like a task that has gone missing.
        setError('');
        const task = exp.tasks.find((t) => t.id === taskId);
        /*
         * Already shown, and unchanged since: keep what is on screen.
         *
         * The instant path below renders from the experience we already hold, so by the time
         * this lands the right task is usually already up. Re-applying anyway would reset the
         * undo stack a second time and throw away anything typed in the meantime — the save
         * refs would be rebuilt from the server copy while the teacher's keystrokes sat in
         * state, which is how an edit disappears a beat after it is made.
         *
         * `contentRevision` is the test, not a flag: it is unchanged by a local edit (nothing
         * has saved yet) and moves the moment someone else saves, which is exactly when
         * re-applying IS right.
         */
        const sameAsShown = appliedRevisionRef.current !== null
          && appliedRevisionRef.current === (task?.contentRevision ?? 1);
        if (!sameAsShown) applyTaskRef.current(exp, taskId);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Everything after the fetch — resolving a fork, backfilling names, normalizing flow —
        // runs inside the `.then()`, so a throw there arrived here indistinguishable from a dead
        // network and was reported as a task that would not load. `loaded` says which side it came
        // from, and the cause itself was being swallowed with no trace at all.
        console.error('[task] load failed', e);
        recordLoadEvent('load', { page: 'task', phase: loaded ? 'apply-threw' : 'fetch-failed', taskId, error: describeError(e) });
        setError(loaded ? i18n.t('experience.errors.somethingWrong') : i18n.t('task.errors.couldNotLoad'));
      })
      .finally(() => { if (!cancelled && !redirecting) setLoading(false); });
    return () => { cancelled = true; };
  }, [experienceId, taskId, navigate]);


  const task = experience?.tasks.find((t) => t.id === taskId);

  // What the page settled on, recorded from an effect rather than from the render that decides it:
  // a render can be discarded or replayed, and a recorder that logged phantom screens would be
  // describing failures that were never shown. Only the error outcome is worth an entry.
  useEffect(() => {
    if (loading || (!error && experience && task)) return;
    recordLoadEvent('screen', {
      page: 'task', showing: 'error', taskId,
      // Which of the three the teacher is looking at, which the message alone no longer tells us.
      why: error ? 'error-set' : !experience ? 'no-experience' : 'task-absent',
      message: error || undefined,
      ...(experience ? sampleIds(experience.tasks.map((x) => x.id)) : {}),
    });
  }, [loading, error, experience, task, taskId]);

  // GPS targets from every 'gps' block anywhere in the Experience (not just this task), for
  // the cross-block haversine sanity check in gps-block.tsx — the current task's live
  // in-progress `blocks` state is used instead of the last-saved snapshot for whichever task
  // is currently open, since that's more up to date.
  const siblingGpsTargets = useMemo(() => {
    if (!experience) return [];
    return experience.tasks.flatMap((t) =>
      (t.id === taskId ? blocks : t.blocks)
        .filter((b) => b.type === 'gps')
        .map((b) => {
          const d = b.data as { targetLat?: string; targetLng?: string };
          const lat = parseFloat(d.targetLat ?? '');
          const lng = parseFloat(d.targetLng ?? '');
          return Number.isFinite(lat) && Number.isFinite(lng) ? { blockId: b.id, taskTitle: t.title, lat, lng } : null;
        })
        .filter((x): x is { blockId: string; taskTitle: string; lat: number; lng: number } => x !== null)
    );
  }, [experience, blocks, taskId]);

  // ── Block operations ───────────────────────────────────────────────────────

  function handleAddBlock(type: BlockType, insertAfterIndex?: number) {
    if (readOnly) return;
    const newBlock: Block = { id: `blk_${crypto.randomUUID()}`, type, order: 0, data: {}, name: computeAutoBlockName(type, blocks, i18n.t), createdAt: new Date().toISOString() };
    const idx = insertAfterIndex !== undefined ? insertAfterIndex + 1 : blocks.length;
    const newBlocks = [...blocks.slice(0, idx), newBlock, ...blocks.slice(idx)].map((b, i) => ({ ...b, order: i }));
    setBlocksWithRef(newBlocks);
    pushOp({ type: 'add-block', label: i18n.t('task.a11y.addedBlock', { label: blockLabel(type) }), block: newBlock, idx });
    // selectedBlockId only drives the desktop inline-expanded panel — on phone it left a freshly
    // added block stuck in that same inline view instead of the full-screen editor every other
    // entry point uses (tapping an existing collapsed card routes through handlePhoneEditOpen).
    // Unlike openBlockPanel's undo/redo case (which deliberately never reopens the full-screen
    // editor on phone, since restoring old data isn't an explicit "start editing" action), adding
    // a block IS one, so opening it here is the right call, not an exception to that rule.
    if (isPhone) handlePhoneEditOpen(newBlock.id);
    else { forgetSettingsOpen(); setSelectedBlockId(newBlock.id); }
    setTimeout(() => document.getElementById(`block-${newBlock.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }

  function handleDuplicateBlock(id: string) {
    if (readOnly) return;
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const original = blocks[idx]!;
    const copy: Block = { ...original, id: `blk_${crypto.randomUUID()}`, name: computeAutoBlockName(original.type, blocks, i18n.t), heritageId: original.id, createdAt: new Date().toISOString() };
    const insertIdx = idx + 1;
    const newBlocks = [...blocks.slice(0, insertIdx), copy, ...blocks.slice(insertIdx)].map((b, i) => ({ ...b, order: i }));
    setBlocksWithRef(newBlocks);
    pushOp({ type: 'duplicate-block', label: i18n.t('task.a11y.duplicatedBlock', { label: blockLabel(original.type) }), block: copy, idx: insertIdx });
  }

  // ── Copy and paste ─────────────────────────────────────────────────────────

  /**
   * The clipboard lives outside React (it has to outlive this page), so it is mirrored into render
   * state: re-read on mount, after this tab copies, and when another tab does — a paste affordance
   * must not go on offering a block that a second tab has since replaced.
   */
  const [clipboard, setClipboard] = useState(() => readClipboard());
  useEffect(() => subscribeClipboard(() => setClipboard(readClipboard())), []);

  /** A paste held up by variable names this task already uses for something else. */
  /** A paste held up by a decision: which set members to bring, and/or variable names that clash. */
  const [pendingPaste, setPendingPaste] = useState<{ items: ClipboardBlockItem[]; subjectId: string; insertAfterIndex?: number } | null>(null);
  /** A block paste held open while the teacher decides which referenced tasks to bring. */
  const [pendingPull, setPendingPull] = useState<{
    rows: PullRow[];
    /** The SOURCE experience's tasks, to snapshot the chosen ones from. */
    sourceTasks: Task[];
    /** And its aliases and experience variables, which a pulled task needs as much as its own
     *  blocks do — see `runPull`. */
    sourceAliases: AnswerRefAlias[];
    sourceVariables: TaskVariable[];
    items: ClipboardBlockItem[];
    subjectId: string;
    insertAfterIndex?: number;
    aliases: AnswerRefAlias[];
  } | null>(null);
  const [clipboardAnnouncement, setClipboardAnnouncement] = useState('');
  const [clipboardAnnounceKey, setClipboardAnnounceKey] = useState(0);
  /** The block whose Copy button is currently showing a tick. Copying changes nothing else on the
   * page, so without this the button reads as doing nothing at all. */
  const [justCopiedId, setJustCopiedId] = useState<string | null>(null);

  function announceClipboard(message: string) {
    setClipboardAnnouncement(message);
    // Keyed like the Preview reset announcement: copying twice must be spoken twice rather than
    // falling silent on an identical string.
    setClipboardAnnounceKey(k => k + 1);
  }

  function handleCopyBlock(id: string) {
    const block = blocksRef.current.find(b => b.id === id);
    if (!block) return;
    /*
     * A SCENARIO SET TRAVELS WITH ITS MEMBER, captured HERE rather than looked up at paste time.
     *
     * The clipboard is already a snapshot for exactly this reason — the source task may be edited
     * or deleted long before the paste, and the paste may happen in another tab. A reference to
     * "the rest of my set" would have nothing to resolve against by then.
     *
     * `items` is in the SOURCE TASK's order so the set arrives looking like itself; the block
     * that was actually copied rides along as `subjectId`. Null means this block is in no set,
     * and the clipboard holds one item as before.
     */
    const memberIds = scenarioSetMembers(id, blocksRef.current);
    const members = memberIds
      ? memberIds.map(mid => blocksRef.current.find(b => b.id === mid)).filter((b): b is Block => !!b)
      : [block];
    /*
     * The variables travel WITH the block, from BOTH SCOPES.
     *
     * `variablesRef` is the task's own list; an EXPERIENCE variable resolves here without being in
     * it. Capturing only the task's meant a block whose equation reads `{river}` — defined once on
     * the experience, which is the whole point of that scope — arrived somewhere else carrying
     * nothing, and every equation in it reported a non-numeric answer. The paste side has always
     * compared against `variablesInForce`; only the copy side was narrower, and the asymmetry is
     * what let a chip travel without its definition.
     *
     * In-experience pastes are unaffected: the name already resolves there, `planVariableImport`
     * matches the definition and REUSES it rather than importing a second copy. Across
     * experiences it imports as a task variable, which is the conservative outcome the paste
     * already defaults to — the block keeps behaving exactly as it did.
     */
    writeClipboard({
      kind: 'blocks',
      source: { experienceId, taskId: taskId ?? '' },
      subjectId: id,
      items: members.map(b => ({ block: b, variables: variablesUsedByBlock(b, variablesInForceRef.current) })),
      // Aliases travel with the blocks for the same reason the variables do: a chip stores an
      // alias id and nothing else, so copied without them it names nothing at the far end.
      aliases: aliasesNamedBy(members, aliasesRef.current),
    });
    setClipboard(readClipboard());
    // The one place a copy is actually reported. With a set it carries more than the block the
    // teacher pressed, and a screen reader hearing "Copied MCQ block" would be told the wrong size.
    announceClipboard(members.length > 1
      ? i18n.t('task.a11y.copiedWithSet', { label: blockLabel(block.type), count: members.length - 1 })
      : i18n.t('task.a11y.copiedBlock', { label: blockLabel(block.type) }));
    // Two confirmations, because they answer different questions: the tick says "that press
    // registered", the flash says "this is the block you took". The standing answer to "what is on
    // the clipboard" is the Paste button, which names it.
    setJustCopiedId(id);
    window.setTimeout(() => setJustCopiedId(current => (current === id ? null : current)), 1200);
    flashBlock(id);
  }

  function commitPaste(
    insertAfterIndex: number | undefined,
    choices: Record<string, ConflictChoice>,
    /** Block ids to bring. Absent means every item on the clipboard. */
    include?: Set<string>,
  ) {
    const payload = readClipboard();
    if (payload?.kind !== 'blocks') return;
    const items = include ? payload.items.filter(i => include.has(i.block.id)) : payload.items;
    if (items.length === 0) return;
    // Captured before the refs move: what was imported is the difference, and that is what a single
    // undo has to take back out along with the blocks.
    const beforeVariables = variablesRef.current;
    const result = applyBlockPaste({
      items,
      blocks: blocksRef.current,
      variables: beforeVariables,
      // Matched and name-checked against both scopes; only ever appended to the task's own.
      inForce: variablesInForceRef.current,
      /* For rebinding a driven link's cross-task address. The scope's list rather than
         `experience.tasks`, because it already substitutes the live blocks for the current
         task — a driver is always in an EARLIER task, so this is belt and braces, but the two
         lists disagreeing is exactly how a reference resolver comes to see a stale block. */
      tasks: answerRefScope?.tasks,
      insertAfterIndex,
      choices,
      autoName: (type, blocks) => computeAutoBlockName(type, blocks, i18n.t),
    });
    const imported = result.variables.slice(beforeVariables.length);

    /*
     * ADOPT THE ALIASES THE PASTED CHIPS NAME, and only the ones not already here.
     *
     * Same-experience (which every Duplicate is) finds them all held already and adopts
     * nothing: minting copies would give one address two aliases and break the rule that makes
     * one rename reach every chip. Across experiences the id is kept rather than re-minted,
     * because the chips arriving alongside already name it.
     */
    const offered = aliasesNamedBy(items.map(i => i.block), payload.aliases ?? []);
    const adopted = offered.filter(a => !aliasesRef.current.some(held => held.id === a.id));

    /*
     * AND REPAIR A HELD ALIAS THAT CANNOT RESOLVE, from the address the clipboard offers.
     *
     * The paste endpoint applies the same rule, but only runs when there is a TASK to pull.
     * Once that task is here, pasting the same block again calls no server at all — so a ghost
     * address, left by a duplication from before aliases travelled, would survive every retry.
     * Which is exactly what a teacher does after seeing a reference arrive broken.
     */
    const repairs = planAliasRepairs(
      (experienceRef.current?.tasks ?? []) as never,
      aliasesRef.current,
      offered,
    );

    if (adopted.length > 0 || repairs.length > 0) {
      const byId = new Map(repairs.map(r => [r.aliasId, r.members]));
      writeAliases([
        ...aliasesRef.current.map(a => (byId.has(a.id) ? { ...a, members: byId.get(a.id)! } : a)),
        ...adopted,
      ]);
    }

    setBlocksWithRef(result.blocks);
    setVariablesWithRef(result.variables);
    pushOp({
      type: 'paste-blocks',
      label: i18n.t('task.a11y.pastedBlock', { label: blockLabel(result.pasted[0]?.type ?? 'text') }),
      blocks: result.pasted, idx: result.insertIdx, variables: imported, aliases: adopted,
    });

    const first = result.pasted[0];
    if (!first) return;
    announceClipboard(i18n.t('task.a11y.pastedBlock', { label: blockLabel(first.type) }));
    // Flashed, not opened. A pasted block arrives already configured, so unlike adding one this is
    // not the teacher saying "I am about to author this" — showing where it landed is enough.
    //
    // AFTER the scroll, not before it. Flashing first meant a 700ms pulse could be most of the way
    // through before a smooth scroll had brought the block on screen, so the one thing the flash
    // exists to do — point at it — was the thing it missed.
    setTimeout(() => {
      const el = document.getElementById(`block-${first.id}`);
      if (!el) return;
      const box = el.getBoundingClientRect();
      const onScreen = box.top >= 0 && box.bottom <= window.innerHeight;
      if (onScreen) { flashBlock(first.id); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      window.setTimeout(() => flashBlock(first.id), 400);
    }, 50);
  }

  function handlePasteBlocks(insertAfterIndex?: number) {
    if (readOnly) return;
    const payload = readClipboard();
    // Emptied in another tab since this one rendered.
    if (!payload) { setClipboard(null); return; }
    // A task on the clipboard is not something a block list can accept — the gaps between blocks
    // only ever offer a block, so this is the union refusing at the boundary rather than at a gap.
    if (payload.kind !== 'blocks') return;
    /*
     * Against everything in force, not just this task's.
     *
     * An experience variable is resolvable here and its name is taken, so comparing with the
     * task's list alone meant pasting a block carrying `river` into a task whose `river` is an
     * EXPERIENCE variable raised no conflict at all — the incoming one was imported as a second
     * `river`, and `variablesInForce` then silently dropped it. Two live definitions of one name
     * is the state this whole model says cannot exist.
     */
    /*
     * THE SET ONLY MATTERS WHERE IT CANNOT BE FOUND.
     *
     * A copied set member brings its whole set on the clipboard, but pasting back into the task
     * that still holds the leader would drop in a DUPLICATE leader and duplicate siblings. So the
     * test is the same one `relinkScenario` uses: if the leader resolves here, the link will
     * survive on its own and only the subject is pasted, silently, exactly as before this existed.
     */
    const subject = payload.items.find(i => i.block.id === payload.subjectId) ?? payload.items[0];
    if (!subject) return;
    const leaderId = (subject.block.data as { followsBlockId?: string } | undefined)?.followsBlockId;
    const leaderResolves = !!leaderId && blocksRef.current.some(b => b.id === leaderId);
    const items = leaderResolves ? [subject] : payload.items;

    /*
     * THE REFERENCES COME FIRST, because they are a question about the EXPERIENCE and the two
     * below are questions about this task. Asked in the other order, a teacher would settle a
     * variable clash for a block that then turns out to need three tasks brought with it.
     */
    void askPullThenPaste(payload, items, subject.block.id, insertAfterIndex);
  }

  /** The rest of the paste, once the references question is settled one way or the other. */
  function proceedPaste(
    items: ClipboardBlockItem[],
    subjectId: string,
    insertAfterIndex?: number,
  ) {
    // More than one block to bring is itself a question, and the answer changes which variables
    // come, so the conflicts cannot be settled before it. One dialog, conflicts recomputed live.
    if (items.length > 1) { setPendingPaste({ items, subjectId, insertAfterIndex }); return; }

    const conflicts = findVariableConflicts(incomingVariables(items), variablesInForceRef.current);
    // Only a genuine mismatch interrupts. An unknown name is imported and an identical definition
    // is reused, and in neither case is there anything a teacher could usefully choose between.
    if (conflicts.length) { setPendingPaste({ items, subjectId, insertAfterIndex }); return; }
    commitPaste(insertAfterIndex, {}, new Set(items.map(i => i.block.id)));
  }

  /**
   * WOULD THIS PASTE BRING A REFERENCE TO AN ANSWER THAT IS NOT HERE?
   *
   * A chip names an answer living in some task. Pasted into an experience without it, the
   * reference is dead on arrival and the block goes red. So before pasting, work out which
   * tasks would have to come too, and ask.
   *
   * Only when there is something to ask about, which is the silence rule the scenario-set
   * paste already follows: pasting back into the experience the block came from finds its
   * answers present and behaves exactly as it did before this existed.
   */
  async function askPullThenPaste(
    payload: ClipboardBlocks,
    items: ClipboardBlockItem[],
    subjectId: string,
    insertAfterIndex?: number,
  ) {
    const sourceId = payload.source.experienceId;
    const go = () => proceedPaste(items, subjectId, insertAfterIndex);
    if (!experience || !taskId || !sourceId || sourceId === experienceId) { go(); return; }
    if (!(payload.aliases ?? []).length) { go(); return; }

    let source: Experience;
    // Being unable to OFFER the pull is no reason to refuse the paste: the fallback is the
    // state that shipped before this, a named and repointable broken reference.
    try { source = await api.get<Experience>(`/experiences/${sourceId}`); }
    catch { go(); return; }

    const rows = pullRows({
      // A block copy has no task to walk from, so the blocks are wrapped in a stand-in.
      sourceTasks: [...(source.tasks ?? []), seedTaskForBlocks(items.map(i => i.block))],
      sourceAliases: source.answerRefAliases ?? [],
      seedTaskIds: [PASTED_BLOCKS_SEED],
      alreadyHere: alreadyHereIds(experience.tasks ?? []),
    });
    if (!rows.length) { go(); return; }
    setPendingPull({ rows, sourceTasks: source.tasks ?? [], items, subjectId, insertAfterIndex,
      aliases: payload.aliases ?? [],
      sourceAliases: source.answerRefAliases ?? [],
      sourceVariables: source.variables ?? [] });
  }

  /**
   * Bring the chosen tasks in, THEN paste the blocks.
   *
   * Two steps and not one: the tasks are a paste of their own, and they have to exist before
   * the blocks land so the chips resolve against them rather than against nothing.
   *
   * The server installs the aliases, not this. An alias member names a BLOCK, and only the
   * paste pass knows the ids the copies were given — see the `arriving` rule there.
   */
  async function runPull(taskIds: string[]) {
    const pull = pendingPull;
    if (!pull || !taskId) return;
    setPendingPull(null);
    try {
      const pulled = pullPasteOrder(pull.rows, taskIds)
        .map(id => pull.sourceTasks.find(tk => tk.id === id))
        .filter((tk): tk is Task => !!tk)
        // Carrying the source's experience variables, then folding them into each task's own:
        // a pulled task is always crossing documents, so nothing here will resolve `{river}`.
        .map(tk => foldExperienceVariables(snapshotTask(tk, pull.sourceVariables)));
      const updated = await api.post<Experience>(
        `/experiences/${experienceId}/tasks/paste`,
        {
          tasks: pulled,
          // Directly ahead of the task receiving the blocks, in ITS lane: an answer has to be
          // reached before the question citing it, and a pathway member's dependency belongs
          // in that pathway rather than on the spine.
          beforeTaskId: taskId,
          /*
           * THE PULLED TASKS' OWN ALIASES TOO, not only the ones the pasted blocks cite.
           *
           * The walk that chose these tasks is transitive — a pulled task can cite an answer
           * in a task pulled behind it — but the alias payload was only ever the copied
           * blocks' census. So the deepest task arrived holding a chip naming an alias nobody
           * had sent, which is worse than a dangling address: there is no name and no epitaph
           * to repoint, just an id that resolves to nothing.
           */
          aliases: withPulledAliases(pull.aliases, pulled, pull.sourceAliases),
        },
      );
      setExperience(updated);
      /*
       * THE REF, NOW, not on the next render.
       *
       * `aliasesRef` is refreshed during render from `experience`, and `proceedPaste` below
       * runs synchronously — before React has re-rendered. Left to that, `commitPaste` reads
       * the PRE-PULL list, decides the arriving aliases are not held yet, and writes them
       * back with their SOURCE members, overwriting the correctly remapped ones the server
       * just installed. The references then point at the experience they came from and the
       * pull silently achieves nothing.
       */
      aliasesRef.current = updated.answerRefAliases ?? [];
    } catch {
      // The paste still happens. A failed pull leaves exactly the broken-but-named reference
      // that skipping would have, which the manager can repoint.
      setToastMsg(i18n.t('task.paste.pull.failed'));
    }
    proceedPaste(pull.items, pull.subjectId, pull.insertAfterIndex);
  }

  /** Undefined with an empty clipboard — what hides the paste affordance entirely, rather than
   * leaving a dead button on the page. */
  const clipboardBlocks = clipboard?.kind === 'blocks' ? clipboard : null;
  /* Only ever read for truthiness — `task-content` gates the paste affordances on it and renders
   * the DESTINATION's name, never this one. Anything dressed onto it here would not reach a screen. */
  const clipboardSubject = clipboardBlocks
    ? (clipboardBlocks.items.find(i => i.block.id === clipboardBlocks.subjectId) ?? clipboardBlocks.items[0])?.block
    : undefined;
  const clipboardLabel = clipboardSubject && !readOnly
    ? (clipboardSubject.name ?? blockLabel(clipboardSubject.type))
    : undefined;

  function placeAt(insertAfterIndex: number) {
    // -1 means "before the first block"; applyBlockPaste turns it into index 0.
    handlePasteBlocks(insertAfterIndex);
  }

  /**
   * Cmd/Ctrl+V, anchored on the FOCUSED block.
   *
   * Clicking a block opens its editor rather than selecting it, so the page has no selection to
   * paste "after" — DOM focus is the only durable sense of where the teacher is. With nothing
   * focused it appends, which is also where the quick-add row's Paste puts things.
   */
  const pasteRef = useRef<(index?: number) => void>(() => {});
  pasteRef.current = handlePasteBlocks;
  useEffect(() => {
    if (readOnly) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'v' || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      // Never steal a real paste: in a field or contenteditable the browser's own is what is meant.
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (!readClipboard()) return;
      e.preventDefault();
      const card = el?.closest('[id^="block-"]');
      const idx = card ? blocksRef.current.findIndex(b => `block-${b.id}` === card.id) : -1;
      pasteRef.current(idx >= 0 ? idx : undefined);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [readOnly]);

  /**
   * WHY A BLOCK CANNOT BE DELETED, or undefined when it can — §7.4.
   *
   * Supplied to the cards through `BlockDeleteGuardProvider`, which is what DISABLES the two
   * trash buttons and puts the reason on hover. The first build let the press through and
   * showed a toast, which reads worst on the control it lands on: the armed delete is two
   * steps, so a teacher armed it, pressed again, and watched nothing happen. The variables
   * panel and the spine builder both disable-and-explain instead, and so does this now.
   *
   * `handleRemoveBlock` still asks, because delete is also reachable by keyboard and from the
   * phone editor, and a disabled button is a statement rather than an enforcement.
   */
  /*
   * EVERY LOAD-BEARING DEPENDENCY IN THE EXPERIENCE, in one pass.
   *
   * Memoised and keyed by the block depended ON, because the guard below is asked for every
   * card on screen: a per-block walk of every block's data would be quadratic in an
   * experience's blocks, on the render path, for an answer that is the same for all of them.
   *
   * From `answerRefScope`, which already holds the whole experience with THIS task's live
   * blocks spliced in — the scenario guard reads `blocksRef.current` and could, because a
   * follower is always in the same task. A driver is in an earlier one by definition, so that
   * list cannot answer this and the scope is the only thing that can.
   */
  const dependents = useMemo(
    () => dependentsByBlock(
      (answerRefScope?.tasks ?? []) as Parameters<typeof dependentsByBlock>[0],
      experience?.answerRefAliases ?? [],
    ),
    [answerRefScope?.tasks, experience?.answerRefAliases],
  );

  /**
   * THE RECALL RINGS, and the row the teacher is pointing at.
   *
   * Computed here rather than reported up from Inspect: the panel's own copy would have to
   * travel through an effect, and an effect that reports a value and clears it on unmount
   * flashes every time the value changes. The one input the panel has that this does not is
   * `setStandIn`, which only decides whether the band can offer a control — not what is
   * outstanding — so both can read the same function and agree.
   *
   * `recallMarkFor` settles which mark a block wears; its note has the rules.
   */
  const { standIns: recallStandIns } = useRecallStandIns();
  const [litRecall, setLitRecall] = useState<string | null>(null);
  const markedTaskId = shownTaskId ?? taskId;
  const previewFeeders = useMemo(
    () => (isPreview && markedTaskId
      ? recallFeeders(answerRefScope, preview.session, markedTaskId, a => aliasChipName(a, answerRefScope!, i18n.t))
      : []),
    [isPreview, answerRefScope, preview.session, markedTaskId]);
  const recallMark = useCallback(
    (blockId: string) => recallMarkFor(previewFeeders, recallStandIns, litRecall, blockId),
    [previewFeeders, litRecall, recallStandIns]);

  /**
   * THE REFERENCES INSPECT CAN SET, so the sentence stops offering to.
   *
   * The band lists every outstanding reference with its own menu, which is strictly better than
   * the control buried in the prose: one place, named, rather than found by hunting the page
   * for dimmed words. So wherever a teacher can reach the band, the chips are plain marked
   * text -- still saying the sentence stands on a value nobody gave, no longer clickable.
   *
   * REACHABLE, not visible, and the two differ on a portrait phone. The sheet covers the task
   * it describes, so the band and the chip are never on screen together there; but the toggle
   * that opens it sits on the bottom bar and stays, so the band is always one tap away and the
   * chip is never the only way to set anything. Landscape and desktop have it simply docked.
   *
   * The one place it is NOT reachable is a docked surface whose slot the read-through has
   * taken: no band, and no toggle to summon one, because nothing summoned the panel there in
   * the first place. The chips go back to being controls, which is the whole reason this is a
   * condition rather than a constant.
   *
   * Empty off the preview and whenever nothing is listed, which leaves every other surface --
   * the editor's bench chips above all -- exactly as it was.
   */
  const bandReachable = isPreview
    && (isPhone && !isPhoneLandscape ? true : a11yPanel !== 'reading');
  const bandedAliasIds = useMemo(
    () => (bandReachable ? new Set(previewFeeders.flatMap(f => f.aliasIds)) : EMPTY_ALIAS_IDS),
    [bandReachable, previewFeeders]);
  /* One object while its parts hold: the chip renderer memoises on the whole scope, and a fresh
     one per render would rebuild every chip on the page each time anything else changed. */
  const bandedScope = useMemo(
    () => (answerRefScope ? { ...answerRefScope, bandedAliasIds } : answerRefScope),
    [answerRefScope, bandedAliasIds]);

  function deleteBlockedReason(id: string): string | undefined {
    return blockDeleteReason(id, {
      blocks: blocksRef.current,
      tasks: answerRefScope?.tasks ?? [],
      currentTaskId: taskId,
      dependents,
      t: i18n.t,
      labelOf: type => blockLabel(type as BlockType),
    });
  }

  function handleRemoveBlock(id: string) {
    if (readOnly) return;
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const block = blocks[idx]!;
    /*
     * A SCENARIO LEADER CANNOT BE DELETED WHILE ANYTHING FOLLOWS IT — §7.4.
     *
     * The owner's rule, and the reason it is a refusal here where deleting a VARIANT is not:
     * removing a variant leaves its followers with one fewer choice, which the armed delete
     * and the undo stack together make recoverable. Removing the leader leaves every follower
     * pointing at nothing — its answer key still authored, its scenario gone, and no control
     * anywhere that can put it back short of undo. That is a different size of mistake.
     *
     * Refused rather than cascaded for the same reason the variable guard refuses: deleting
     * one thing should not quietly rewrite several others. Named, so "unlink them" is an
     * instruction rather than a riddle — the same shape as `inUseBy`.
     *
     * Here and not on the button: delete is reachable from the card, the phone editor and the
     * keyboard, and a guard that only one of them honours is not a guard.
     */
    if (deleteBlockedReason(id)) return;
    const left = blocks.filter(b => b.id !== id).map((b, i) => ({ ...b, order: i }));
    setBlocksWithRef(left);
    /*
     * THE NAMES THIS DELETION ORPHANS GO WITH IT, in the same undo step.
     *
     * A block holds chips, so deleting it can leave their names cited by nothing. Sweeping
     * them separately is correct and was ONE PRESS TOO MANY: undoing a single gesture took
     * two, the first restoring names and the second the block. Reported in those words.
     *
     * Computed HERE rather than by arming the sweep, because here is the only place that
     * knows the deletion and the alias change are one act. `unusedAliases` is still the one
     * definition of unused — asked against the blocks as they will be.
     */
    const orphaned = unusedAliases(
      ((experienceRef.current?.tasks ?? []).map(tk => (tk.id === taskId ? { ...tk, blocks: left } : tk))) as never,
      aliasesRef.current,
    );
    pushOp({
      type: 'remove-block',
      label: i18n.t('task.a11y.deletedBlock', { label: blockLabel(block.type) }),
      block, idx,
      ...(orphaned.length ? { aliases: orphaned } : {}),
    });
    if (orphaned.length) {
      const keep = new Set(orphaned.map(a => a.id));
      writeAliases(aliasesRef.current.filter(a => !keep.has(a.id)));
    }
    if (selectedBlockId === id) setSelectedBlockId(null);
    // Delete is reachable from INSIDE the phone editor, and a session left pointing at the deleted
    // block would commit an `update-block` op for something that no longer exists on close.
    if (sessionCovers(phoneSessionRef.current, id)) {
      phoneSessionRef.current = null;
      setPhoneBlockCanUndo(false);
      setPhoneBlockCanRedo(false);
      setMobileEditingId(null);
    }
  }

  function handleMoveBlock(from: number, to: number) {
    if (readOnly) return;
    setBlocksWithRef(arrayMove(blocks, from, to).map((b, i) => ({ ...b, order: i })));
    pushOp({ type: 'move-block', label: i18n.t('task.a11y.movedBlock'), from, to });
  }

  /**
   * §7.3 — A LEADER LOSING A VARIANT TAKES THAT ROW OUT OF EVERY FOLLOWER.
   *
   * Done HERE, rather than in each follower's own projection, because this is the one place
   * every block write passes through, phone and desktop alike — and because it has to be one
   * write. N followers pruning themselves would be N undo steps, so a single undo would put the
   * variant back and leave the answers gone.
   *
   * What each follower should then hold is `pruneFollowers`' decision, not this function's.
   */
  function handleUpdateBlock(id: string, data: Record<string, unknown>, opts?: BlockUpdateOptions) {
    if (readOnly) return;
    const before = blocksRef.current.find(b => b.id === id)?.data;
    const pruned = opts?.normalization ? [] : pruneFollowers(id, before, data, blocksRef.current, opts?.removedVersionId);
    const prunedById = new Map(pruned.map(p => [p.blockId, p.next]));
    const applyAll = (list: typeof blocksRef.current) => list.map(b =>
      b.id === id ? { ...b, data }
        : prunedById.has(b.id) ? { ...b, data: prunedById.get(b.id)! }
          : b);

    const session = phoneSessionRef.current;
    if (sessionCovers(session, id)) {
      // Phone block-editor session: track in separate history, skip the task stack entirely.
      setBlocksWithRef(applyAll(blocksRef.current));
      // A sibling changed from inside this editor is settled on CLOSE, not stepped through by the
      // in-editor Undo — see `PhoneSession.others`. Without it the committed op would describe
      // the leader alone and one undo would restore the variant with its answers still gone.
      let next = recordEdit(session!, data);
      for (const p of pruned) next = recordOther(next, p.blockId, p.prev);
      phoneSessionRef.current = next;
      setPhoneBlockCanUndo(sessionCanUndo(next));
      setPhoneBlockCanRedo(sessionCanRedo(next));
      return;
    }
    // Recorded BEFORE the edit is applied: the stack reads the current data as its baseline. A
    // block healing its own data is applied and saved, but never recorded — see `BlockUpdateOptions`.
    if (pruned.length > 0) {
      // ONE op, because it was one decision. Separate ops would let a single undo take half of it.
      const block = blocksRef.current.find(b => b.id === id);
      stack.push({
        type: 'update-blocks',
        label: i18n.t('task.a11y.editedBlock', { label: blockLabel(block?.type ?? 'text') }),
        entries: [{ blockId: id, prev: (before ?? {}) as Record<string, unknown>, next: data }, ...pruned],
      });
    } else if (!opts?.normalization) {
      stack.recordBlockUpdate(id, data);
    }
    setBlocksWithRef(applyAll(blocksRef.current));
  }

  /**
   * NOT in the undo/redo stack, deliberately — one of exactly two mutations that are not.
   *
   * A rename is small metadata, and clearing the field reverts it in place (see InputBlockFrame's
   * commitName), so history is not the tool for recovering from it.
   *
   * The other exemption is `handleFlowChange`. This comment used to claim variables were exempt
   * too; they have not been for some time — `add-variable`, `remove-variable` and `update-variable`
   * are three of the eleven op types. A scope decision was reversed and the note recording it was
   * not, which is its own small lesson about exemptions written as prose.
   */
  function handleRenameBlock(id: string, name: string) {
    if (readOnly) return;
    setBlocksWithRef(blocksRef.current.map(b => b.id === id ? { ...b, name } : b));
  }

  // ── Undo / Redo ────────────────────────────────────────────────────────────

  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Point at a block: the shared `.agavi-pulse`, same 700ms envelope the board uses.
   *
   * Cleared OFF and back ON across a frame, because pulsing the same block twice in a row would
   * otherwise set state it already holds — no class change, so the CSS animation never restarts and
   * the second pulse is invisible. The board solves this with a key; here a frame is enough and
   * costs no prop threading.
   *
   * The timer also matched nothing: it cleared at 800ms against a 700ms animation, so the class
   * outlived its own effect by a frame's worth of nothing.
   */
  function flashBlock(blockId: string) {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setHighlightedBlockId(null);
    requestAnimationFrame(() => setHighlightedBlockId(blockId));
    flashTimer.current = setTimeout(() => setHighlightedBlockId(null), 700);

    /**
     * And bring it into view, which matters most for the case this is least able to explain:
     * undoing a delete. The block comes back wherever it used to be, which on a long task is
     * often off screen — so the ring plays somewhere the teacher cannot see and the undo reads as
     * having done nothing at all.
     *
     * Polled, because the block is being restored as this runs and its element does not exist for
     * the first frames. Already-visible blocks are left alone, so an undo does not shove the page
     * around for something the teacher is looking at.
     */
    const deadline = performance.now() + 600;
    const bring = () => {
      const el = document.getElementById(`block-${blockId}`);
      if (el) { scrollIntoViewIfNeeded(el); return; }
      if (performance.now() < deadline) requestAnimationFrame(bring);
    };
    requestAnimationFrame(bring);
  }

  /**
   * Undo/redo touched a block that is not the subject of the editor.
   *
   * Points at it, and re-reads it ONLY if it happens to be the block on screen. Restoring a deleted
   * block used to OPEN it, which quietly replaced whatever the teacher had open with something they
   * had not asked to edit — and then pulsed a ring around the panel, because the flash and the
   * selection were suddenly the same block.
   *
   * The rule: the editor opens when you create a block or click one, and when an undo/redo changes
   * what is INSIDE the open block. Adding, removing, moving, copying and pasting a block change the
   * LIST, not a block's contents, so they point without opening.
   */
  function touchBlock(blockId: string) {
    flashBlock(blockId);
  }

  /** The open block just disappeared — close the panel, but only if it was that one. */
  function closeIfOpen(blockId: string) {
    if (blockId === selectedBlockId) setSelectedBlockId(null);
  }

  /**
   * Show what an undo or redo just changed.
   *
   * Opens the block — a change INSIDE a block is the one case where reopening the editor is right,
   * since that is where the change is — and then hands the field ids to the reveal ladder, which
   * pages carousels, flips to the feedback panel and opens the settings drawer as needed. Falls
   * back to pulsing the block only when the field is genuinely not on the page.
   *
   * Two things this deliberately no longer does. It does not wait a fixed 60ms before looking:
   * `openBlockPanel` remounts the panel on purpose, so the field is reliably absent for the first
   * frames and present a few frames later, and a single timeout raced that remount and reported
   * TAGGED fields as missing. And it does not inspect the block's data to guess whether the change
   * was in feedback — the frame owns `isEditingFeedback`, so the ladder asks the frame and, unlike
   * a guess made from here, puts the panel back if the field turns out not to be there.
   */
  function revealUndoneChange(op: Extract<Op, { type: 'update-block' }>) {
    openBlockPanel(op.blockId);
    const fields = op.fields ?? [];
    if (!fields.length) { flashBlock(op.blockId); return; }
    // 'superseded' means a later undo/redo took over mid-reveal; it owns the surfaces now, and
    // flashing here would put a whole-block ring on top of the newer, precise one.
    void pulseFirstField(fields, op.blockId).then(r => { if (r === 'missing') flashBlock(op.blockId); });
  }

  function openBlockPanel(blockId: string) {
    // Phone portrait: undo/redo never opens the editor — a whole phone editing session already
    // collapses to one task-level op on close, and re-flashing the full-screen editor open/closed
    // on every undo/redo click would be confusing. selectedBlockId (which drives isSelected) is a
    // desktop-only concept for reopening the embedded panel, so it's skipped here on phone — the
    // block just re-renders with the restored data and flashes, same as any other undo target.
    if (!isPhone) setSelectedBlockId(blockId);
    setTimeout(() => document.getElementById(`block-${blockId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }

  function handleUndo() { stack.undo(); }
  function handleRedo() { stack.redo(); }

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  const handleUndoRef = useRef(handleUndo);
  handleUndoRef.current = handleUndo;
  const handleRedoRef = useRef(handleRedo);
  handleRedoRef.current = handleRedo;
  const handlePhoneBlockUndoRef = useRef(() => {});
  const handlePhoneBlockRedoRef = useRef(() => {});

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (!e.ctrlKey && !e.metaKey) return;
      // While a phone block session is open it owns undo — the on-screen buttons already route
      // there, and the keyboard reaching PAST it to the task stack left the two histories to
      // diverge: `blocks` moved while the session's own history and `prevData` did not, so the op
      // committed on close described a change that was partly an undo. Reachable on a desktop,
      // because a short window is classified as a phone.
      const inSession = phoneSessionRef.current !== null;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (inSession) handlePhoneBlockUndoRef.current(); else handleUndoRef.current();
      }
      if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        if (inSession) handlePhoneBlockRedoRef.current(); else handleRedoRef.current();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // ── Title / Save / Nav ─────────────────────────────────────────────────────

  // On a 409 (someone else saved this task since we last read it — see assertRevision in
  // experiences.service.ts), a full reload is the simplest correct response: re-fetching just
  // this task in-place would still need to reconcile the (rare) case where the conflicting save
  // also forked the task into a new id, which is its own separate problem (see contentRevisionRef's
  // own comment) — a full reload sidesteps that entirely by just starting over.
  function reloadAfterConflict() {
    setToastMsg(i18n.t('task.errors.conflictReloading'));
    setTimeout(() => window.location.reload(), 1200);
  }

  /** Point the URL at the task a save just forked us onto, so the editor keeps writing to the live
   * version instead of a tombstone. `replace`, because the stale id is not a place to go back to. */
  function followForkAfterSave(updated: Experience) {
    if (!taskId) return;
    const liveId = resolveLiveTaskId(updated.tasks, taskId);
    if (liveId !== taskId) navigate(`/app/task/${liveId}?experience=${experienceId}`, { replace: true });
  }

  async function handleTitleSave(newTitle: string) {
    if (readOnly || !experience || !taskId || newTitle === taskTitle) return;
    const prevTitle = taskTitle;
    pushOp({ type: 'update-title', label: i18n.t('task.a11y.changedTitle'), prev: prevTitle, next: newTitle });
    setTaskTitle(newTitle);
    try {
      const updated = await api.patch<Experience>(`/experiences/${experienceId}/tasks/${taskId}`, { title: newTitle, expectedRevision: contentRevisionRef.current });
      // Absent when this save forked the task (a new task id) — not reconciled here, see
      // contentRevisionRef's comment; the next save attempt will 404 against the stale id,
      // same pre-existing failure mode this had before contentRevision tracking existed.
      const updatedTask = updated.tasks.find((t) => t.id === taskId);
      if (updatedTask) contentRevisionRef.current = updatedTask.contentRevision ?? contentRevisionRef.current;
      else followForkAfterSave(updated);
      // Same reason as the blocks save below: the cached experience is what a hop back to this
      // task renders from, so a title saved here has to land in it too.
      setExperience(updated);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) reloadAfterConflict();
      else setToastMsg(i18n.t('task.errors.couldNotSaveTitle'));
    }
  }

  async function handleSave(): Promise<boolean> {
    if (readOnly || !experience || !taskId) return false;
    flushPendingUpdate();
    setSaving(true);
    setSaveFailed(false);
    try {
      const cleanVariables = variables.filter(isSavableVariable);
      const updated = await api.put<Experience>(`/experiences/${experienceId}/tasks/${taskId}/blocks`, { blocks, variables: cleanVariables, flow, expectedRevision: contentRevisionRef.current });
      const updatedTask = updated.tasks.find((t) => t.id === taskId);
      if (updatedTask) contentRevisionRef.current = updatedTask.contentRevision ?? contentRevisionRef.current;
      // Absent means THIS save forked the task: the write landed on a new id and ours is now a
      // tombstone. Following it immediately is what stops the next autosave writing against the
      // dead id — which the server still accepts, forking again and leaving another duplicate live
      // task behind, one per edit batch.
      else followForkAfterSave(updated);
      /*
       * FOLD THE SAVE BACK INTO THE CACHED EXPERIENCE.
       *
       * This used to be skipped because nothing read the cached copy of a task after it was first
       * applied. The stepper changed that: hopping to a sibling and back renders the destination
       * straight out of this document, so a cached copy that stopped at the last load shows the
       * task as it was BEFORE everything saved since — pre-edit content, for as long as the
       * revalidating fetch takes.
       *
       * It also decides whether the parked undo history comes back: that match is on
       * `contentRevision`, and a stale cached revision fails it every time, so every round trip
       * would silently drop a history that was still perfectly good.
       */
      setExperience(updated);
      savedBlocksJsonRef.current    = JSON.stringify(blocks);
      savedVariablesJsonRef.current = JSON.stringify(variables);
      savedFlowJsonRef.current      = JSON.stringify(flow);
      // A sweep that was waiting for exactly this — see `sweepUnusedAliases`. A no-op unless
      // leaving a block armed it, so an ordinary save costs one ref read.
      sweepUnusedAliases();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) reloadAfterConflict();
      else {
        setToastMsg(i18n.t('task.errors.couldNotSaveBlocks'));
        setSaveFailed(true);
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Create a brand-new task as a detour off THIS task (pathwayOf = current task) and return its
  // id, so the Flow picker can point an action straight at a freshly-born pathway. Persists
  // immediately via the tasks endpoint (the same one that seeds pathwayOf) — the goto that lands
  // on the returned id is a normal flow edit and rides the usual autosave. setExperience folds the
  // new task into the picker's task list without clobbering the live (unsaved) blocks/flow edits,
  // which live in their own state, not in `experience`.
  async function createDetourTask(names: { pathwayName: string; taskTitle: string }): Promise<string | null> {
    if (readOnly || !experience || !taskId) return null;
    const beforeIds = new Set(experience.tasks.map((t) => t.id));
    // Mint a proper NAMED pathway off this task (matching the board's "New branch"), not a loose
    // detour: a fresh pathwayId + the teacher-chosen names, so the new remediation groups + renders as
    // a real, findable pathway everywhere.
    try {
      const updated = await api.post<Experience>(`/experiences/${experienceId}/tasks`, {
        title: names.taskTitle,
        pathwayOf: taskId,
        pathwayId: `pw_${crypto.randomUUID()}`,
        pathwayName: names.pathwayName,
      });
      setExperience(updated);
      const created = updated.tasks.find((t) => !beforeIds.has(t.id));
      return created?.id ?? null;
    } catch {
      setToastMsg(i18n.t('task.errors.couldNotCreatePathway'));
      return null;
    }
  }

  /**
   * Make another task at the end of this track and go to it — the stepper's end-of-track menu.
   *
   * It existed only on the experience board, so finishing a task and wanting the next one meant
   * leaving the editor, adding, and coming back in.
   *
   * ## What a DUPLICATE copies, and what it deliberately does not
   *
   * Blocks and task variables, which is what the request was about: a task in a sequence
   * usually shares its predecessor's intro text and images, so starting from a copy is
   * starting from the story rather than from nothing. Every block is re-ided with
   * `heritageId` pointing at the original, exactly as duplicating ONE block does.
   *
   * FLOW IS NOT COPIED, and that is a decision rather than an omission. Flow conditions
   * address blocks by id, and every id has just changed — carried across unremapped they
   * would point at blocks that do not exist in the new task, which is worse than no flow at
   * all because it looks authored. Its `goto`s are a routing decision about where THIS task
   * leads, which a copy has no business inheriting. A remap is a real piece of work and
   * belongs with Flow's own scenario round, not smuggled in here.
   *
   * Two requests rather than one: the POST mints the task (and the pathway bookkeeping the
   * server owns), the PUT fills it. Both endpoints already existed, which is also why this
   * needs no backend change.
   */
  async function addTaskAtEnd(mode: 'new' | 'duplicate') {
    if (readOnly || !experience || !taskId) return;
    const source = experience.tasks.find(t => t.id === taskId);
    const beforeIds = new Set(experience.tasks.map(t => t.id));
    try {
      const created = await api.post<Experience>(`/experiences/${experienceId}/tasks`, {
        title: mode === 'duplicate' && source
          ? i18n.t('task.stepper.copyTitle', { title: source.title })
          : i18n.t('experience.newTask'),
      });
      const newId = created.tasks.find(t => !beforeIds.has(t.id))?.id;
      if (!newId) throw new Error('no task came back');
      if (mode === 'duplicate') {
        /* The LIVE blocks, not the saved ones: a teacher duplicates the task they are looking
           at, and autosave may not have caught up with the last keystroke. */
        const copied = blocks.map(b => ({
          ...b,
          id: `blk_${crypto.randomUUID()}`,
          heritageId: b.id,
          createdAt: new Date().toISOString(),
        }));
        await api.put<Experience>(`/experiences/${experienceId}/tasks/${newId}/blocks`, {
          blocks: copied,
          variables: variables.filter(isSavableVariable),
          flow: undefined,
        });
      }
      handleNavigate(taskHref(newId, experienceId));
    } catch {
      setToastMsg(i18n.t('task.errors.couldNotCreateTask'));
    }
  }

  const isDirty = JSON.stringify(blocks)    !== savedBlocksJsonRef.current
    || JSON.stringify(variables) !== savedVariablesJsonRef.current
    || JSON.stringify(flow)      !== savedFlowJsonRef.current;

  // Auto-save: any blocks/variables/flow change reaches the server ~1s after the last edit —
  // same debounced pattern as the canvas's own autosave (experience-workspace-page.tsx). Depends
  // on the actual content, not the derived `isDirty` boolean, so the timer correctly resets on
  // every keystroke instead of firing a fixed 1s after only the FIRST edit in a burst — see that
  // file's matching comment for the full reasoning; it matters far more here (continuous typing)
  // than there (occasional structural edits).
  useEffect(() => {
    // readOnly here is a belt-and-suspenders check for the moment take-over lands mid-edit
    // (handleSave itself already no-ops while read-only) — every mutating handler already
    // refuses to run while read-only, so isDirty shouldn't normally become true in that state.
    if (!isDirty || readOnly) return;
    const timer = setTimeout(() => { void handleSave(); }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, variables, flow, readOnly]);

  // Backstop for a genuine mid-flight save (closing/refreshing the tab, not just the in-app Back
  // button — that's UnsavedChangesModal's job, gated on isDirty||saving already). Deliberately NOT
  // a general "you have unsaved changes" nag: autosave means unsaved state is normally only the
  // ~1s debounce window, which isn't worth interrupting a close over. Only the narrow "a request
  // is actually in flight right now, closing would abandon it" case warrants the native prompt.
  useEffect(() => {
    if (!saving) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = ''; // legacy Chrome/Edge still require this to show the native prompt
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saving]);

  // Closes an already-open Variables/Flow panel the moment take-over lands mid-session — without
  // this, a panel opened before another tab/device claimed ownership would stay open exactly as
  // editable-looking as before (handleOpenVariables/handleOpenFlow's own readOnly guard only stops
  // a NEW open, it can't retroactively close one already in progress). Matters most on phone,
  // where these render as a full-screen createPortal outside the inert-wrapped content area.
  useEffect(() => {
    if (!readOnly) return;
    setVariablesPanelOpen(false);
    setFlowPanelOpen(false);
    // Also close the editing modals that render ABOVE the resume banner, or the banner is
    // unreachable and the only way out is a hard refresh (reported by the product owner). The
    // mobile library picker is a page-level portal outside the inert wrapper (stays clickable,
    // covering the banner); the math / reactions editors live inside the selected block's editor,
    // so deselecting the block unmounts them. Both routes to a trapped modal are closed here.
    setShowLibraryModal(false);
    setSelectedBlockId(null);
    setRefsPanelOpen(false);
  }, [readOnly]);

  // Surfaced as a badge on the Flow tab button so a teacher notices a Special event broke
  // (deleted feedback, or a setting change that made a targeted state unreachable) without
  // needing to think to reopen Flow after editing an unrelated block.
  /*
   * THE DOT MEANS "SOMETHING IN HERE CANNOT FIRE", and a starved All Correct is exactly that.
   *
   * It counted paused event RULES only, so a task whose correct state no block can reach — the
   * most complete way for a flow to be stuck — opened a panel with a red card inside and nothing
   * on the button to say go and look. Reported as a flow with correct disabled carrying no mark.
   */
  const hasFlowIssues = flow.eventRules.some((rule) => isRulePaused(rule, blocks))
    || unacceptedStarvedBlocks(blocks, flow).length > 0;

  /*
   * ONE count, read by the button (does it appear at all, and does it carry a dot) and by the
   * panel it opens. Derived from `experience.tasks` rather than from `blocks`, because a
   * reference's health is a question about the whole experience: deleting a block three tasks
   * away turns a chip red here while nothing on this task changed.
   */
  const refStats = useMemo(
    () => answerRefCount(experience?.tasks ?? [], taskId ?? '', experience?.answerRefAliases ?? []),
    [experience?.tasks, taskId, experience?.answerRefAliases]);

  /**
   * THE THREE PANEL SWITCHES, defined once and rendered on all three surfaces.
   *
   * Order is Variables, References, Flow. The two icon-only switches sit together and Flow's
   * word anchors the right of the group where there is room for it; on a phone nobody gets a
   * word, so the order simply holds.
   *
   * References is absent entirely when the task has none. Unlike the other two it has no
   * subject of its own — a task either authors references or it does not, and an empty panel
   * is a button that teaches nothing.
   */
  /*
   * THE PANEL IS TASK-SCOPED, so it asks the capability and not the block.
   *
   * Which blocks may TAKE a variable is a per-block question and belongs to
   * `useVariableAffordance`; whether a teacher may author one at all is this. Absent rather than
   * disabled, because at rung zero there is no rung to unlock from here — the switch is in
   * Settings, and an inert button in three toolbars would point at nothing on this screen.
   */
  /*
   * GRANDFATHERED, because this panel is the ONLY way out.
   *
   * Turning the capability off with variables already authored would otherwise take away the one
   * screen that can delete them, leaving a task that goes on randomizing with no control to stop
   * it — the same trapdoor that gating the version and scenario trash doors opened, and the same
   * repair: gate adoption, never abandonment.
   *
   * This is the ONLY grandfather clause left on variables, and deliberately so. A field holding a
   * chip used to keep its insert control too, which rewarded the workaround rather than protected
   * authored work: paste one chip into a block the rung does not support and the button unlocked
   * for more. Such a chip is simply invalid now and raises `variable-not-supported-here`. This
   * clause survives because it is the EXIT — it is what lets a teacher go and delete the variable
   * the error names. Experience-scoped variables count too, being authored from this same panel.
   */
  const variablesCapabilityOn = useCapability('variables');

  /*
   * THE REFERENCES PANEL, gated the same way and grandfathered for the same reason.
   *
   * It is the only screen that can repoint or delete a name, so withholding it from a teacher
   * who has authored some would leave chips they can see and cannot touch — the trapdoor the
   * version pager and the scenario menu each opened once. So the panel follows what EXISTS,
   * while the controls that make a new one follow the capability.
   */
  const recallCapabilityOn = useCapability('answerRecall');
  const mayOpenRefs = recallCapabilityOn || (experience?.answerRefAliases?.length ?? 0) > 0;
  const mayAuthorVariables = variablesCapabilityOn
    || variables.length > 0 || (experienceVariables?.length ?? 0) > 0;

  const panelToggles = (showLabels: boolean): PanelToggleItem[] => [
    ...(mayAuthorVariables ? [{
      id: 'variables',
      icon: <Braces aria-hidden='true' className='h-5 w-5' />,
      label: t('task.bottomBar.variables'),
      active: variablesPanelOpen,
      disabled: readOnly,
      onToggle: () => variablesPanelOpen ? setVariablesPanelOpen(false) : handleOpenVariables(),
    }] : []),
    ...(mayOpenRefs ? [{
      id: 'references',
      // The BRACKETS, not a link. The link iconography belongs to the chip, where it carries a
      // status a glyph on a button could not; here it would just be a second link symbol
      // competing with the one in the prose. `[]` is what the insert button shows too, so the
      // two halves of the feature — put one in, manage them all — share a signifier.
      /*
       * Lucide's `Brackets`, not a typed `[]`.
       *
       * A monospace glyph could be matched to `Braces` in height but never in weight: one is a
       * 2px-stroked drawing on a 24px grid, the other is a font's idea of a bracket, and side
       * by side the typed one read as thinner and shorter however it was sized. Same family,
       * same stroke, same box — nothing left to tune.
       */
      icon: <Brackets aria-hidden='true' className='h-5 w-5' />,
      // The label carries the state, because the dot beside it cannot be heard.
      label: refStats.broken > 0
        ? t('blocks.answerRef.manager.needsAttention')
        : t('blocks.answerRef.manager.title'),
      active: refsPanelOpen,
      disabled: readOnly,
      badge: refStats.broken > 0 ? ('error' as const) : undefined,
      onToggle: () => refsPanelOpen ? setRefsPanelOpen(false) : handleOpenRefs(),
    }] : []),
    {
      id: 'flow',
      icon: <FlowIcon aria-hidden='true' className='h-5 w-5' />,
      label: hasFlowIssues ? t('task.bottomBar.flowNeedsAttention') : t('task.bottomBar.flow'),
      text: t('task.bottomBar.flow'),
      active: flowPanelOpen,
      disabled: readOnly,
      badge: hasFlowIssues ? ('warning' as const) : undefined,
      onToggle: () => flowPanelOpen ? setFlowPanelOpen(false) : handleOpenFlow(),
      // Only Flow shows its word, and only where there is room for one.
      showLabel: showLabels,
    },
  ];

  /**
   * Every exit from the editor, guarded — the experience crumb and the task stepper alike.
   *
   * Was `handleBack`, which only ever went to the experience. Generalising it is what let the
   * back chevron go: its guard was the one thing it owned that the Experience crumb did not.
   */
  function guardedExit(run: () => void) {
    // Also gate on `saving` — don't let a teacher navigate away mid-flight-save without at
    // least a beat to let it land (the save itself isn't aborted by navigating, but the
    // in-flight response's contentRevision reconciliation would be lost).
    if (isDirty || saving) { pendingExitRef.current = run; setShowUnsavedModal(true); }
    else run();
  }

  function handleNavigate(to: string) { guardedExit(() => navigate(to)); }

  /**
   * Where this task sits structurally — derived ONCE and handed to both steppers.
   *
   * The nav bar shows it on a phone and the title bar from `sm` up (measured: that row has 48px
   * spare at 375px against the ~98px it needs). Two renderings of one position; deriving it
   * twice would be two chances for them to disagree about where you are.
   */
  const taskStepperTrack = taskTrack(experience?.tasks ?? [], taskId);

  /** Do whatever the modal was deciding about; fall back to the experience. */
  function runPendingExit() {
    const run = pendingExitRef.current ?? (() => navigate(`/app/experience/${experienceId}`));
    pendingExitRef.current = null;
    run();
  }

  function handleLeaveWithoutSaving() {
    /*
     * Those edits are not coming back, so neither is the history that describes them.
     *
     * Undo steps hold the value before an edit and Redo holds the value after. Discarding the
     * edits leaves the content back at every step's `prev`, so a parked history would come back
     * with Undo doing nothing visible and REDO offering to reinstate work the teacher just said
     * to throw away. Marking the task here is the only place that knows the exit was a discard —
     * by the time `parkUndoHistory` runs, a discard and an ordinary hop look identical.
     */
    discardedTaskRef.current = shownTaskIdRef.current;
    setShowUnsavedModal(false);
    runPendingExit();
  }

  async function handleSaveAndLeave() {
    const ok = await handleSave();
    if (ok) { setShowUnsavedModal(false); runPendingExit(); }
  }

  // ── Variable operations ────────────────────────────────────────────────────

  function handleAddVariable(variable: TaskVariable) {
    if (readOnly) return;
    const idx = variablesRef.current.length;
    setVariablesWithRef([...variablesRef.current, variable]);
    pushOp({ type: 'add-variable', label: i18n.t('task.settings.variables.undoAdd'), variable, idx });
  }

  function handleUpdateVariable(id: string, patch: Partial<Omit<TaskVariable, 'id'>>) {
    if (readOnly) return;
    const prev = variablesRef.current.find(v => v.id === id);
    if (!prev) return;
    const next = { ...prev, ...patch };
    // If the name changed, rename all variable references in every block's data.
    // This covers blocks that read directly from props (TextBlock, MCQBlock, etc.).
    // Blocks that own local state (NumericalBlock) detect renames via their own
    // useEffect watching the variables context.
    if (patch.name !== undefined && patch.name !== prev.name && prev.name && patch.name) {
      // Refuse a rename onto a name another variable already holds. The sweep matches on NAME, so
      // it cannot tell one variable's chips from another's: renaming `a` to `b` while a different
      // variable is already called `b` rewrites BOTH sets of chips to `b` and the two become
      // permanently indistinguishable. Rejecting the rename is the only non-destructive answer —
      // two variables sharing a name is not a state the resolver could represent anyway.
      const clash = variablesRef.current.some(v => v.id !== id && v.name === patch.name);
      if (clash) return;
      const renamed = blocksRef.current.map(b => ({
        ...b,
        data: renameVarInBlockData(b.data as Record<string, unknown>, prev.name, patch.name as string, id),
      }));
      setBlocksWithRef(renamed);
      // A sibling EQUATION holds `{oldName}` too, and it is the one reference that lives outside
      // block data. The row and its dependents have to move together or `luminosity = {mass}^3.5`
      // is left pointing at a name nothing defines.
      setVariablesWithRef(variablesRef.current.map(v => v.id === id ? next
        : (v.formula ? { ...v, formula: renameVarInFormula(v.formula, prev.name, patch.name as string) } : v)));
      pushOp({ type: 'update-variable', label: i18n.t('task.settings.variables.undoUpdate'), variableId: id, prev, next });
      return;
    }
    setVariablesWithRef(variablesRef.current.map(v => v.id === id ? next : v));
    // Coalesced: an equation and a range are TYPED, so one step per letter would bury every other
    // edit in the stack. A rename takes the branch above and is deliberately its own step.
    pushOp({ type: 'update-variable', label: i18n.t('task.settings.variables.undoUpdate'), variableId: id, prev, next, coalesce: `var:${id}` });
  }

  /**
   * Which variables are referenced, for the panel's delete guard.
   *
   * A TASK variable only has to answer for this task. An EXPERIENCE variable has to answer for
   * the whole experience, which is the only version of the question that means anything when
   * every task can see it — and it is answerable without a request because the detail response
   * carries every task's blocks.
   */
  const variableUsage = useMemo(() => {
    // A sibling EQUATION is a dependent too, and the same scope rule applies to it: a task
    // variable answers for this task's equations, an experience variable for every task's.
    const taskScope = [...variables, ...(experience?.variables ?? [])];
    const everyVariable = [
      ...(experience?.variables ?? []),
      ...(experience?.tasks ?? []).flatMap(t => (t.id === taskId ? variables : (t.variables ?? []))),
    ];
    /*
     * WHERE a variable is used, not merely whether — the page is the only layer that knows the
     * task titles, so it is the one that can label a place.
     *
     * A block has no name of its own, so a place is its TYPE plus its position among blocks of
     * that type in its task: "MCQ 2". For a task other than the open one the task title leads,
     * since "MCQ 2" alone would send a teacher looking in the wrong place entirely.
     */
    const placesIn = (name: string, task: { id: string; title?: string; blocks?: Block[] }): string[] => {
      const list = task.id === taskId ? blocks : (task.blocks ?? []);
      const seen = new Map<string, number>();
      return list.flatMap(b => {
        const n = (seen.get(b.type) ?? 0) + 1;
        seen.set(b.type, n);
        if (!isVarUsed(name, [b])) return [];
        return [i18n.t('task.settings.variables.placeNumbered', { block: blockLabel(b.type), n })];
      });
    };

    const thisTask = { id: taskId ?? '', blocks };
    const otherTasks = (experience?.tasks ?? [])
      .filter(t => t.id !== taskId)
      .map(t => ({ id: t.id, title: t.title, blocks: t.blocks }));

    /*
     * THIS TASK FIRST. A teacher looking at a refusal is looking at THIS screen, so the useful
     * answer names blocks they can go and fix right now. Only when the variable appears nowhere
     * here does naming a block stop helping — "MCQ2" in a task you are not looking at points at
     * the wrong screen — and the answer becomes which TASKS to go to.
     */
    const NAMED = 2;
    const reason = (
      name: string,
      searchOtherTasks: boolean,
      varList: TaskVariable[],
    ): VariableUsage => {
      if (!name) return false;
      const here = placesIn(name, thisTask);
      const elsewhere = searchOtherTasks
        ? otherTasks.filter(t => isVarUsed(name, t.blocks ?? []))
        : [];
      if (here.length > 0) {
        const hereBlocks = here.length;
        return {
          kind: 'places',
          names: here.slice(0, NAMED),
          // Uses in other tasks are counted, not named: the teacher is on this screen.
          more: Math.max(0, hereBlocks - NAMED) + elsewhere.length,
        };
      }
      if (elsewhere.length > 0) {
        return {
          kind: 'tasks',
          names: elsewhere.slice(0, NAMED).map(t => t.title ?? ''),
          more: Math.max(0, elsewhere.length - NAMED),
        };
      }
      const dependent = firstDependentOf(name, varList);
      return dependent ? { kind: 'formula', dependent } : false;
    };

    return Object.fromEntries([
      // A TASK variable answers for this task; an EXPERIENCE variable for every task.
      ...variables.map(v => [v.id, reason(v.name, false, taskScope)]),
      ...(experience?.variables ?? []).map(v => [v.id, reason(v.name, true, everyVariable)]),
    ]) as Record<string, VariableUsage>;
  }, [experience, taskId, blocks, variables]);

  /**
   * PROMOTION: naming an experience variable something a task already uses.
   *
   * Held as pending state rather than applied, because the teacher has to answer first. Only ever
   * raised from a COMMITTED name — the row holds a draft and commits on blur, which is also why
   * typing `timespace` past an existing `time` does not raise it on the way.
   */
  const [promotion, setPromotion] = useState<{
    id: string;
    name: string;
    /** Every task that defines this name, matching or not — all of them are absorbed. */
    holders: { taskId: string; title: string; variable: TaskVariable }[];
    /** Only those whose numbers will actually MOVE. The dialog is about these and nothing else. */
    conflicts: { taskId: string; title: string; variable: TaskVariable }[];
    /** The range the promoted variable ends up with — see `definitionFor`. */
    definition: Definition;
  } | null>(null);
  /**
   * What to do with each CONFLICTING task, keyed by task id.
   *
   * Defaults to `separate`, which is paste's default and for its reason: keeping a task's own
   * numbers cannot silently change what a teacher already authored, and `shared` can. The teacher
   * flips the ones they actually want converted.
   */
  const [promotionChoices, setPromotionChoices] = useState<Record<string, 'shared' | 'separate'>>({});
  /** Names the teacher said no to, so the row shows the same error a duplicate name does. */
  const [declinedNames, setDeclinedNames] = useState<string[]>([]);

  /** Every task defining a TASK variable of this name, wherever it is. */
  function taskVariablesNamed(name: string) {
    return (experienceRef.current?.tasks ?? []).flatMap(t => {
      const list = t.id === taskId ? variablesRef.current : (t.variables ?? []);
      const variable = list.find(v => v.name === name);
      return variable ? [{ taskId: t.id, title: t.title, variable }] : [];
    });
  }

  /** Same range, same everything that decides what a student sees — so replacing changes nothing. */
  type Definition = Pick<TaskVariable, 'min' | 'max' | 'step' | 'formula' | 'value'>;

  /** The SAME function the paste conflict dialog renders with, not the same shape as it — two
   *  copies of this drifted apart to the point where neither knew a constant existed. */
  const describeRange = (v: Definition) => describeDefinition(v, t);

  /** Nothing has been said about what it IS yet — the state a variable is in the moment it is
   *  added, since the row asks for a name first and a range after. */
  const hasNoDefinition = (v: Definition) =>
    !Number.isFinite(v.min) && !Number.isFinite(v.max) && !Number.isFinite(v.step) && !v.formula;

  /**
   * What the promoted variable should END UP as.
   *
   * ADOPTS the existing definition when the new variable has none, which is the ordinary case: a
   * teacher adds an experience variable and types `river` before saying what `river` is. Taking
   * the empty definition at its word would delete `river[1..10]` from every task and leave an
   * undefined variable behind — the destructive reading of an action meant as "make this shared".
   */
  const definitionFor = (incoming: Definition, holders: { variable: TaskVariable }[]): Definition => {
    const first = holders[0]?.variable;
    if (!first || !hasNoDefinition(incoming)) return incoming;
    // `value` as well as the range: without it a promotion that inherits a CONSTANT's
    // definition inherited no definition at all, and every holder then compared equal to a
    // variable with no value.
    return { min: first.min, max: first.max, step: first.step, formula: first.formula, value: first.value };
  };

  /**
   * Carry out a promotion: the experience variable takes the name, and the task-level copies it
   * replaces are deleted.
   *
   * The copies live in tasks nobody has open, which is why `PATCH .../tasks/:id` grew a
   * `variables` field — writing them through the blocks endpoint would mean sending block content
   * this page may be holding a stale copy of.
   *
   * The chips are deliberately left alone. They store the NAME, and the name still resolves, now
   * to the experience variable: nothing is re-anchored because nothing was anchored to an id.
   */
  async function applyPromotion(
    p: NonNullable<typeof promotion>,
    choices: Record<string, 'shared' | 'separate'>,
  ) {
    const list = experienceVariablesRef.current ?? [];
    const prev = list.find(v => v.id === p.id);
    if (!prev) return;

    /*
     * ONE AT A TIME, and that is not a style preference.
     *
     * Every task lives inside the SAME experience document, so a write per task plus the
     * experience's own are several writes to one document. Fired together they raced on
     * mongoose's `__v` and the later ones came back `VersionError: No matching document found`,
     * which is a 500 and a promotion that half happened.
     */
    const absorbed: string[] = [];
    for (const holder of p.holders) {
      /*
       * A task whose definition MATCHES is absorbed without asking — there was nothing to decide.
       * One that differs does what the teacher chose. SHARED drops its row, so its chips resolve
       * to the experience variable and its numbers move. SEPARATE keeps its numbers by renaming
       * the row and every chip pointing at it, which frees the name without touching the
       * question.
       */
      const conflicted = p.conflicts.some(c => c.taskId === holder.taskId);
      if (conflicted && (choices[holder.taskId] ?? 'separate') === 'separate') {
        const taskOf = experienceRef.current?.tasks.find(t => t.id === holder.taskId);
        if (!taskOf) continue;
        const freeName = freeVariableName(holder.variable.name, [
          ...list.map(v => v.name), ...(taskOf.variables ?? []).map(v => v.name), p.name,
        ]);
        if (holder.taskId === taskId) {
          setVariablesWithRef(variablesRef.current.map(v => (v.id === holder.variable.id ? { ...v, name: freeName } : v)));
          setBlocksWithRef(blocksRef.current.map(b => ({
            ...b,
            data: renameVarInBlockData(b.data as Record<string, unknown>, holder.variable.name, freeName, holder.variable.id),
          })));
        } else {
          const plan = planTaskRename(taskOf, holder.variable.name, freeName, holder.variable.id);
          if (plan) await renameAcrossTasks([plan]);
        }
        continue;
      }

      absorbed.push(holder.taskId);
      if (holder.taskId === taskId) {
        setVariablesWithRef(variablesRef.current.filter(v => v.id !== holder.variable.id));
      } else {
        const remaining = (experienceRef.current?.tasks.find(t => t.id === holder.taskId)?.variables ?? [])
          .filter(v => v.id !== holder.variable.id);
        try {
          await api.patch(`/experiences/${experienceId}/tasks/${holder.taskId}`, { variables: remaining });
        } catch { /* the panel still shows the promoted variable; a reload re-reads the truth */ }
      }
    }

    // Local copy too, or the guards and the collision check keep seeing variables that are gone.
    const promoted = (v: TaskVariable) => ({ ...v, ...p.definition, name: p.name });
    setExperience(prevExp => prevExp && {
      ...prevExp,
      variables: list.map(v => (v.id === p.id ? promoted(v) : v)),
      tasks: prevExp.tasks.map(t => (absorbed.includes(t.id)
        ? { ...t, variables: (t.variables ?? []).filter(v => v.name !== p.name) }
        : t)),
    });
    const next = list.map(v => (v.id === p.id ? promoted(v) : v));
    experienceVariablesRef.current = next;
    // Last, and awaited for the same reason as the loop above.
    try {
      await api.patch(`/experiences/${experienceId}`, { variables: next.filter(isSavableVariable) });
    } catch { /* as above */ }
    setDeclinedNames(names => names.filter(n => n !== p.name));
    /*
     * A PROMOTION RETIRES ITS OWN OPS, and leaves the rest of the history alone.
     *
     * It writes tasks that are not loaded, and `StackState` holds only this task's lists and the
     * experience's — so an undo of the op that CREATED this variable would remove it and bring
     * none of the absorbed task copies back, orphaning every chip. Measured before this existed:
     * every `river` in the experience disappeared.
     *
     * Clearing the whole stack was the first answer and cost too much — a teacher loses every
     * block edit they made before promoting. Only the ops ABOUT THIS VARIABLE are unsafe, so
     * those are dropped and the cursor pulled back past each one. Everything else still undoes.
     *
     * Not rigorous in general: the surviving ops were recorded against states that included the
     * removed ones. For variable ops against block ops that is harmless, since neither reads the
     * other's half of `StackState`.
     */
    const { ops, cursor } = stack.peek();
    const retired = (op: Op) =>
      (op.type === 'add-variable' || op.type === 'remove-variable')
        ? op.scope === 'experience' && op.variable.id === p.id
        : op.type === 'update-variable' && op.scope === 'experience' && op.variableId === p.id;
    stack.restore({
      ops: ops.filter(op => !retired(op)),
      cursor: cursor - ops.slice(0, cursor + 1).filter(retired).length,
    });
  }

  // ── Experience variable operations ─────────────────────────────────────────

  /**
   * The tasks OTHER than this one whose blocks reference a name.
   *
   * The experience detail response carries every task's blocks, so this needs no request. It is
   * what makes an experience variable's delete guard and rename guard answerable at all: the
   * task-level versions of both only ever looked at the task in front of the teacher.
   */
  function otherTasksUsing(name: string): Task[] {
    if (!name) return [];
    return (experienceRef.current?.tasks ?? []).filter(t => t.id !== taskId
      && (isVarUsed(name, t.blocks ?? []) || isVarUsedByFormula(name, t.variables ?? [])));
  }

  /**
   * Write the experience's variables: local state first so the panel answers immediately, then
   * the PATCH.
   *
   * A different document from the task, so this does not ride the task's autosave. The endpoint
   * sets the field behind `!== undefined`, so a write here cannot disturb anything else about the
   * experience — which matters, because an experience-level write is the shape that has wiped one.
   */
  function writeExperienceVariables(next: TaskVariable[]) {
    setExperience(prev => (prev ? { ...prev, variables: next } : prev));
    experienceVariablesRef.current = next;
    // Filtered, like the task's save above: a row with no name yet is the teacher mid-typing,
    // and sending it is a 400 the moment Add is pressed.
    api.patch(`/experiences/${experienceId}`, { variables: next.filter(isSavableVariable) }).catch(() => {});
  }

  /**
   * Carry a rename into the tasks that are not open, one at a time.
   *
   * SEQUENTIAL for the same reason the promotion is: every task lives in the same experience
   * document, and parallel writes lose on mongoose's `__v`.
   *
   * `expectedRevision` is the whole basis for writing a task nobody is editing. The page holds a
   * copy from the experience it loaded and someone else may have saved since; the revision turns
   * that from a silent clobber into a refusal, which this reports by name rather than letting the
   * rename half-land in silence.
   */
  async function renameAcrossTasks(plans: TaskRenamePlan[]): Promise<string[]> {
    const refused: string[] = [];
    for (const plan of plans) {
      try {
        await api.put(`/experiences/${experienceId}/tasks/${plan.taskId}/blocks`, {
          blocks: plan.blocks,
          variables: plan.variables.filter(isSavableVariable),
          expectedRevision: plan.expectedRevision,
        });
        // Keep the page's copy in step, so a second rename plans against what is now stored.
        setExperience(prev => prev && {
          ...prev,
          tasks: prev.tasks.map(t => (t.id === plan.taskId
            ? { ...t, blocks: plan.blocks, variables: plan.variables, contentRevision: plan.expectedRevision + 1 }
            : t)),
        });
      } catch {
        refused.push(plan.title);
      }
    }
    if (refused.length) setToastMsg(i18n.t('task.settings.variables.renameRefused', { tasks: refused.join(', ') }));
    return refused;
  }

  /**
   * RENAME A WHOLE SET, as one operation.
   *
   * This was N calls to `handleUpdateVariable` / `handleUpdateExperienceVariable`, one per
   * member, and in the experience scope that lost data two separate ways:
   *
   *  - each call captured the variable list BEFORE its `await` and wrote it after, so the last
   *    write to land erased every earlier member's rename;
   *  - each call planned its cross-task writes against the same loaded `contentRevision`, so
   *    the first PUT bumped it and the rest came back 409.
   *
   * What a teacher saw was "rename refused" and one member moved to the new name while the
   * others stayed — a second set, every single attempt.
   *
   * Neither is fixable by reordering the N calls, because the unit is wrong: a set rename is one
   * thing the teacher did. So it is one sweep of this task's blocks, one plan per other task,
   * one write per scope, and one entry on the undo stack.
   */
  function handleRenameSet(from: string, to: string, scope: VariableScope) {
    if (readOnly || !to || to === from) return;

    const experienceList = experienceVariablesRef.current ?? [];
    const owning = scope === 'experience' ? experienceList : variablesRef.current;
    const members = owning.filter(v => v.set === from && v.name);
    if (members.length === 0) return;

    const pairs = members.map(m => [m.name, composeSetName(to, setLeaf(m))] as const);

    // Refused whole, never in part. The per-variable guard's reasoning holds here and matters
    // more: landing some members on the new name and leaving the rest is the exact half-state
    // this rewrite exists to stop producing.
    const taken = new Set([...experienceList, ...variablesRef.current]
      .filter(v => !members.some(m => m.id === v.id)).map(v => v.name));
    if (pairs.some(([, to2]) => taken.has(to2))) return;

    /** Renamed rows (only in the list that owns the set) and swept formulas (in both). */
    const renameIn = (list: TaskVariable[], owns: boolean) => list.map(v => {
      let next = owns && v.set === from ? { ...v, set: to, name: composeSetName(to, setLeaf(v)) } : v;
      for (const [a, b] of pairs) {
        if (next.formula) next = { ...next, formula: renameVarInFormula(next.formula, a, b) };
      }
      return next;
    });

    // This task's blocks, swept once for every pair. The open task is never in the plans below:
    // it is saved by its own autosave, like any other edit made here.
    let swept = blocksRef.current;
    for (const [i, [a, b]] of pairs.entries()) {
      const id = members[i]?.id;
      swept = swept.map(blk => ({
        ...blk, data: renameVarInBlockData(blk.data as Record<string, unknown>, a, b, id),
      }));
    }
    setBlocksWithRef(swept);
    setVariablesWithRef(renameIn(variablesRef.current, scope === 'task'));

    if (scope === 'experience') {
      const nextList = renameIn(experienceList, true);
      const plans = planSetRenameAcrossTasks(experienceRef.current?.tasks ?? [], taskId, pairs);
      // Sequenced exactly as a single experience rename is, and for the same reason: these are
      // several writes to ONE experience document, and unawaited they race on mongoose's `__v`.
      void (async () => {
        await renameAcrossTasks(plans);
        writeExperienceVariables(nextList);
      })();
    }

    pushOp({
      type: 'rename-set', from, to, scope,
      label: i18n.t('task.settings.variables.undoRenameSet'),
    });
  }

  /**
   * DUPLICATE A SET, as one operation.
   *
   * `duplicateSet` does the hard part — re-pointing the copy's internal references so `starB_lum`
   * is computed from `starB_mass` and not still from star A. This only has to land the result as
   * a single write and a single undo step, rather than the N `add-variable` calls it was, which
   * cost the teacher five presses of undo to take back one press of Duplicate.
   */
  function handleDuplicateSet(set: string, scope: VariableScope) {
    if (readOnly) return;
    const list = scope === 'experience' ? (experienceVariablesRef.current ?? []) : variablesRef.current;
    const { added } = duplicateSet(set, list, () => `var_${crypto.randomUUID()}`);
    if (added.length === 0) return;

    const idx = list.length;
    if (scope === 'experience') writeExperienceVariables([...list, ...added]);
    else setVariablesWithRef([...list, ...added]);

    pushOp({
      type: 'add-set', set: added[0]!.set!, variables: added, idx, scope,
      label: i18n.t('task.settings.variables.undoDuplicateSet'),
    });
  }

  /**
   * DELETE A SET AND EVERYTHING IN IT, as one operation.
   *
   * This was one `remove-variable` per member, which is what "the delete is deleting elements
   * within the set instead of the whole set at once" was: five rows vanishing one after another,
   * and five presses of undo to get them back — through four intermediate states in which an
   * equation stands on a member that has not come back yet.
   *
   * The guard is `setDeleteBlocker`'s, not the per-variable one: members depending on EACH OTHER
   * is not a reason to stop, because they are leaving together. Only a dependent left behind is.
   */
  function handleDeleteSet(set: string, scope: VariableScope) {
    if (readOnly) return;
    const experienceList = experienceVariablesRef.current ?? [];
    const list = scope === 'experience' ? experienceList : variablesRef.current;
    const members = list.filter(v => v.set === set);
    if (members.length === 0) return;

    // Everything that could still be pointing at a member, which for an experience variable is
    // every task in the experience and not merely the open one.
    const outsiders = [...experienceList, ...variablesRef.current].filter(v => v.set !== set);
    for (const m of members) {
      if (!m.name) continue;
      if (isVarUsed(m.name, blocksRef.current)) return;
      if (isVarUsedByFormula(m.name, outsiders)) return;
      if (scope === 'experience' && otherTasksUsing(m.name).length > 0) return;
    }

    const idx = list.findIndex(v => v.set === set);
    const gone = new Set(members.map(m => m.id));
    if (scope === 'experience') writeExperienceVariables(list.filter(v => !gone.has(v.id)));
    else setVariablesWithRef(list.filter(v => !gone.has(v.id)));

    pushOp({
      type: 'remove-set', set, variables: members, idx, scope,
      label: i18n.t('task.settings.variables.undoDeleteSet'),
    });
  }

  function handleAddExperienceVariable(variable: TaskVariable) {
    if (readOnly) return;
    const list = experienceVariablesRef.current ?? [];
    writeExperienceVariables([...list, variable]);
    pushOp({
      type: 'add-variable', scope: 'experience', variable, idx: list.length,
      label: i18n.t('task.settings.variables.undoAddExperience'),
    });
  }

  function handleUpdateExperienceVariable(id: string, patch: Partial<Omit<TaskVariable, 'id'>>) {
    if (readOnly) return;
    const list = experienceVariablesRef.current ?? [];
    const prev = list.find(v => v.id === id);
    if (!prev) return;
    const next = { ...prev, ...patch };

    /*
     * NAMING, which is not the same as RENAMING, and conflating them hid the promotion entirely.
     *
     * A variable added here has no name yet — the row asks for one first — so a guard written as
     * "the name changed AND there was one before" skips the very case the promotion exists for:
     * typing `river` into a new experience variable while tasks already define `river`. It
     * committed silently instead of asking.
     */
    if (patch.name !== undefined && patch.name && patch.name !== prev.name) {
      // Only another EXPERIENCE variable is a duplicate. A TASK variable of the same name is the
      // promotion below, and treating it as a clash is the dead end this ran into: the row never
      // committed, so the question was never asked.
      if (list.some(v => v.id !== id && v.name === patch.name)) return;
      /*
       * REFUSED while another task references it. Chips store the NAME, so a rename has to sweep
       * every block that holds one — and for an experience variable those blocks can be in tasks
       * that are not loaded for editing. Sweeping them means a write per task, through the fork
       * and revision machinery, which is a much larger change than this.
       *
       * Refusing is the honest half-measure: a rename that swept only the open task would leave
       * every other task's chips pointing at a name that no longer exists. Changing a RANGE,
       * which is what the scope exists for, needs no sweep and is never refused.
       */
      /*
       * The name the teacher typed is one a TASK variable already holds somewhere. Ask rather
       * than refuse: refusing would leave an experience built out of tasks that each defined the
       * same variable with no way to collapse them, which is the state this whole scope exists
       * to get out of.
       */
      const holders = taskVariablesNamed(patch.name);
      if (holders.length > 0) {
        const definition = definitionFor(next, holders);
        /*
         * ONLY A REAL MISMATCH INTERRUPTS, which is the rule the paste path already follows:
         * "an unknown name is imported and an identical definition is reused, because in neither
         * case is there anything a teacher could usefully choose between."
         *
         * Listing every task that merely SHARES the name was the complaint — it read as a wall of
         * consequences for an action that, where the definitions agree, changes nothing a student
         * sees. Here that is the common case and the usual answer is no dialog at all.
         */
        const conflicts = holders.filter(h => !sameDefinition(h.variable, definition));
        const pending = { id, name: patch.name, holders, conflicts, definition };
        if (conflicts.length === 0) { void applyPromotion(pending, {}); return; }
        setPromotionChoices(Object.fromEntries(conflicts.map(c => [c.taskId, 'separate' as const])));
        setPromotion(pending);
        return;
      }

      /*
       * Only now is it a real rename, and only a variable that HAD a name has chips to sweep.
       *
       * This was REFUSED whenever another task referenced the name, because chips store the name
       * and those blocks can be in tasks that are not loaded — a sweep of only this one would
       * leave the rest pointing at a name that no longer exists. They are swept now, each write
       * guarded by its own revision. See `renameAcrossTasks`.
       */
      if (prev.name) {
        setBlocksWithRef(blocksRef.current.map(b => ({
          ...b,
          data: renameVarInBlockData(b.data as Record<string, unknown>, prev.name, patch.name as string, id),
        })));
        /*
         * SEQUENCED, not fired alongside the write below.
         *
         * Every task lives in the SAME experience document, so the per-task block writes and the
         * experience's own PATCH are several writes to one document; unawaited they raced on
         * mongoose's `__v` and one of them lost. That is what "the rename sometimes pops back"
         * was. `applyPromotion` already sequences for this reason; this path did not.
         */
        const plans = planRenameAcrossTasks(
          experienceRef.current?.tasks ?? [], taskId, prev.name, patch.name, id,
        );
        void (async () => {
          await renameAcrossTasks(plans);
          writeExperienceVariables(list.map(v => (v.id === id ? next : v)));
        })();
        pushOp({
          type: 'update-variable', scope: 'experience', variableId: id, prev, next,
          label: i18n.t('task.settings.variables.undoUpdateExperience'),
        });
        return;
      }
    }

    writeExperienceVariables(list.map(v => (v.id === id ? next : v)));
    // Same reasoning as the task scope: typed fields coalesce, a rename takes the branch above.
    pushOp({
      type: 'update-variable', scope: 'experience', variableId: id, prev, next,
      coalesce: `var:${id}`,
      label: i18n.t('task.settings.variables.undoUpdateExperience'),
    });
  }

  function handleRemoveExperienceVariable(id: string) {
    if (readOnly) return;
    const list = experienceVariablesRef.current ?? [];
    const idx = list.findIndex(v => v.id === id);
    if (idx === -1) return;
    const variable = list[idx]!;
    // The guard spans the whole experience, which is the only version of it that means anything
    // for a variable every task can see.
    if (variable.name && (isVarUsed(variable.name, blocksRef.current)
      || isVarUsedByFormula(variable.name, [...list, ...variablesRef.current])
      || otherTasksUsing(variable.name).length > 0)) return;
    writeExperienceVariables(list.filter(v => v.id !== id));
    pushOp({
      type: 'remove-variable', scope: 'experience', variable, idx,
      label: i18n.t('task.settings.variables.undoRemoveExperience'),
    });
  }

  function handleRemoveVariable(id: string) {
    if (readOnly) return;
    const idx = variablesRef.current.findIndex(v => v.id === id);
    if (idx === -1) return;
    const variable = variablesRef.current[idx]!;
    // Guard: don't delete a variable anything still depends on — a block, or a sibling equation.
    if (variable.name && (isVarUsed(variable.name, blocksRef.current)
      || isVarUsedByFormula(variable.name, [...variablesRef.current, ...(experienceVariablesRef.current ?? [])]))) return;
    setVariablesWithRef(variablesRef.current.filter(v => v.id !== id));
    pushOp({ type: 'remove-variable', label: i18n.t('task.settings.variables.undoRemove'), variable, idx });
  }

function handlePhoneEditOpen(blockId: string) {
    flushPendingUpdate();
    phoneSessionRef.current = openSession(blockId, blocksRef.current.find(b => b.id === blockId)?.data ?? {});
    setPhoneBlockCanUndo(false);
    setPhoneBlockCanRedo(false);
    setMobileEditingId(blockId);
  }

  function handlePhoneEditClose() {
    const session = phoneSessionRef.current;
    phoneSessionRef.current = null;
    setPhoneBlockCanUndo(false);
    setPhoneBlockCanRedo(false);
    setMobileEditingId(null);
    // One op for the whole visit, or none — `closeSession` also returns none when the block was
    // deleted mid-session, which used to commit an op for a block that no longer existed.
    const commit = closeSession(
      session,
      blocksRef.current.find(b => b.id === session?.blockId)?.data,
      bid => blocksRef.current.find(b => b.id === bid)?.data as Record<string, unknown> | undefined,
    );
    if (!commit) return;
    const block = blocksRef.current.find(b => b.id === commit.blockId);
    const label = i18n.t('task.a11y.editedBlock', { label: block ? blockLabel(block.type) : commit.blockId });
    /*
     * A visit that moved SIBLINGS commits as one multi-block op — spreading `commit` into an
     * `update-block` would have dropped `others` on the floor without a type error, because a
     * spread's extra properties are not checked. One editor visit is still one undo step; it
     * just now covers everything the visit changed.
     */
    if (commit.others.length > 0) {
      stack.push({
        type: 'update-blocks', label,
        entries: [{ blockId: commit.blockId, prev: commit.prev, next: commit.next }, ...commit.others],
      });
      return;
    }
    stack.push({
      type: 'update-block', label, blockId: commit.blockId, prev: commit.prev, next: commit.next,
      fields: changedFields(commit.prev, commit.next),
    });
  }

  function handlePhoneBlockUndo() {
    if (readOnly) return;
    const session = phoneSessionRef.current;
    const step = session && stepBack(session);
    if (!step) return;
    phoneSessionRef.current = step.session;
    setBlocksWithRef(blocksRef.current.map(b => b.id === step.session.blockId ? { ...b, data: step.data } : b));
    setPhoneBlockCanUndo(sessionCanUndo(step.session));
    setPhoneBlockCanRedo(sessionCanRedo(step.session));
    setPhoneBlockMountKey(k => k + 1);
  }

  function handlePhoneBlockRedo() {
    if (readOnly) return;
    const session = phoneSessionRef.current;
    const step = session && stepForward(session);
    if (!step) return;
    phoneSessionRef.current = step.session;
    setBlocksWithRef(blocksRef.current.map(b => b.id === step.session.blockId ? { ...b, data: step.data } : b));
    setPhoneBlockCanUndo(sessionCanUndo(step.session));
    setPhoneBlockCanRedo(sessionCanRedo(step.session));
    setPhoneBlockMountKey(k => k + 1);
  }

/**
   * Enter Preview, leaving every block-editing surface behind.
   *
   * Preview is the student's view, so nothing should still be "being edited" inside it. Today each
   * block checks `isPreview` before its editing branches, so a retained id renders nothing — but
   * the state survived the trip and the block sprang back open on the way out, and an editing id
   * that outlives the mode with no editing in it only stays invisible until someone reorders those
   * checks.
   *
   * Closes through the SAME paths the close buttons use rather than nulling the ids: the phone
   * session owns an undo entry (handlePhoneEditClose collapses the session's edits into one
   * task-level op), so a bare `setMobileEditingId(null)` would drop that edit out of the undo
   * history entirely.
   */
  function handleEnterPreview() {
    if (mobileEditingId) handlePhoneEditClose();
    setSelectedBlockId(null);
    // Both authoring panels close on the way in. Leaving Flow open was visible rather than
    // theoretical: its rows label conditions by BLOCK NAME, and those labels kept rendering over a
    // Preview that is meant to be the student's view of the task. Variables closes for the same
    // reason — Preview draws ONE roll for the whole task, so a panel offering to edit the ranges it
    // was drawn from invites an edit Preview will not reflect.
    setFlowPanelOpen(false);
    setVariablesPanelOpen(false);
    setRefsPanelOpen(false);
    preview.start(experienceId);
  }

  function handleOpenVariables() {
    // `isPreview` for the same reason as `readOnly` below: Preview is the student's view, and the
    // phone portal renders OUTSIDE the content area, so it would float an authoring panel over it.
    if (isPreview) return;
    // Guarding here (rather than only on handleAddVariable/etc.) means the phone full-screen
    // portal below never opens at all while read-only — that portal renders outside the inert-
    // wrapped content area (it's a createPortal to document.body), so it would otherwise show an
    // editable-looking panel whose own Add/Update/Remove handlers silently no-op.
    if (readOnly) return;
    // The selected block STAYS selected. The panel takes the shared slot ahead of the editor, and
    // closing it hands the slot back, so a teacher who opens Variables mid-edit returns to the
    // block they were editing rather than to a dimmed canvas.
    setFlowPanelOpen(false);
    setRefsPanelOpen(false);
    setVariablesPanelOpen(true);
  }

  function handleOpenFlow() {
    // Same reasoning as handleOpenVariables above.
    if (isPreview || readOnly) return;
    setVariablesPanelOpen(false);
    setRefsPanelOpen(false);
    setFlowPanelOpen(true);
  }

  function handleOpenRefs() {
    // Same reasoning as handleOpenVariables above. Read-only matters here for a reason the other
    // two do not have: the panel's one action WRITES to blocks, so a read-only teacher would be
    // offered a Re-point button that silently does nothing.
    if (isPreview || readOnly) return;
    setVariablesPanelOpen(false);
    setFlowPanelOpen(false);
    setRefsPanelOpen(true);
  }

  /**
   * Every chip on this task that named `from` now names `to`, in one undoable edit per block.
   *
   * Driven off `repointAnswerRefs`' identity return rather than a diff: a block whose data comes
   * back as the same object held no matching chip, so it is never touched and never lands in the
   * undo stack as a no-op step.
   */
  /**
   * Point an alias at a different answer. Every chip naming it follows, with no sweep.
   *
   * This used to rewrite each chip's own stored address, which is why it had to be one undo op
   * across N blocks. The alias made that structural: there is one object, so there is one
   * write, and no intermediate state where half the chips have moved. The blocks are not
   * touched at all — which is also why this is not in the block undo stack.
   */
  /**
   * Travel to a task a name is used on, from the Answer Recall panel.
   *
   * The panel reports a break against the task it HAPPENS on, which may not be this one, and
   * until now that was a problem a teacher could read and not reach. The panel closes on the
   * way: it reopens on whatever task they land on, and one left hanging over a task they did
   * not choose to open is furniture in the way.
   */
  function openTaskFromPanel(id: string) {
    setRefsPanelOpen(false);
    navigate(`/app/task/${id}?experience=${experienceId}`);
  }

  /**
   * @param replacing which ADDRESS is being retargeted, when the name holds several.
   *
   * Without it, re-pointing a name with two equivalent answers collapsed it to one and the
   * teacher could not tell which they had just lost. The control now sits on the address it
   * acts on, so this says which.
   */
  function handleRepointAlias(aliasId: string, to: AnswerRef, replacing?: AnswerRef) {
    if (readOnly) return;
    const live = aliasesRef.current.find(a => a.id === aliasId);
    if (live) {
      const next = { ...to, label: aliasMemberLabel(to) };
      updateAlias(aliasId, {
        // Named address: swap that one in place, so its siblings and their order survive.
        // No address named: the name as a whole moves, which is what it meant before.
        members: replacing
          ? live.members.map(m => (sameRef(m, replacing) ? next : m))
          : [next],
      });
      return;
    }

    /*
     * THE ROW HAS NO ALIAS TO EDIT, which is the one case re-point used to fail silently at.
     *
     * Two kinds reach here: a chip naming an alias that is gone, and a chip authored before
     * aliases existed, whose row carries the empty id. Editing a list that does not contain
     * the id wrote the same list back, so the button worked and nothing moved.
     *
     * The fix is a chip edit rather than an alias edit: find or make the alias for the chosen
     * answer, then rewrite every chip on this task that named the dead id so it names the live
     * one. One `update-blocks` op, because it is one decision across however many blocks.
     */
    const targetId = ensureAlias(to);
    const name = aliasDisplayName(
      aliasesRef.current.find(a => a.id === targetId),
      { tasks: experience?.tasks ?? [], aliases: aliasesRef.current }, i18n.t);
    const entries: { blockId: string; prev: Record<string, unknown>; next: Record<string, unknown> }[] = [];
    for (const block of blocksRef.current) {
      const prev = (block.data ?? {}) as Record<string, unknown>;
      const next = retargetChips(prev, aliasId, targetId, name);
      if (next !== prev) entries.push({ blockId: block.id, prev, next });
    }
    if (entries.length === 0) return;
    const byId = new Map(entries.map(e => [e.blockId, e.next]));
    stack.push({ type: 'update-blocks', label: i18n.t('blocks.answerRef.manager.reanchorOp'), entries });
    setBlocksWithRef(blocksRef.current.map(b => byId.has(b.id) ? { ...b, data: byId.get(b.id)! } : b));
  }


  /**
   * NOT in the undo/redo stack — the second and last exemption, and a DATED one.
   *
   * Flow's model is mid-rework: the condition list is due to replace the block-by-state grid and
   * the task-level goto picker is being reconciled to the board's. Op types written against the
   * grid would have to be written again against whatever replaces it, so this waits rather than
   * being done twice. Revisit when that lands — see `docs/copy-paste.md`'s sibling flow notes.
   *
   * Recorded 2026-09-20. If you are reading this after the flow rework shipped, this exemption has
   * expired and flow edits should be undoable like everything else.
   */
  /**
   * Flow edits are UNDOABLE, which they were not until now.
   *
   * They were one of two mutations exempt from the stack, and unlike the other one (a rename, which
   * you undo by clearing the field) there was no reason for it — a deleted event, a re-pointed
   * destination and a reordered list are all things a teacher can do by accident and cannot put
   * back by hand.
   *
   * The caller says what it did. One `update-flow` op covers every kind of flow edit, so the label
   * is the only thing that distinguishes "Undo: deleted event" from "Undo: moved event", and the
   * panel is the only place that knows which it was.
   */
  function handleFlowChange(next: TaskFlowConfig, label: string, coalesce?: string) {
    if (readOnly) return;
    stack.push({ type: 'update-flow', label, prev: flowRef.current, next, ...(coalesce ? { coalesce } : {}) });
    setFlowWithRef(next);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-ws-bg'>
        <p className='text-sm text-ws-muted'>{t('task.loading')}</p>
      </div>
    );
  }

  if (error || !experience || !task) {
    // Three different failures used to share one message, so "Task not found" was reported for all
    // of them and told us nothing about which. Both keys already exist; only the mapping is new:
    //   - `error`            a request that actually failed, with its own message
    //   - no experience      the experience itself did not load
    //   - no task            the experience loaded and this task is genuinely not in it
    const failure = error || (!experience ? t('task.errors.couldNotLoad') : t('task.errors.notFound'));
    return (
      <div className='flex min-h-screen flex-col items-center justify-center gap-4 bg-ws-bg'>
        <p className='text-sm text-red-500'>{failure}</p>
        <button onClick={() => navigate(-1)} className='text-sm text-[#03258c] underline'>{t('common.goBack')}</button>
      </div>
    );
  }

  function renderDragOverlay() {
    if (!activeDrag) return null;
    if (activeDrag.source === 'library') {
      const meta = BLOCK_META[activeDrag.blockType];
      if (!meta) return null;
      const { Icon, labelKey } = meta;
      return (
        <div className='flex flex-col items-center gap-1.5 rounded-lg border border-ws-btn-active-fg bg-ws-elevated px-4 py-3 shadow-lg text-ws-btn-active-fg'>
          <Icon className='h-5 w-5' />
          <span className='text-xs'>{t(labelKey)}</span>
        </div>
      );
    }
    const block = activeDrag.block;
    // onSelect is a required no-op here, not just an omission: every block shares the same
    // `isEditing = embedded || (!onSelect && !hasContent)` initial-state pattern, so an empty block
    // (no question/answer/asset/series yet) being reordered would otherwise open its full editing
    // panel as the drag ghost instead of the compact card — heavier to render on every drag frame
    // (and visibly wrong) compared to the collapsed preview every other block gets.
    const overlayProps = { isPreview: false as const, isMain: true, isSelected: false, isDragOver: false, experienceId, onSelect: () => {}, onUpdate: () => {}, onRemove: () => {}, onDuplicate: () => {} };
    return (
      <div className='rounded-xl shadow-xl ring-2 ring-ws-btn-active-fg opacity-95 pointer-events-none'>
        {block.type === 'text'      ? <TextBlock     block={block} {...overlayProps} /> :
         block.type === 'image'     ? <ImageBlock    block={block} {...overlayProps} /> :
         block.type === 'mcq'       ? <MCQBlock      block={block} {...overlayProps} /> :
         block.type === 'numerical' ? <NumericalBlock block={block} {...overlayProps} /> :
         block.type === 'keyword'   ? <KeywordBlock  block={block} {...overlayProps} /> :
         block.type === 'fitb'      ? <FitbBlock     block={block} {...overlayProps} /> :
         block.type === 'dropdown'  ? <DropdownBlock block={block} {...overlayProps} /> :
         block.type === 'sound'     ? <SoundBlock    block={block} {...overlayProps} /> :
         block.type === 'dataviz'   ? <DataVizBlock  block={block} {...overlayProps} /> :
         block.type === 'slider'    ? <SliderBlock   block={block} {...overlayProps} /> :
         block.type === 'gps'       ? <GpsBlock      block={block} {...overlayProps} /> :
         block.type === 'ordering'  ? <OrderingBlock block={block} {...overlayProps} /> :
         block.type === 'scale'     ? <ScaleBlock    block={block} {...overlayProps} /> :
         block.type === 'compass'   ? <CompassBlock  block={block} {...overlayProps} /> :
         block.type === 'rotation'     ? <RotationBlock    block={block} {...overlayProps} /> :
         <div className='rounded-xl border border-ws-border bg-ws-surface p-4'><span className='text-sm text-ws-muted'>{block.type}</span></div>}
      </div>
    );
  }

  handlePhoneBlockUndoRef.current = handlePhoneBlockUndo;
  handlePhoneBlockRedoRef.current = handlePhoneBlockRedo;

  const phoneUndoValue = {
    canUndo: phoneBlockCanUndo,
    canRedo: phoneBlockCanRedo,
    undoLabel: null,
    redoLabel: null,
    onUndo: handlePhoneBlockUndo,
    onRedo: handlePhoneBlockRedo,
  };

  /**
   * The task's one phone: what it can be pointed at, and whether anything reads it.
   *
   * Derived here rather than in the sheet so the pin has somewhere sensible to start before the
   * sheet is ever opened — the provider seeds its position from the first authored target, and a
   * task whose sensor blocks have no target yet is the only case that falls back to nowhere.
   */
  const simTargets = gpsTargetsIn(blocks, n => t('blocks.gps.title') + ' ' + n);
  const simBands = compassBandsIn(blocks, n => t('blocks.compass.title') + ' ' + n);
  const simRotationBands = rotationBandsIn(blocks, n => t('blocks.rotation.title') + ' ' + n);
  const hasGpsBlock = taskHasBlock(blocks, 'gps');
  const hasCompassBlock = taskHasBlock(blocks, 'compass');
  const hasRotationBlock = taskHasBlock(blocks, 'rotation');
  const simTiltBands = tiltBandsIn(blocks, n => t('blocks.tilt.title') + ' ' + n);
  const hasTiltBlock = taskHasBlock(blocks, 'tilt');
  const hasSensorBlock = hasGpsBlock || hasCompassBlock || hasRotationBlock || hasTiltBlock;

  /**
   * A task with nothing to answer never grades, so it never submits.
   *
   * A "pause to read this" task is finished the moment it is read, and a Submit on it would open a
   * dialog reporting nought of nought — three zeros and no reason to have been interrupted. Gated on
   * block TYPE, the same rule the flow panel uses to decide that such a task has only one event.
   */
  const previewHasInput = taskHasGradableBlock(blocks);
  // Before a submission there is no recorded outcome, so the label falls back to what the config
  // says All Correct would do -- still honest, and what it said all along while Flow was parked.
  const previewEnds = previewNext ? previewNext.kind === 'end' : flow.allCorrectAction.type === 'end';
  /** Submit is spent once the task is complete, or was never offered at all. */
  const previewDone = previewComplete || !previewHasInput;

  /**
   * Preview's own bottom furniture, rendered INSIDE the task column.
   *
   * One row now, not two. The results bar that used to sit above it has been retired: it was trying
   * to be the verdict from a position where only furniture sits, and the verdict is a dialog now
   * (see `preview-submission.tsx`).
   *
   * The bar stops where the task stops, so it does not run under the review panel — Preview is meant
   * to look like a student's screen, and a student has no review panel to run under.
   *
   * ## What a student will and will not see
   *
   * Three of these controls are TEACHER FURNITURE and are marked `data-teacher-only`, which keeps
   * them out of the accessibility read-through as well as out of the student runner: Reroll draws
   * another variant, the sensor pill stands in for a phone, and Reset — newly — puts the task back.
   *
   * Reset moved into that cluster rather than staying beside Submit, and the move settles three
   * things at once. It is no longer a destructive control one slip away from the primary action; it
   * is no longer nonsensical sitting live next to a Continue button, because it is not next to it;
   * and it can stay enabled after the task completes, which it has to, since it is now the only way
   * back to a Submit. A student does not need it: a retry unlocks the whole task with the answers
   * still in it, so "start this again" is editing, not clearing. If students ever should be able to
   * wipe a task, that is a setting a teacher turns on, not a button parked next to Submit.
   */
  /**
   * Dismiss the verdict and land somewhere the detail is.
   *
   * The dialog's two exits differ only in WHERE, so they are one function. Both have to move the
   * student: closing a dialog back onto the same scroll position they submitted from is how the old
   * silent Submit felt, and the whole redesign is about not doing that again.
   *
   * The move waits a frame because `ModalShell` restores focus to whatever opened it as it unmounts,
   * and that restore would otherwise land on top of this one — on a Submit button that has just been
   * disabled, which is to say on nothing.
   */
  function dismissSummary(blockId: string | null) {
    setSummaryOpen(false);
    requestAnimationFrame(() => {
      const el = blockId ? document.getElementById(`block-${blockId}`) : null;
      if (el) {
        // `revealElement` rings as well as scrolls, the same gesture the accessibility report's
        // "Show me" uses — landing a student next to four blocks and leaving them to work out which
        // one was meant is most of the way back to telling them nothing.
        revealElement(el);
        /**
         * Focus the CONTROL, not the card.
         *
         * The card was the obvious target and it was wrong twice over. Fix means "go and change
         * this answer", so the place to arrive is the thing that takes the answer — landing on the
         * card leaves a keyboard user one more move away than they need to be. And a card is not
         * focusable, so it had to be given `tabindex="-1"`, which Chrome then treats as
         * `:focus-visible`: a block-sized browser-blue box that outlives the pulse and reads as a
         * selection. Two rings on one card, one of them permanent.
         *
         * A control gets an ordinary control-sized focus ring where a student expects one, and the
         * pulse does the "this block" part on its own.
         */
        const control = el.querySelector<HTMLElement>(
          'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]),'
          + ' button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        control?.focus({ preventScroll: true });
        return;
      }
      // Review: back to the top of the task, which is where a read-it-through starts. The scroller
      // is TaskContent's own, not the page's, and it says so — see `data-task-scroll`.
      previewRootRef.current?.querySelector('[data-task-scroll]')?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /**
   * THE INSPECT TOGGLE, on the bottom bar's left.
   *
   * It lived beside the accessibility group in the header for a while, which cost three
   * placements -- the editor row, the portrait sub-bar and the row that replaces it in
   * landscape are three different bars, and adding it to one left a landscape phone with no
   * button at all. The bottom bar is ONE row on both phone orientations, and its left slot has
   * been empty since Reroll and Reset moved to the panel's own attempts band.
   *
   * It also belongs here on its merits: this bar is the task's own, not the workspace
   * chrome's, and Inspect describes the task.
   *
   * PORTRAIT ONLY. The panel is the default occupant of its slot on a desktop AND in landscape,
   * where it is simply there beside the task; a control to summon something already on screen
   * toggles nothing. Portrait has nowhere to put it beside the task, so there it is a sheet
   * with a door, and this is the door.
   */
  const inspectToggle = isPhone && !isPhoneLandscape && isPreview ? (
    /* Teacher furniture, like the sensor pill beside it: every mount of the panel it opens is
       marked `data-teacher-only`, so neither the student runner nor the emulator describes it. */
    <IconButton
      size='lg'
      data-teacher-only=''
      isActive={inspectOpen}
      onClick={() => setInspectOpen(o => !o)}
      aria-label={t('task.bottomBar.inspect')}
      title={t('task.bottomBar.inspect')}
      className='shrink-0 border border-ws-border'
    >
      <ScanEye aria-hidden='true' className='h-5 w-5' />
    </IconButton>
  ) : null;

  const previewBottom = !isPreview ? null : (
    <>
      {/* The colour-vision filter definitions the vision simulation references. SVG `<defs>`, so
          they render nothing and their position does not matter — only that they exist in the
          document while Preview does. Lost once while this bar was being moved, which would have
          silently broken every vision mode. */}
      <PreviewA11yFilters />
      {/* Preview's single bottom Submit — evaluates every block's current answer at once, replacing
          each block's own individual Submit button while in Preview (see each block's isPreview
          branch). Its own row rather than folded into the per-viewport nav bars above, which
          already juggle three responsive layouts, so it reads as one control at every size.

          Inside the TASK column, not below the whole content area: it used to span the review panel
          too, claiming width that is not the task's. A student's Submit has no review panel to run
          under, and Preview exists to look like a student's screen. */}
      <div className='flex shrink-0 items-center gap-2 border-t border-ws-border bg-ws-surface px-4 py-3'>
            {/* FIRST, so it is the bar's left end on both phone orientations. Null on desktop,
                where the panel it opens is simply always there. */}
            {inspectToggle}
            {/* Reroll draws another student's variant of the task. Only offered when the task
                actually has something to draw — otherwise it's a control that provably does nothing.
                A VERSION BANK COUNTS: a task whose only randomness is a scenario bank has no named
                variables at all, and gating on those alone left the one control that could show a
                second scenario off the screen entirely. Keyword's own block-level Reroll already
                carries the same clause, for the same reason. See `taskHasDraw`.
                EVERYTHING IN FORCE, not the task's own list: the roll two screens up already draws
                both scopes (`experienceRoll` merged with the task's), so gating on `variables`
                alone hid a working control from every task whose randomness is EXPERIENCE-scoped —
                and a task with no variables of its own is the ordinary case for an experience that
                declares them once and reuses them.
                Icon-only and left-aligned: it is the one control here that is not part of the
                answer-and-check loop, and separating it leaves the CENTRE free for the consolidated
                sensor controls (a student meets one set of them, not one per GPS/compass/angle
                block, so the per-block simulate bars come out of Preview and land here). */}
            {/* Reroll and Reset have moved to the debug panel's attempts bar. Neither is a
                student control -- one draws a different student's variant, the other puts the
                run back to nothing -- and both belong beside the tally they reset, which is the
                readout that shows they worked. What is left on this bar is the answer-and-check
                loop a student actually meets. */}
            {/* Stepping back through earlier visits, in the teacher cluster because that is what it
                is: a student carries on from where they left off and has no use for a lock that
                shows them a superseded answer. Offered only once there is more than one visit to
                step between — with a single visit the controls would provably do nothing.

                The position is a live region rather than a title, because the arrows do not move
                focus and the only thing that changes when you press one is a number and the task
                behind it. */}
            {previewVisits.length > 1 && (
              <div data-teacher-only='' className='flex shrink-0 items-center gap-1'>
                <button
                  type='button'
                  onClick={() => setReviewIndex(i => Math.max(0, (i ?? previewVisits.length - 1) - 1))}
                  disabled={reviewIndex === 0}
                  aria-label={t('task.bottomBar.reviewBack')}
                  title={t('task.bottomBar.reviewBack')}
                  className='flex h-9 w-9 items-center justify-center rounded-md border border-ws-border text-ws-text transition-colors hover:bg-ws-hover disabled:opacity-40'
                >
                  <ChevronLeft aria-hidden='true' className='h-4 w-4' />
                </button>
                <span aria-live='polite' className='min-w-[5.5rem] text-center text-xs text-ws-muted'>
                  {t('task.bottomBar.reviewPosition', {
                    n: (reviewIndex ?? previewVisits.length - 1) + 1,
                    total: previewVisits.length,
                  })}
                </span>
                <button
                  type='button'
                  /* Stepping past the last visit returns to live rather than stopping on it: the
                     newest visit and "answering now" are the same screen, and a lock the student
                     cannot leave by going forward is one they have to find Reset to escape. */
                  onClick={() => setReviewIndex(i => (i === null || i >= previewVisits.length - 1 ? null : i + 1))}
                  disabled={reviewIndex === null}
                  aria-label={t('task.bottomBar.reviewForward')}
                  title={t('task.bottomBar.reviewForward')}
                  className='flex h-9 w-9 items-center justify-center rounded-md border border-ws-border text-ws-text transition-colors hover:bg-ws-hover disabled:opacity-40'
                >
                  <ChevronRight aria-hidden='true' className='h-4 w-4' />
                </button>
              </div>
            )}

            {/*
              The consolidated sensor controls, which the spacer here was always reserved for.

              ONE pill, not a strip: on a 390px phone the bar's own buttons leave about 170px, and a
              mode switch plus a select plus two number boxes does not fit in that at any type size.
              So the pill states the device and opens a panel — a bottom sheet on a phone, a popover
              above the bar on anything wider.

              Rendered only when a sensor block exists, since otherwise it is a control that provably
              does nothing — the same rule Reroll follows for variables.
            */}
            {/* `data-teacher-only` for the same reason as Reroll: a student's phone reads its own
                sensors, so nothing in here exists on their screen. Safe on the spacer rather than
                the pill because the div holds nothing else, and it covers the desktop popover too
                (the phone sheet portals to `document.body`, already outside the scan root). */}
            <div data-teacher-only='' className='relative flex min-w-0 flex-1 justify-center'>
              {hasSensorBlock && (
                <>
                  <SensorSimulatorToggle open={sensorSheetOpen} onToggle={() => setSensorSheetOpen(v => !v)} />
                  {sensorSheetOpen && (
                    <SensorSimulatorSheet
                      targets={simTargets}
                      bands={simBands}
                      rotationBands={simRotationBands}
                      hasRotation={hasRotationBlock}
                      tiltBands={simTiltBands}
                      hasTilt={hasTiltBlock}
                      hasGps={hasGpsBlock}
                      hasCompass={hasCompassBlock}
                      onClose={() => setSensorSheetOpen(false)}
                    />
                  )}
                </>
              )}
            </div>

            <span aria-live='polite' className='sr-only'>
              {/* The region stays mounted — a live region has to exist before the change to be
                  announced — while the text node inside is replaced on every reset, so pressing
                  Reset twice is spoken twice instead of falling silent on the identical string. */}
              <span key={previewResetKey}>{previewResetAnnouncement}</span>
            </span>
            {/*
              ONE primary, whose label is the state of the task.

              Submit while there is still something to answer; Continue or Finish once there is not.
              Not two buttons side by side: a Continue sitting greyed beside a live Submit for the
              whole of a student's first attempt is a promise of a destination they cannot see yet,
              and on a 390px bar it is also the width Submit needs.

              `previewSubmit` is deliberately not disabled while the summary dialog is open — the
              dialog is modal and takes the focus, so nothing can reach this until it closes.
            */}
            <Button
              ref={previewPrimaryRef}
              variant='primary'
              size='lg'
              className='shrink-0 px-6'
              disabled={previewDone ? !previewNext : false}
              onClick={previewDone ? (previewNext ? followPreviewRoute : undefined) : submitPreview}
            >
              {previewDone
                ? t(previewEnds ? 'task.bottomBar.previewFinish' : 'task.bottomBar.previewContinue')
                : t('task.bottomBar.previewSubmit')}
            </Button>
          </div>
    </>
  );

  return (
    <AnswerRefProvider value={bandedScope}>
    <LocalVariableProvider value={localVariableNames}>
    <ReducedMotionOverrideContext.Provider value={isPreview && simReducedMotion}>
    <TaskVariablesContext.Provider value={variablesInForceHere}>
    <TaskPreviewRollContext.Provider value={rollInForce}>
    {/*
      ONE device for the whole task, which is what a phone is.

      Every sensor block used to carry its own simulator, so two GPS blocks could each report being
      exactly on target — a thing no single phone can do, and exactly what a teacher needs Preview
      to reveal. Mounted around the whole tree rather than only around Preview so the state survives
      leaving and re-entering; blocks read it only when `isPreview`, and the editor's own per-block
      simulators are untouched by it.
    */}
    <SensorSimulatorProvider initialPosition={simTargets[0]?.at}>
    {/* One ambient chip-resolution scope for the whole of Preview, so EVERY block's PreviewHtml
        resolves against the same roll without each block having to install its own provider — that
        includes the five (Slider, Scale, GPS, Compass, Angle) which keep no roll state at all and
        whose question was previously the only chip-bearing field with nothing to resolve it.
        `null` outside Preview is what makes display mode render chips as {name} pills.
        Blocks that ARE mid-test nest their own provider inside this one, so block-level testing still
        wins locally — which is exactly the intended precedence. */}
    <BlockDeleteGuardProvider value={deleteBlockedReason}>
    <BlockCopyProvider value={readOnly ? null : { copy: handleCopyBlock, justCopiedId }}>
    <BlockPreviewProvider value={previewRoll}>
    <PhoneUndoProvider value={phoneUndoValue}>
    <div className='flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-ws-bg'>
      {toastMsg && <Toast message={toastMsg} onDismiss={() => setToastMsg('')} />}
      {/* Copy leaves nothing visible on the page, so without this a screen-reader user presses it
          and gets silence. Mounted always, keyed inside, so a second copy is spoken again. */}
      <span aria-live='polite' className='sr-only'>
        <span key={clipboardAnnounceKey}>{clipboardAnnouncement}</span>
      </span>
      {pendingPull && (
        <PullThroughDialog
          rows={pendingPull.rows}
          // Dismissing abandons the paste. The teacher was asked a question about it and has
          // not answered, so committing either version would be deciding on their behalf.
          onCancel={() => setPendingPull(null)}
          onSkip={() => {
            const { items, subjectId, insertAfterIndex } = pendingPull;
            setPendingPull(null);
            proceedPaste(items, subjectId, insertAfterIndex);
          }}
          onConfirm={(ids) => { void runPull(ids); }}
        />
      )}

      {pendingPaste && (
        <PasteConflictDialog
          items={pendingPaste.items}
          subjectId={pendingPaste.subjectId}
          // Both scopes: what a name clashes with, and so the renamed preview is actually free.
          inForce={variablesInForceHere}
          onCancel={() => setPendingPaste(null)}
          onConfirm={(choices, include) => {
            const { insertAfterIndex } = pendingPaste;
            setPendingPaste(null);
            commitPaste(insertAfterIndex, choices, include);
          }}
        />
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        accessibility={{
          screenReaderInstructions: {
            draggable: i18n.t('task.a11y.reorderHint'),
          },
          announcements: {
            onDragStart({ active }) {
              const data = active.data.current;
              if (data?.source === 'library') {
                const meta = BLOCK_META[data.blockType as BlockType];
                const label = meta ? i18n.t(meta.labelKey) : (data.blockType as string);
                return i18n.t('task.a11y.pickedUpLibrary', { label });
              }
              const idx = blocks.findIndex(b => b.id === active.id);
              return i18n.t('task.a11y.pickedUpBlock', { position: idx + 1, total: blocks.length });
            },
            onDragOver({ active, over }) {
              if (!over) return undefined;
              const data = active.data.current;
              if (data?.source === 'library') {
                const overId = String(over.id);
                if (overId === 'library-cancel') return i18n.t('task.a11y.overLibrary');
                if (overId.startsWith('gap-')) return i18n.t('task.a11y.overGap');
                const overIdx = blocks.findIndex(b => b.id === overId);
                if (overIdx !== -1) return i18n.t('task.a11y.overBlock', { position: overIdx + 1 });
                return undefined;
              }
              const newIdx = blocks.findIndex(b => b.id === String(over.id));
              return newIdx !== -1 ? i18n.t('task.a11y.movingTo', { position: newIdx + 1, total: blocks.length }) : undefined;
            },
            onDragEnd({ active, over }) {
              const data = active.data.current;
              if (data?.source === 'library') {
                const meta = BLOCK_META[data.blockType as BlockType];
                const label = meta ? i18n.t(meta.labelKey) : (data.blockType as string);
                return over && String(over.id) !== 'library-cancel' ? i18n.t('task.a11y.inserted', { label }) : i18n.t('task.a11y.insertCancelled');
              }
              if (!over || active.id === over.id) return i18n.t('task.a11y.reorderCancelled');
              const newIdx = blocks.findIndex(b => b.id === String(over.id));
              return i18n.t('task.a11y.movedTo', { position: newIdx + 1, total: blocks.length });
            },
            onDragCancel({ active }) {
              const data = active.data.current;
              return i18n.t(data?.source === 'library' ? 'task.a11y.insertCancelled' : 'task.a11y.reorderCancelled');
            },
          },
        }}
      >
        <TaskNavbar
          experienceTitle={experience.title}
          track={taskStepperTrack}
          tasks={experience.tasks}
          taskId={taskId}
          experienceId={experienceId}
          collectionId={experience.collectionId}
          onNavigate={handleNavigate}
          onGuard={guardedExit}
        />

        {/* Directly under the breadcrumbs lane, which is the one row that stays live: navigating
            away has to remain possible, and it is the only way out once everything below is inert.
            In flow rather than portaled — the portal existed to outrank the editing modals, and
            those cannot be opened at all now that the content below is inert. */}
        {readOnly && <TakeOverBanner onResume={resume} compact={isPhoneLandscape} />}

        <div className='flex flex-1 overflow-hidden'>
          {!isPreview && (
            <div
              ref={setLibraryCancelRef}
              className={`hidden md:flex shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out ${(!showLibrary || selectedBlockId || ((variablesPanelOpen || flowPanelOpen || refsPanelOpen) && needsSubbar)) ? 'w-0 lg:w-[17rem]' : 'w-[17rem]'}`}
              {...(readOnly ? { inert: '' } : {})}
              aria-hidden={readOnly}
            >
              <BlockLibrary onAdd={handleAddBlock} isDraggable />
            </div>
          )}

          <div className='flex flex-1 flex-col overflow-hidden'>
            {/* The titlebar is read-only too, and says so. Its own editing controls already
                disabled themselves, which left the bar looking live above a dimmed workspace —
                the same limbo the experience builder's title bar was in. */}
            <div
              className='relative shrink-0'
              {...(readOnly ? { inert: '' } : {})}
              aria-hidden={readOnly}
            >
              {readOnly && (
                <div aria-hidden='true' className='absolute inset-0 z-40 cursor-not-allowed bg-ws-surface/40' />
              )}
            <TaskTitlebar
              /* Centred over the editing column from `sm` up; the nav bar carries it on a
                 phone, where this row has no space. ONE track, derived once above. */
              centerSlot={taskStepperTrack && (
                <TaskStepper track={taskStepperTrack} tasks={experience.tasks} currentTaskId={taskId ?? ''}
                  onGo={id => handleNavigate(taskHref(id, experienceId))}
                  onAdd={readOnly ? undefined : addTaskAtEnd} />
              )}
              taskTitle={taskTitle}
              saving={saving}
              saveFailed={saveFailed}
              isDirty={isDirty}
              canUndo={stack.canUndo}
              canRedo={stack.canRedo}
              undoLabel={stack.undoLabel}
              redoLabel={stack.redoLabel}
              onTitleSave={handleTitleSave}
              onSave={handleSave}
              onUndo={handleUndo}
              onRedo={handleRedo}
              isPhone={isPhone}
              readOnly={readOnly}
              rightSlot={isPhoneLandscape ? (
                !isPreview ? (
                  <div className='flex items-center gap-1'>
                    {/* All icons here — the header slot has no width for a word, and the phone
                        orientations agree with each other rather than with desktop. */}
                    <PanelToggleGroup items={panelToggles(false)} ariaLabel={t('task.panels.groupLabel')} />
                    <Button variant='primary' onClick={handleEnterPreview} aria-label={t('task.bottomBar.preview')}
                      icon={<Play aria-hidden='true' fill='currentColor' />}>
                      {t('task.bottomBar.preview')}
                    </Button>
                  </div>
                ) : (
                  <div className='flex items-center gap-1'>
                    {/* Mirrors the editor row above: same wrapper and gap, and Exit Preview keeps
                        the Preview button's own box, so toggling never moves or resizes it. The
                        review controls take the space the Variables and Flow buttons occupy — both
                        are closed and unopenable in Preview, so the slot is theirs. */}
                    <PreviewA11yControls vision={visionMode} onVision={setVisionMode} largeText={largeText} onLargeText={setLargeText} reducedMotion={simReducedMotion} onReducedMotion={setSimReducedMotion} panel={a11yPanel} onPanel={choosePanel} />
                    {/* Exiting IS the pressed state of Preview — the same border-primary on
                        `bg-ws-btn-active` it always had, now on the shared geometry so it stops
                        looking rounder and taller than everything beside it. */}
                    <Button
                      pressed
                      onClick={() => preview.end()}
                      aria-label={t('task.bottomBar.exitPreview')}
                      icon={<EyeOff aria-hidden='true' />}
                    >
                      {t('task.bottomBar.exitPreviewShort')}
                    </Button>
                  </div>
                )
              ) : undefined}
            />
            </div>

            {/* Sub-bar — tablet portrait + phone portrait */}
            {needsSubbar && (
              <div className='flex shrink-0 items-center gap-2 border-b border-ws-border bg-ws-surface px-3 py-2'>
                {!isPreview ? (
                  <>
                    {/* Icons, like landscape. At 390px a single word would push Preview off the
                        edge, and the two phone orientations showing the same row matters more
                        than matching desktop. */}
                    <PanelToggleGroup stretch items={panelToggles(false)} ariaLabel={t('task.panels.groupLabel')} />
                    {/* Natural width, not `flex-1`. Two `flex-1` siblings grow EQUALLY from
                        unequal bases, so Preview's longer content left it wider than the group
                        — and the group is the half of the row that should dominate. */}
                    <Button variant='primary' onClick={handleEnterPreview} className='shrink-0'
                      icon={<Play aria-hidden='true' fill='currentColor' />}>
                      {t('task.bottomBar.preview')}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* flex-[2] and flex-1: the editor row is three equal buttons, so Exit Preview
                        keeps the last third and the review controls take the two the panel
                        buttons had. */}
                    <div className='flex min-w-0 shrink items-center gap-2'>
                      <PreviewA11yControls vision={visionMode} onVision={setVisionMode} largeText={largeText} onLargeText={setLargeText} reducedMotion={simReducedMotion} onReducedMotion={setSimReducedMotion} panel={a11yPanel} onPanel={choosePanel} compact />
                    </div>
                    <Button
                      pressed
                      onClick={() => preview.end()}
                      aria-label={t('task.bottomBar.exitPreview')}
                      icon={<EyeOff aria-hidden='true' />}
                      className='ml-auto shrink-0'
                    >
                      {t('task.bottomBar.exitPreviewShort')}
                    </Button>
                  </>
                )}
              </div>
            )}



            <div
              /* Less above than below: the block list opens with a card, and 24px of workspace
                 padding stacked on top of the first paste gap left a conspicuous well before any
                 content. The bottom keeps its room, where the last card needs somewhere to end.

                 NONE of it on a phone in Preview. The gutter is workspace furniture — it frames the
                 task as one object among the editor's panels — and Preview on a phone is supposed to
                 look like a student's screen, where the task IS the screen. Left in, it put a bright
                 band around the dimmed task and a matching one around the bottom bar, so the bar
                 read as floating rather than as the foot of the page. */
              className={`relative flex flex-1 overflow-hidden ${isPhone && isPreview ? '' : 'pt-3 pb-6 px-4 gap-6'}`}
              {...(readOnly ? { inert: '' } : {})}
              aria-hidden={readOnly}
            >
              {/* Deliberately simple v1 read-only enforcement: every mutating handler already
               * refuses to run while read-only regardless, so this is purely the visual/
               * interaction affordance layer, not the actual enforcement:
               * - the dimmed overlay is the pointer/visual signal for sighted mouse users
               * - `inert` (+ aria-hidden, matching this codebase's established
               *   block-editor-drawer.tsx/rich-text-editor.tsx pattern) removes the whole
               *   subtree from tab order AND the accessibility tree, so keyboard and
               *   screen-reader users can't land on a control that would silently no-op —
               *   without `inert` a keyboard user could still Tab onto e.g. the quick-add
               *   buttons and press Enter, hitting the handler guard with no visible feedback. */}
              {readOnly && (
                <div
                  aria-hidden='true'
                  className='absolute inset-0 z-40 cursor-not-allowed bg-ws-surface/40'
                />
              )}
              {/*
                The task and its own bottom furniture, as ONE column beside the review panel.

                The Submit bar used to be docked below this whole row, so it spanned the panel too
                and claimed space that is not the task's. Preview exists to look like what a
                student sees, and a student's Submit does not run under a teacher's review panel.
              */}
              {/* `relative` is the anchor the submission summary covers — see its `contained` prop.
                  The dialog stops where the task column stops, so it greys out the task and its own
                  bar and NOT the review panel beside them: Preview exists to look like a student's
                  screen, and a student has no review panel for a dialog to be dimming.

                  The SCAN ROOT, and it spans the dialog as well as the task under it. */}
              <div ref={scanRootRef} className='relative flex min-w-0 flex-1 flex-col overflow-hidden'>
              {/*
                Phone PORTRAIT's read-through, in the column rather than over it.

                `data-teacher-only` because it now lives INSIDE the scan root, and a review that
                reads its own controls back to you is describing an experience no student has.

                Above the student surface, so it pushes the task down: as a `fixed` bar it sat on the
                first thing in the task, which made the one element you could never read the one the
                read-through starts with.
              */}
              {isPhone && !isPhoneLandscape && isPreview && a11yPanel === 'reading' && (
                <div data-teacher-only=''>
                  <PreviewA11yPanel
                    phoneFrame getRoot={getPreviewRoot} getScanRoot={getScanRoot}
                    scanKey={previewRevision} onClose={() => setA11yPanel(null)} />
                </div>
              )}
              {/*
                The student's surface, suppressed entirely while the summary is up.

                `inert` + `aria-hidden` covers the BAR as well as the task, which is the point of the
                two being wrapped together here: Submit sits behind the dialog, and a teacher
                stepping the read-through was still being handed it as a row. `isHidden` is what both
                scans skip on, so one attribute does the suppressing and the describing at once —
                what a student cannot reach is not what the review describes.
              */}
              <div
                className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden gap-2'
                {...(summaryOpen ? { inert: '' } : {})}
                aria-hidden={summaryOpen || undefined}
              >
              {/*
                The task, and the two states in which it stops taking answers.

                **While the summary is up** it is `inert` and `aria-hidden` — ordinary modal
                behaviour, not a lock: content behind a dialog belongs out of the tab order and out
                of the accessibility tree, and `inert` swallows pointer events too. It gets NO tint
                of its own. It had one, and that was the bug: the dialog already paints
                `bg-ws-modal-overlay` across the whole column, so a second veil left the task under
                two washes while the bar below it had one — two treatments on one screen, and the
                feedback colours muddied under a 50% white for good measure.

                **Once the task is complete** it is neither inert nor dimmed. `inert` would take the
                whole task out of the accessibility tree, and the entire point of the state it is
                entering is that the student can go back and READ their feedback — a screen-reader
                user most of all. So the review lock is pointer-events only, with no tint at all,
                so nothing stands between them and what they are reviewing. See the div below.
              */}
              {/* `inert` and `aria-hidden` moved up to the scan root, so they cover the bar too;
                  what is left here is the review lock.

                  REVIEWING LOCKS THE TASK for the same reason completing it does: what is on
                  screen is a record of something already handed in, and a control that accepted
                  an edit would be offering to change the past.

                  IT IS `pointer-events`, NOT AN OVERLAY, and the difference is scrolling. The
                  lock was an `absolute inset-0` shield, which sits OUTSIDE the task's own
                  scroll container — so a wheel over it had no scrollable ancestor to act on and
                  simply did nothing. Reported as "i was scrolled to the top of the exercise and
                  scrolling then became disabled", which is the worst possible moment for it:
                  the state exists so a student can go back and read their feedback, and the
                  feedback is below the fold.

                  `pointer-events: none` is CSS only, so unlike `inert` it leaves the whole task
                  in the accessibility tree — which is the constraint that ruled `inert` out and
                  still rules. It stops the mouse and not the keyboard, the same bounded gap the
                  shield had: every block grades a SNAPSHOT taken at submit, so a widget nudged
                  afterwards changes no feedback and, with Submit already spent, can never be
                  re-graded. Closing that properly means a `locked` prop each of the fourteen
                  blocks honours, which is a job of its own. */}
              <div className='relative flex min-h-0 min-w-0 flex-1 flex-col'>
              <FiredStatesProvider value={isPreview ? firedStates : null}>
              <ReviewAttemptProvider attempt={reviewing}>
              {/* `null` off the preview, so the editor's canvas never withholds a saved answer
                  from the teacher on the strength of a policy about students. */}
              <ArrivalPolicyProvider policy={isPreview ? flow.onArrival ?? null : null}>
              <TaskContent
                rootRef={previewRootRef}
                /* Preview only. The editor has Answer Recall's own panel for this and a ring
                   around a block whose reference simply has not been run yet would be marking
                   the normal state of every experience being written. */
                recallMark={isPreview ? recallMark : undefined}
                previewReviewIndex={reviewIndex}
                /* INSIDE the scroller — see `reviewLocked`'s own note. Applied out here it
                   killed scrolling, which is the opposite of what this state is for. */
                reviewLocked={(previewComplete || !!reviewing) && !summaryOpen}
                viewStyle={isPreview ? visionFilterStyle(visionMode, largeText) : undefined}
                title={task.title}
                experienceId={experienceId}
                taskId={taskId!}
                shownTaskId={shownTaskId}
                blocks={blocks}
                isPreview={isPreview}
                previewSubmitSignal={previewSubmitCount}
                previewResetKey={previewResetKey}
                selectedBlockId={selectedBlockId}
                mobileEditingId={mobileEditingId}
                phoneBlockMountKey={phoneBlockMountKey}
                onPhoneEditOpen={handlePhoneEditOpen}
                onPhoneEditClose={handlePhoneEditClose}
                onShowLibrary={() => {
                  const isMobile = !window.matchMedia('(min-width: 768px)').matches;
                  if (isMobile) setShowLibraryModal(true);
                  else setShowLibrary(true);
                }}
                highlightedBlockId={highlightedBlockId}
                // Chosen by the teacher, so the options drawer starts shut — see
                // `forgetSettingsOpen`. An undo that needs it open opens it itself.
                onSelectBlock={(id) => { forgetSettingsOpen(); setSelectedBlockId(id); setVariablesPanelOpen(false); setFlowPanelOpen(false); }}
                onRemoveBlock={handleRemoveBlock}
                onUpdateBlock={handleUpdateBlock}
                onDropBlock={handleAddBlock}
                onMoveBlock={handleMoveBlock}
                onDuplicateBlock={handleDuplicateBlock}
                clipboardLabel={clipboardLabel}
                onPlaceAt={placeAt}
                onRenameBlock={handleRenameBlock}
                siblingGpsTargets={siblingGpsTargets}
                showBlockNameBadge
              />
              </ArrivalPolicyProvider>
              </ReviewAttemptProvider>
              </FiredStatesProvider>
              </div>
              {previewBottom}
              </div>
              {/*
                INSIDE the scan root, and a sibling of the suppressed surface rather than a child of
                it — which is the whole arrangement in one line.

                While this is up it IS the student's screen, so it is what the review should read,
                and the task behind it should vanish from the report exactly as it vanishes for a
                real reader. `aria-hidden` on the sibling above does the vanishing; being outside
                that sibling is what keeps the dialog itself readable.

                Its rows get no numbered badges, by the rule `useBadgeOffsets` already had for the
                Submit bar: a badge is a pin on a map of the TASK, and a row from somewhere else
                keeps its number in the list and reads in order without one.
              */}
              {summaryOpen && submission && (
                <PreviewSummaryDialog
                  /* Focus comes back here when the dialog closes: the submit that raised it has
                     already re-rendered this bar, so the shell's own capture is the body. */
                  returnFocusRef={previewPrimaryRef}
                  submission={submission}
                  exit={exitContent}
                  exitAt={exitAt}
                  onReview={() => dismissSummary(null)}
                  onFix={() => dismissSummary(submission.firstIncorrectId)}
                  /* The SAME route the bottom bar's primary takes, and offered on the same
                     condition — `previewNext` is what both read, so the dialog cannot offer a
                     Continue the bar has disabled. Closing first: `followPreviewRoute` navigates,
                     and leaving a modal mounted across that is how a focus restore lands on a
                     page that no longer exists. */
                  onContinue={previewNext ? () => { setSummaryOpen(false); followPreviewRoute(); } : undefined}
                  endsExperience={previewEnds}
                />
              )}
              </div>

              {/*
                Phone LANDSCAPE puts the review beside the task, the way a desktop does.

                It used to be a `fixed` rail laid OVER the task, and covering the thing you are
                reviewing is the one thing this panel must not do — the read-through's whole job is
                "does THAT part sound right", which needs both on screen at once. In the row it
                pushes the task in instead, and a landscape phone has the width to spare.

                Docked, so it is the plain bordered panel rather than the coloured sheet: a sheet's
                titlebar earns its weight by being the whole screen, and this is not.
              */}
              {/*
                THE LANDSCAPE DOCK, the same shape the read-through next door uses and for the
                same reason: a panel that supplements the task has to be readable beside it.

                Wider than the read-through's `w-72` because this one carries the connections
                diagram, which is drawn to a 360 viewBox and scales to its column — and the
                price of the dock is that the recalls, the map and the blocks no longer fit at
                once. On a 390px-tall phone that is unavoidable, and the owner's call is that
                scrolling a visible panel beats a modal that hides the task.
              */}
              {/*
                SIMPLY THERE, the way it is on a desktop, and with no titlebar.

                It was summoned and had a header to dismiss it with, which cost 44px of a 242px
                panel to say a word the button that opened it already said. The slot behaves
                like the desktop column instead: this is what occupies it, and the read-through
                REPLACES it rather than stacking beside it -- two docks on a 740px phone leave
                the task about 350px, which is less than either panel is describing.

                No `onClose`, which is also what renders the header away: the panel draws a
                titlebar only where something summoned it, and nothing does here.
              */}
              {isPhoneLandscape && isPreview && a11yPanel !== 'reading' && (shownTaskId ?? taskId) && (
                <div data-teacher-only='' className='flex w-80 shrink-0 flex-col overflow-hidden border-l border-ws-border bg-ws-surface p-2'>
                  <PreviewDebugPanel
                    short
                    onRecallHover={setLitRecall}
                    taskId={(shownTaskId ?? taskId)!}
                    tasks={experience.tasks}
                    chipRegistry={firedStates}
                    onReroll={taskHasDraw(variablesInForceHere, blocks) ? rerollPreview : undefined}
                    onReset={resetPreview}
                  />
                </div>
              )}

              {isPhoneLandscape && isPreview && a11yPanel === 'reading' && (
                /* `w-80`, matching the Inspect dock above it EXACTLY. The two are one slot — the
                   emulator replaces Inspect rather than joining it — so a different width would
                   shunt the task sideways every time a teacher switched between them, on the one
                   layout with the least room to spare. 320px is Inspect's own figure, settled when
                   the attempts bar needed a dock it could anchor to. */
                <div className='flex w-80 shrink-0 flex-col overflow-hidden border-l border-ws-border bg-ws-surface p-2'>
                  <PreviewA11yPanel
                    compact
                    getRoot={getPreviewRoot}
                    getScanRoot={getScanRoot}
                    scanKey={previewRevision}
                    onClose={() => setA11yPanel(null)}
                  />
                </div>
              )}

              {!isPhone && (() => {
                const selected = blocks.find(b => b.id === selectedBlockId);
                // Stable: every block re-reads `block.data` when it changes underneath it, so an
                // undo re-renders the panel rather than rebuilding it. See `use-restored-data`.
                const panelKey = `${selectedBlockId}`;
                const embeddedProps = selected && !isPreview ? {
                  block: selected,
                  isPreview: false as const,
                  embedded: true as const,
                  experienceId,
                  taskId: taskId!,
                  taskTitle,
                  blocks,
                  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => handleUpdateBlock(selected.id, data, opts),
                  onRemove: () => { handleRemoveBlock(selected.id); setSelectedBlockId(null); },
                  onDuplicate: () => handleDuplicateBlock(selected.id),
                  onClose: () => setSelectedBlockId(null),
                  onRename: (name: string) => handleRenameBlock(selected.id, name),
                } : null;
                // Tablet portrait: panel width animates in/out; desktop/tablet landscape: always w-96
                const panelW = needsSubbar
                  ? (rightPanelVisible ? 'w-80 md:w-96' : 'w-0')
                  : 'w-96';
                return (
                  <div className={`hidden md:flex flex-col shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out gap-2 ${panelW}`}>
                    {/* Variables + Flow + Preview — desktop/tablet landscape only; sub-bar handles portrait */}
                    {!needsSubbar && !isPreview && (
                      <div className='flex shrink-0 items-center gap-2'>
                        {/* The only surface wide enough for a word, and Flow gets it: the two
                            icon switches read as a pair and the word anchors the group's right
                            edge. 384px of row against roughly 290px of content. */}
                        <PanelToggleGroup stretch items={panelToggles(true)} ariaLabel={t('task.panels.groupLabel')} />
                        {/* Natural width — see the sub-bar's note. The group takes the rest,
                            which is roughly 70% of the 384px column. */}
                        <Button variant='primary' onClick={handleEnterPreview} className='shrink-0'
                          icon={<Play aria-hidden='true' fill='currentColor' />}>
                          {t('task.bottomBar.preview')}
                        </Button>
                      </div>
                    )}
                    {!needsSubbar && isPreview && (
                      <div className='flex items-center gap-2 pt-1.5'>
                        <div className='flex min-w-0 shrink items-center gap-2'>
                          <PreviewA11yControls vision={visionMode} onVision={setVisionMode} largeText={largeText} onLargeText={setLargeText} reducedMotion={simReducedMotion} onReducedMotion={setSimReducedMotion} panel={a11yPanel} onPanel={choosePanel} compact />
                        </div>
                        <Button
                          pressed
                          onClick={() => preview.end()}
                          aria-label={t('task.bottomBar.exitPreview')}
                          icon={<EyeOff aria-hidden='true' />}
                          className='ml-auto shrink-0'
                        >
                          {t('task.bottomBar.exitPreviewShort')}
                        </Button>
                      </div>
                    )}

                    {/*
                      The slot Variables and Flow vacate on entering Preview, and Behind the Scenes
                      is what sits in it by DEFAULT.

                      Not something you summon: it describes the task already on screen, and a
                      teacher walking an experience wants it the whole way rather than once per
                      question. The read-through IS summoned, and it COVERS this rather than
                      sitting beside it -- there is one slot, and two panels sharing it would
                      resize the task column twice per review. Closing it brings this back
                      untouched, which is also why this panel has no close of its own: there is
                      nothing to close it to.
                    */}
                    {isPreview && a11yPanel === 'reading' ? (
                      <div className='flex min-h-0 flex-1 overflow-hidden'>
                        <PreviewA11yPanel getRoot={getPreviewRoot} getScanRoot={getScanRoot} scanKey={previewRevision} onClose={() => setA11yPanel(null)} />
                      </div>
                    ) : isPreview && (shownTaskId ?? taskId) ? (
                      <div data-teacher-only='' className='flex min-h-0 flex-1 overflow-hidden'>
                        <PreviewDebugPanel taskId={(shownTaskId ?? taskId)!} tasks={experience.tasks} chipRegistry={firedStates}
                          onRecallHover={setLitRecall}
                          onReroll={taskHasDraw(variablesInForceHere, blocks) ? rerollPreview : undefined} onReset={resetPreview} />
                      </div>
                    ) : null}

                    {/* Panel body */}
                    {!isPreview && (
                      <div className='flex flex-1 overflow-hidden'>
                        {/*
                          PANELS FIRST, and a selected block is NOT cleared to make room.

                          Opening Variables or Flow used to deselect the block, so going to
                          define a variable mid-edit closed the editor, dimmed the canvas, and
                          left the teacher to find the block and open it again — while they
                          were in the middle of using it. The two panels and the block editor
                          share one slot, which is the real constraint; the selection never
                          had to be the thing that gave way. Ordering the branches this way
                          lets a panel take the slot while the block stays selected, so
                          closing the panel falls straight back into the editor where it was.
                        */}
                        {variablesPanelOpen ? (
                          <VariablesPanel
                            variables={variables}
                            experienceVariables={experienceVariables ?? []}
                            declinedNames={declinedNames}
                            onRetryName={() => setDeclinedNames([])}
                            variableUsage={variableUsage}
                            mayAdd={variablesCapabilityOn}
                            onAddVariable={handleAddVariable}
                            onUpdateVariable={handleUpdateVariable}
                            onRenameSet={(f, t2) => handleRenameSet(f, t2, 'task')}
                            onRenameExperienceSet={(f, t2) => handleRenameSet(f, t2, 'experience')}
                            onDeleteSet={st => handleDeleteSet(st, 'task')}
                            onDuplicateSet={st => handleDuplicateSet(st, 'task')}
                            onDeleteExperienceSet={st => handleDeleteSet(st, 'experience')}
                            onDuplicateExperienceSet={st => handleDuplicateSet(st, 'experience')}
                            onRemoveVariable={handleRemoveVariable}
                            onAddExperienceVariable={handleAddExperienceVariable}
                            onUpdateExperienceVariable={handleUpdateExperienceVariable}
                            onRemoveExperienceVariable={handleRemoveExperienceVariable}
                            onClose={() => setVariablesPanelOpen(false)}
                          />
                        ) : flowPanelOpen ? (
                          <div className='flex w-full flex-col h-full overflow-hidden rounded-xl border border-ws-primary/30 bg-ws-surface'>
                            <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle dark:bg-ws-text-link/[.20] px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link'>
                              <FlowIcon aria-hidden='true' className='h-6 w-6' />
                              <span className='flex-1'>{t('task.bottomBar.flow')}</span>
                              <button type='button' onClick={() => setFlowPanelOpen(false)} aria-label={t('task.flow.close')}
                                className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'>
                                <X aria-hidden='true' className='h-5 w-5' />
                              </button>
                            </div>
                            <FlowPanel
                              flow={flow}
                              experienceId={experienceId}
                              blocks={blocks}
                              tasks={experience.tasks}
                              currentTaskId={taskId!}
                              initialOpenExit={pendingExit?.slot}
                              initialExitBlockId={pendingExit?.blockId}
                              initialFocusCard={pendingFlowCard}
                              onInitialFocusCardShown={consumePendingFlowCard}
                              onInitialExitOpened={consumePendingExit}
                              onCreateDetourTask={createDetourTask}
                              onChange={handleFlowChange}
                            />
                          </div>
                        ) : refsPanelOpen ? (
                          <div className='flex w-full flex-col h-full overflow-hidden rounded-xl border border-ws-primary/30 bg-ws-surface'>
                            <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle dark:bg-ws-text-link/[.20] px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link'>
                              <Brackets aria-hidden='true' className='h-6 w-6' />
                              <span className='flex-1'>{t('blocks.answerRef.manager.title')}</span>
                              <button type='button' onClick={() => setRefsPanelOpen(false)} aria-label={t('blocks.answerRef.manager.close')}
                                className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'>
                                <X aria-hidden='true' className='h-5 w-5' />
                              </button>
                            </div>
                            <AnswerRefManager
                              tasks={experience.tasks}
                              currentTaskId={taskId!}
                              onOpenBlock={id => { setRefsPanelOpen(false); setSelectedBlockId(id); }}
                              onOpenTask={openTaskFromPanel}
                              onRepoint={handleRepointAlias}
                            />
                          </div>
                        ) : embeddedProps && selected ? (
                          <div className={`flex w-full shrink-0 flex-col h-full overflow-y-auto rounded-xl ${highlightedBlockId === selectedBlockId ? 'agavi-pulse' : ''}`}>
                            {/* The canvas wraps each block in `BlockIdProvider`; this panel
                                renders one OUTSIDE that tree, so it supplies its own — without
                                it the editor's issues panel cannot tell which block it is
                                reporting on and silently drops the reference check. */}
                            <BlockIdProvider blockId={selected.id} taskId={taskId!}>
                            {renderEmbeddedBlock(selected, embeddedProps, panelKey, {
                              initialIsEditingFeedback: blockFeedbackModes[selected.id] ?? false,
                              onFeedbackModeChange: (v) => setBlockFeedbackModes(prev => ({ ...prev, [selected.id]: v })),
                              siblingGpsTargets,
                            })}
                            </BlockIdProvider>
                          </div>
                        ) : !needsSubbar ? (
                          <div className='flex flex-1'>
                            <div className='flex flex-1 p-4 flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-ws-border'>
                              <SquarePen className='h-6 w-6 text-ws-muted' />
                              <p className='text-sm text-ws-muted text-center'>{t('task.selectBlock')}</p>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>


        {/* Phone review sheet — full-screen, because a findings list cannot share a 390px viewport
            with the task it describes. Each row scrolls to and flashes its element instead. */}
        {/*
          PORTRAIT only. Landscape docks the same panel beside the task instead, where a findings
          list and the task it describes both fit.

          "Show me" CLOSES this rather than collapsing it to a bar. The bar it used to leave behind
          carried a "Show list" button and a close, and both were already available: the toolbar's
          own button reopens the review, and it is the button a teacher reaches for anyway. On a
          landscape phone that bar also spent scarce vertical space saying so.
        */}

        {/* Full-screen task settings overlay for phone only */}
        {/*
          PROMOTION. One question, not a per-task diff: the owner's call, and the simpler thing is
          the right default. The two wordings differ only in what they promise — when every
          existing definition matches, this is a promotion and nothing a student sees changes;
          when they differ, it is a replacement and some task's numbers will move.
        */}
        {/*
          ONLY THE TASKS WHOSE NUMBERS MOVE, and each one shown moving.
          Modelled on the paste conflict dialog, which this is the same question as: a name that
          means two things, and a teacher deciding what it should mean. Two things were borrowed.
          First its rule — an identical definition is not a conflict and raises nothing, so the
          usual promotion is silent. Second its grammar — a row per conflict, written as a
          transformation with an arrow, because "what will this become" is the whole question and
          a list of task names does not answer it.
        */}
        {promotion && (
          <ModalShell
            title={(
              /* The variable is named in the title because the title is about it, and set in
                 braces and the chip colour so it reads as the thing on the canvas rather than a
                 word in a sentence. `ws-text-link`, not `ws-primary`, which does not hold up on
                 a dark ground. */
              <>
                {t('task.settings.variables.replaceTitleBefore')}
                <span className='font-mono text-ws-text-link'>{`{${promotion.name}}`}</span>
                {t('task.settings.variables.replaceTitleAfter')}
              </>
            )}
            onClose={() => {
              setDeclinedNames(names => (names.includes(promotion.name) ? names : [...names, promotion.name]));
              setPromotion(null);
            }}
            maxWidth='max-w-lg'
          >
            <div className='flex flex-col gap-4'>
              <p className='text-sm text-ws-muted'>
                {t('task.settings.variables.replaceIntroNew')}
              </p>

              {/* One decision per task, which is the paste dialog's shape and for its reason: a
                  block can carry several variables and they need not go the same way, and here
                  several tasks can disagree and need not either. Each option is written as the
                  transformation it performs, since that is the whole question. */}
              {promotion.conflicts.map(c => {
                const freeName = freeVariableName(c.variable.name, [
                  ...(experienceVariables ?? []).map(v => v.name),
                  ...((experience?.tasks.find(x => x.id === c.taskId)?.variables) ?? []).map(v => v.name),
                  promotion.name,
                ]);
                const transform = (label: string, from: string, to: string) => (
                  <span className='flex flex-col gap-0.5'>
                    <span className='text-sm font-medium text-ws-text'>{label}</span>
                    <span className='grid grid-cols-[1fr_auto_1fr] items-center gap-x-4 text-xs text-ws-muted'>
                      <span className='text-right font-mono'>{from}</span>
                      <ArrowRight aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                      <span className='sr-only'>{t('task.paste.conflict.becomes')}</span>
                      <span className='text-left font-mono text-ws-text'>{to}</span>
                    </span>
                  </span>
                );
                return (
                  <div key={c.taskId} className='flex flex-col gap-2 rounded-xl border border-ws-border p-3'>
                    <span className='text-sm font-semibold text-ws-text'>{c.title}</span>
                    <RadioGroup
                      aria-label={c.title}
                      value={promotionChoices[c.taskId] ?? 'separate'}
                      onChange={(nextChoice) => setPromotionChoices(prev => ({ ...prev, [c.taskId]: nextChoice }))}
                      options={[
                        { value: 'shared', label: transform(
                          t('task.settings.variables.useShared'),
                          describeRange(c.variable), describeRange(promotion.definition)) },
                        { value: 'separate', label: transform(
                          t('task.settings.variables.keepSeparate'), c.variable.name, freeName) },
                      ]}
                    />
                  </div>
                );
              })}

              <div className='flex justify-end gap-2'>
                <Button variant='secondary' onClick={() => {
                  setDeclinedNames(names => (names.includes(promotion.name) ? names : [...names, promotion.name]));
                  setPromotion(null);
                }}>
                  {t('common.cancel')}
                </Button>
                <Button variant='primary' onClick={() => { void applyPromotion(promotion, promotionChoices); setPromotion(null); }}>
                  {t('task.settings.variables.replaceConfirm')}
                </Button>
              </div>
            </div>
          </ModalShell>
        )}

        {isPhone && variablesPanelOpen && createPortal(
          <div ref={variablesFullScreenRef} role='dialog' aria-modal='true' aria-label={t('task.bottomBar.variables')} tabIndex={-1} className='fixed inset-0 z-[110] flex flex-col bg-ws-surface'>
            <VariablesPanel
              fullScreen
              variables={variables}
              experienceVariables={experienceVariables ?? []}
              declinedNames={declinedNames}
              onRetryName={() => setDeclinedNames([])}
              variableUsage={variableUsage}
              mayAdd={variablesCapabilityOn}
              onAddVariable={handleAddVariable}
              onUpdateVariable={handleUpdateVariable}
              onRenameSet={(f, t2) => handleRenameSet(f, t2, 'task')}
              onRenameExperienceSet={(f, t2) => handleRenameSet(f, t2, 'experience')}
              onDeleteSet={st => handleDeleteSet(st, 'task')}
              onDuplicateSet={st => handleDuplicateSet(st, 'task')}
              onDeleteExperienceSet={st => handleDeleteSet(st, 'experience')}
              onDuplicateExperienceSet={st => handleDuplicateSet(st, 'experience')}
              onRemoveVariable={handleRemoveVariable}
              onAddExperienceVariable={handleAddExperienceVariable}
              onUpdateExperienceVariable={handleUpdateExperienceVariable}
              onRemoveExperienceVariable={handleRemoveExperienceVariable}
              onClose={() => setVariablesPanelOpen(false)}
            />
          </div>,
          document.body
        )}

        {/*
          PORTRAIT ONLY, and the landscape half is now a dock — see the column beside the task.

          This used to be full screen in both, on the reasoning that Inspect is "a readout about
          the task rather than a pointer into it", so the diagram should take the width. That
          reads the panel's job too narrowly: it is a SUPPLEMENT to the thing being worked on,
          and a supplement you cannot see beside its subject is a worse supplement than a
          narrower one. The owner's rule: co-view beats a modal for a panel designed to
          accompany the content, and squeezing the task in landscape is a price worth paying.

          Portrait has no width to give, so there it stays a sheet.
        */}
        {isPhone && !isPhoneLandscape && isPreview && inspectOpen && (shownTaskId ?? taskId) && createPortal(
          <div data-teacher-only='' role='dialog' aria-modal='true' aria-label={t('task.bottomBar.inspect')} className='fixed inset-0 z-[110] flex flex-col bg-ws-surface'>
            {/* NO `onRecallHover`: this one covers the task it would be pointing at, so the ring
                has nowhere to land. The dashed rings are still there underneath, waiting. */}
            <PreviewDebugPanel fullScreen taskId={(shownTaskId ?? taskId)!} tasks={experience.tasks} chipRegistry={firedStates}
              onReroll={taskHasDraw(variablesInForceHere, blocks) ? rerollPreview : undefined} onReset={resetPreview} onClose={() => setInspectOpen(false)} />
          </div>,
          document.body
        )}

        {/* Full-screen Flow overlay for phone only — same convention as Variables above, since
            Flow has no needsSubbar/desktop panel slot to fall back to on a phone-sized viewport. */}
        {isPhone && flowPanelOpen && createPortal(
          <div ref={flowFullScreenRef} role='dialog' aria-modal='true' aria-label={t('task.bottomBar.flow')} tabIndex={-1} className='fixed inset-0 z-[110] flex flex-col bg-ws-surface'>
            <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle dark:bg-ws-text-link/[.20] px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link'>
              <FlowIcon aria-hidden='true' className='h-6 w-6' />
              <span className='flex-1'>{t('task.bottomBar.flow')}</span>
              <button type='button' onClick={() => setFlowPanelOpen(false)} aria-label={t('task.flow.close')}
                className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'>
                <X aria-hidden='true' className='h-5 w-5' />
              </button>
            </div>
            <FlowPanel
              flow={flow}
              experienceId={experienceId}
              blocks={blocks}
              tasks={experience.tasks}
              currentTaskId={taskId!}
              initialOpenExit={pendingExit?.slot}
              initialExitBlockId={pendingExit?.blockId}
              initialFocusCard={pendingFlowCard}
              onInitialFocusCardShown={consumePendingFlowCard}
              onInitialExitOpened={consumePendingExit}
              onCreateDetourTask={createDetourTask}
              onChange={handleFlowChange}
            />
          </div>,
          document.body
        )}

        {/* Full-screen References overlay for phone — same convention as Flow above. */}
        {isPhone && refsPanelOpen && createPortal(
          <div ref={refsFullScreenRef} role='dialog' aria-modal='true' aria-label={t('blocks.answerRef.manager.title')} tabIndex={-1} className='fixed inset-0 z-[110] flex flex-col overflow-hidden bg-ws-surface'>
            <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle dark:bg-ws-text-link/[.20] px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link'>
              <Brackets aria-hidden='true' className='h-6 w-6' />
              <span className='flex-1'>{t('blocks.answerRef.manager.title')}</span>
              <button type='button' onClick={() => setRefsPanelOpen(false)} aria-label={t('blocks.answerRef.manager.close')}
                className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'>
                <X aria-hidden='true' className='h-5 w-5' />
              </button>
            </div>
            <AnswerRefManager
              tasks={experience.tasks}
              currentTaskId={taskId!}
              onOpenBlock={id => { setRefsPanelOpen(false); setSelectedBlockId(id); }}
              onOpenTask={openTaskFromPanel}
              onRepoint={handleRepointAlias}
            />
          </div>,
          document.body
        )}

        {showUnsavedModal && (
          <UnsavedChangesModal
            onCancel={() => setShowUnsavedModal(false)}
            onLeave={handleLeaveWithoutSaving}
            onSaveAndLeave={handleSaveAndLeave}
          />
        )}

        {showLibraryModal && (
          <div className='fixed inset-0 z-50 md:hidden' onClick={() => setShowLibraryModal(false)}>
            <div className='absolute inset-0 bg-ws-modal-overlay' />
            <div
              className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-5rem)] h-[calc(100%-10rem)] max-w-sm rounded-xl border-2 border-ws-border shadow-xl bg-ws-sidebar p-2'
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowLibraryModal(false)}
                className='absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover hover:text-ws-text'
                aria-label={t('common.close')}
              >
                <X className='h-6 w-6' />
              </button>
              <div className='h-full w-full rounded-lg overflow-hidden'>
                <BlockLibrary
                  onAdd={(type) => { handleAddBlock(type); setShowLibraryModal(false); }}
                  onClose={() => setShowLibraryModal(false)}
                />
              </div>
            </div>
          </div>
        )}
        <DragOverlay dropAnimation={null}>{renderDragOverlay()}</DragOverlay>
      </DndContext>
    </div>
    </PhoneUndoProvider>
    </BlockPreviewProvider>
    </BlockCopyProvider>
    </BlockDeleteGuardProvider>
    </SensorSimulatorProvider>
    </TaskPreviewRollContext.Provider>
    </TaskVariablesContext.Provider>
    </ReducedMotionOverrideContext.Provider>
    </LocalVariableProvider>
    </AnswerRefProvider>
  );
}
